/**
 * Angel Ashes — Memorials & Keepsakes catalog + selection helpers.
 * Consumed by booking-wizard.js (same theme section).
 */
(function (global) {
  'use strict'

  function truthy(v) {
    return v === true || v === 'true' || v === 1 || v === '1'
  }

  function numOrNull(v) {
    if (v == null || v === '') return null
    var n = Number(v)
    return isFinite(n) ? n : null
  }

  /** Theme Editor Variant 1–6 slots (Admin variant order). */
  var VARIANT_MAX_KG_SLOT_COUNT = 6

  function memorialSettingsObject(itemBlockOrSettings) {
    if (!itemBlockOrSettings) return null
    if (itemBlockOrSettings.settings && typeof itemBlockOrSettings.settings === 'object') {
      return itemBlockOrSettings.settings
    }
    return itemBlockOrSettings
  }

  function memorialItemBlock(itemBlockOrSettings) {
    if (!itemBlockOrSettings) return null
    if (itemBlockOrSettings.settings) return itemBlockOrSettings
    return { settings: itemBlockOrSettings }
  }

  /**
   * Theme Editor image_picker as a usable URL. Liquid should emit a CDN string
   * (`image_url`); also accept `{ src }` / `{ url }` if a payload ships an object.
   */
  function teImageUrl(value) {
    if (value == null || value === false) return null
    if (typeof value === 'string') {
      var s = value.trim()
      return s && s !== '[object Object]' ? s : null
    }
    if (typeof value === 'object') {
      return teImageUrl(
        value.src ||
          value.url ||
          value.image ||
          (value.preview_image && (value.preview_image.src || value.preview_image.url)) ||
          null
      )
    }
    return null
  }

  /** Nameplate metal token from Gold/Silver colour state. */
  function normalizeNameplateMetal(value) {
    var v = String(value || '').trim().toLowerCase()
    if (v === 'gold' || v === 'silver') return v
    return null
  }

  /** TE personalise thumbs: colour-specific slots plus shared fallback. */
  function personaliseImagesFrom(source) {
    if (!source) return { shared: null, gold: null, silver: null }
    return {
      shared: teImageUrl(source.personaliseImage || source.personalise_image),
      gold: teImageUrl(source.personaliseImageGold || source.personalise_image_gold),
      silver: teImageUrl(source.personaliseImageSilver || source.personalise_image_silver),
    }
  }

  /**
   * Derive the opposite-metal CDN/TE URL when merchants only uploaded one slot.
   * Clay Photo Frame ships Gold + Silver blanks as Clay-Photo-Gold-Blank.png /
   * Clay-Photo-Silver-Blank.png — swapping the metal token recovers the other.
   * Returns null when the URL has no Gold/Silver token (or already matches).
   * Same path for both metals: only the filename token differs.
   */
  function deriveMetalPersonaliseUrl(url, targetMetal) {
    var src = String(url || '').trim()
    var metal = normalizeNameplateMetal(targetMetal)
    if (!src || !metal) return null
    var hasGold = /gold/i.test(src)
    var hasSilver = /silver/i.test(src)
    if (metal === 'silver') {
      if (hasSilver && !hasGold) return src
      if (!hasGold) return null
      return src
        .replace(/Gold/g, 'Silver')
        .replace(/gold/g, 'silver')
        .replace(/GOLD/g, 'SILVER')
    }
    if (hasGold && !hasSilver) return src
    if (!hasSilver) return null
    return src
      .replace(/Silver/g, 'Gold')
      .replace(/silver/g, 'gold')
      .replace(/SILVER/g, 'GOLD')
  }

  /**
   * Explicit TE metal slot (Files CDN via image_url), else Gold↔Silver filename
   * derivation from the other slot / shared image. Gold and Silver use the
   * identical order — never override with a theme asset_url.
   */
  function resolveMetalPersonaliseUrl(imgs, colour) {
    imgs = imgs || { shared: null, gold: null, silver: null }
    var metal = normalizeNameplateMetal(colour)
    if (metal === 'gold') {
      if (imgs.gold) return imgs.gold
      return (
        deriveMetalPersonaliseUrl(imgs.silver, 'gold') ||
        deriveMetalPersonaliseUrl(imgs.shared, 'gold') ||
        null
      )
    }
    if (metal === 'silver') {
      if (imgs.silver) return imgs.silver
      return (
        deriveMetalPersonaliseUrl(imgs.gold, 'silver') ||
        deriveMetalPersonaliseUrl(imgs.shared, 'silver') ||
        null
      )
    }
    return null
  }

  /**
   * Personalise-step thumb for nameplate / engraving.
   * Selected metal slot (or Gold↔Silver derived twin) → shared → null.
   */
  function resolvePersonaliseStepImage(source, colour) {
    var imgs = personaliseImagesFrom(source)
    var metalUrl = resolveMetalPersonaliseUrl(imgs, colour)
    if (metalUrl) return metalUrl
    return imgs.shared || null
  }

  /** TE merchandising card photos: colour-specific slots plus shared Card photo. */
  function cardImagesFrom(source) {
    if (!source) return { shared: null, gold: null, silver: null }
    return {
      shared: teImageUrl(source.cardImage || source.card_image),
      gold: teImageUrl(source.cardImageGold || source.card_image_gold),
      silver: teImageUrl(source.cardImageSilver || source.card_image_silver),
    }
  }

  /**
   * Cart / review / rail merchandising thumb for a committed Gold/Silver
   * nameplate. Explicit Card photo — Gold/Silver → Gold↔Silver filename twin
   * from the other card slot / shared Card photo → shared Card photo (front).
   * Personalise artboards are last-resort only (clay back, dual-window blanks)
   * when no Card photo exists. Never a theme asset_url.
   * Silver must not keep a Gold-named Card photo — that is the cart bug.
   */
  function resolveMetalCardImage(source, colour) {
    var cards = cardImagesFrom(source)
    var personalise = personaliseImagesFrom(source)
    var metal = normalizeNameplateMetal(colour)
    if (metal === 'gold') {
      return (
        cards.gold ||
        deriveMetalPersonaliseUrl(cards.silver, 'gold') ||
        deriveMetalPersonaliseUrl(cards.shared, 'gold') ||
        cards.shared ||
        resolveMetalPersonaliseUrl(personalise, 'gold') ||
        personalise.shared ||
        null
      )
    }
    if (metal === 'silver') {
      return (
        cards.silver ||
        deriveMetalPersonaliseUrl(cards.gold, 'silver') ||
        deriveMetalPersonaliseUrl(cards.shared, 'silver') ||
        cards.shared ||
        resolveMetalPersonaliseUrl(personalise, 'silver') ||
        null
      )
    }
    return cards.shared || null
  }

  /** Title, option values, SKU, and image URL — checkout can only show variant images. */
  function variantMetalHaystack(variant) {
    if (!variant) return ''
    return [
      variant.title,
      variant.name,
      variant.sku,
      variant.option1,
      variant.option2,
      variant.option3,
      variant.image,
    ]
      .map(function (part) {
        return String(part || '')
      })
      .join(' ')
  }

  /**
   * When the product has dedicated Gold/Silver variants (same price, not a
   * weight-tier size), pick the matching variant so Shopify cart *and* checkout
   * use that variant’s image. One-variant SKUs and sized urns stay as-is.
   * Checkout cannot render `_Cart_Image` — the variant featured image is the
   * only reliable colour match there. Merchant must assign Gold/Silver variant
   * images in Admin (TE Files / Card photo — Silver).
   */
  function variantIdForNameplateMetal(card, colour, currentId) {
    var metal = normalizeNameplateMetal(colour)
    var product = card && card.product
    if (!metal || !product || !Array.isArray(product.variants) || product.variants.length < 2) {
      return currentId
    }
    if (productHasWeightTierSizeImages(product, card._variantCutoffSettings)) return currentId
    var current = null
    var matches = []
    var token = new RegExp('\\b' + metal + '\\b', 'i')
    var i
    for (i = 0; i < product.variants.length; i++) {
      var v = product.variants[i]
      if (!v || v.available === false) continue
      if (String(v.id) === String(currentId)) current = v
      if (token.test(variantMetalHaystack(v))) matches.push(v)
    }
    if (matches.length !== 1) return currentId
    if (current && Number(matches[0].price) !== Number(current.price)) return currentId
    return matches[0].id
  }

  /**
   * Review Photo Tribute when the family skipped / did not upload a photo.
   * Dedicated blank-urn JPEG → shared Personalise step image (Wooded-Photo Blank).
   * Never Card photo or Shopify product photo (those often show a sample pet).
   */
  function resolveReviewBlankCardImage(source) {
    if (!source) return null
    var blank = teImageUrl(source.reviewBlankCardImage || source.review_blank_card_image)
    if (blank) return blank
    return teImageUrl(source.personaliseImage || source.personalise_image)
  }

  /**
   * Photo Tribute on Review (urn + photo ask / Photo Tribute naming).
   * Ink & Clay keep their own review path.
   */
  function reviewUsesBlankCardWhenNoPhoto(galleryItem, card) {
    var title = String(
      (galleryItem && galleryItem.title) ||
      (card && (card.title || card.styleTitle)) ||
      ''
    )
    if (/\bphoto\s+tribute\b/i.test(title)) return true
    var group = String((galleryItem && galleryItem.memorialGroupKey) || '').toLowerCase()
    if (group !== 'urn') return false
    if (card && card.needsPhoto) return true
    var product = card && card.product
    var settings = (card && card._variantCutoffSettings) || {
      needs_photo: card && card.needsPhoto,
      style_title: card && card.styleTitle,
    }
    return looksLikePhotoTributeProduct(product, settings)
  }

  /**
   * Frame PNG for compositing the client photo (nameplate thumb, sidebar, zoom).
   * Metal slot / derived twin → shared personalise → Photo mockup frame.
   * Not used on the photo upload step — that step uses resolvePhotoStepMockupFrame.
   */
  function resolvePersonaliseMockupFrame(source, colour) {
    if (!source) return null
    var imgs = personaliseImagesFrom(source)
    var metalUrl = resolveMetalPersonaliseUrl(imgs, colour)
    if (metalUrl) return metalUrl
    if (imgs.shared) return imgs.shared
    var metal = normalizeNameplateMetal(colour)
    if (!metal && imgs.gold) return imgs.gold
    if (!metal && imgs.silver) return imgs.silver
    return teImageUrl(source.photoMockupFrame || source.photo_mockup_frame)
  }

  /**
   * Photo-step card + large preview frame (also summary / review mockups).
   * When Gold/Silver is committed, prefer that metal’s personalise artboard
   * (including Gold↔Silver filename derivation) so sidebar + thumbs swap with
   * the nameplate. Do not fall through to a shared Photo mockup while metal is
   * committed — that PNG is often a single Gold bake.
   * No metal → Theme Editor Photo mockup frame → personalise fallback → null.
   */
  function resolvePhotoStepMockupFrame(source, colour) {
    if (!source) return null
    var metal = normalizeNameplateMetal(colour)
    var imgs = personaliseImagesFrom(source)
    var metalUrl = resolveMetalPersonaliseUrl(imgs, colour)
    if (metalUrl) return metalUrl
    if (metal) {
      // Committed metal but no artboard/token to swap — still avoid a wrong-metal
      // Photo mockup when a shared personalise exists (often the Gold twin).
      if (imgs.shared) return imgs.shared
      if (imgs.gold) return imgs.gold
      if (imgs.silver) return imgs.silver
    }
    var mockup = teImageUrl(source.photoMockupFrame || source.photo_mockup_frame)
    if (mockup) return mockup
    return resolvePersonaliseMockupFrame(source, colour)
  }

  /** Committed Gold/Silver on a product’s nameplate bucket — never entry.addons (may be another SKU). */
  function nameplateMetalForProduct(entry, productId) {
    if (!entry || productId == null || String(productId) === '') return null
    var buckets = entry.itemAddons && typeof entry.itemAddons === 'object' ? entry.itemAddons : null
    var bucket = buckets && buckets[String(productId)]
    var np = bucket && bucket.nameplate
    if (!np || String(np.choiceId || '') === 'no') return null
    return normalizeNameplateMetal(np.colour)
  }

  function firstAvailableVariant(product) {
    if (!product || !product.variants || !product.variants.length) return null
    for (var i = 0; i < product.variants.length; i++) {
      if (product.variants[i] && product.variants[i].available !== false) return product.variants[i]
    }
    // Do not return a sold-out variant — /cart/add.js 422s the whole booking.
    return null
  }

  /** Square memorial-grid thumbs. Request this width, not the Shopify master. */
  var MEMORIAL_GRID_THUMB_PX = 360
  /**
   * Summary / ashes-dest / small UI thumbs — same CDN width as the grid so
   * sidebar and follow-up cards reuse the warm browser cache (CSS scales down).
   */
  var MEMORIAL_SUMMARY_THUMB_PX = MEMORIAL_GRID_THUMB_PX
  /**
   * Review memorial gallery tiles (non-mockup). Same width as the grid so urn-step
   * warm hits the browser cache. Do not use MEMORIAL_ARTBOARD_PX here — that
   * upsizes Shopify product masters (e.g. Satin Pouch 1800px → ~140KB webp at
   * width=800) while TE card thumbs stay ~30–40KB.
   */
  var MEMORIAL_REVIEW_THUMB_PX = MEMORIAL_GRID_THUMB_PX
  /** Photo-addon card frame thumb (printed photo / engraving ask). */
  var PHOTO_ADDON_THUMB_PX = 240
  /**
   * Personalise / review mockup artboards and nameplate blanks.
   * Display and warmup must use this same width or the warm cache misses.
   */
  var MEMORIAL_ARTBOARD_PX = 800
  /** Goodbye Room hero + door — CSS max height is ~208px; 800 covers 2× retina. */
  var GOODBYE_VISUAL_PX = 800

  /**
   * Shopify CDN derivative for thumbs — 36px rail should not decode a 2000px master.
   * Leaves non-Shopify URLs unchanged.
   */
  function sizedShopifyImageUrl(url, width) {
    var src = String(url || '').trim()
    if (!src || !width) return src
    var w = Math.max(16, Math.round(Number(width) || 0))
    if (!w) return src
    if (/^(data:|blob:)/i.test(src)) return src
    var shopifyHost =
      /cdn\.shopify\.com/i.test(src) ||
      /shopifycdn\.com/i.test(src) ||
      /\/cdn\/shop\//i.test(src) ||
      /\/files\//i.test(src)
    if (!shopifyHost) return src
    if (/[?&]width=\d+/i.test(src)) src = src.replace(/([?&]width=)\d+/i, '$1' + w)
    else src += (src.indexOf('?') >= 0 ? '&' : '?') + 'width=' + w
    if (!/\.svg(\?|$)/i.test(src) && !/[?&]format=/i.test(src)) src += '&format=webp'
    return src
  }

  /* ------------------------ image warm / prefetch ------------------------ */

  /** Deduped Image() warm so step mounts hit disk/memory cache. */
  var warmedImageUrls = Object.create(null)

  function prefetchImageUrl(url, opts) {
    opts = opts || {}
    var src = String(url || '').trim()
    if (!src || /^(data:|blob:)/i.test(src)) return
    if (warmedImageUrls[src]) return
    warmedImageUrls[src] = 1
    try {
      var img = new Image()
      img.decoding = 'async'
      try {
        img.fetchPriority = opts.high ? 'high' : 'low'
      } catch (ePri) { /* older browsers */ }
      img.src = src
    } catch (eImg) { /* ignore */ }
  }

  function prefetchImageUrls(urls, opts) {
    if (!urls || !urls.length) return
    for (var i = 0; i < urls.length; i++) prefetchImageUrl(urls[i], opts)
  }

  function runWhenIdle(fn, timeoutMs) {
    if (typeof fn !== 'function') return
    var delay = timeoutMs == null ? 1400 : timeoutMs
    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(function () { fn() }, { timeout: delay })
    } else if (typeof window !== 'undefined') {
      window.setTimeout(fn, Math.min(120, delay))
    }
  }

  function pushWarmUrl(list, url) {
    var u = String(url || '').trim()
    if (u && list.indexOf(u) === -1) list.push(u)
  }

  /** Theme Editor memorial_item image fields used across grid / personalise / mockups. */
  var MEMORIAL_BLOCK_IMAGE_KEYS = [
    'card_image',
    'card_image_gold',
    'card_image_silver',
    'review_blank_card_image',
    'included_card_image',
    'swatch_image',
    'personalise_image',
    'personalise_image_gold',
    'personalise_image_silver',
    'photo_mockup_frame',
    'nameplate_preview_gold',
    'nameplate_preview_silver',
  ]

  var MEMORIAL_BLOCK_ARTBOARD_KEYS = {
    review_blank_card_image: 1,
    personalise_image: 1,
    personalise_image_gold: 1,
    personalise_image_silver: 1,
    photo_mockup_frame: 1,
    nameplate_preview_gold: 1,
    nameplate_preview_silver: 1,
  }

  /** Collect TE memorial images as grid thumbs vs full artboards (by field key). */
  function collectMemorialBlockWarmParts(config) {
    var thumbs = []
    var full = []
    function pushProductImage(product) {
      if (!product || typeof product !== 'object') return
      pushWarmUrl(thumbs, sizedShopifyImageUrl(product.image, MEMORIAL_GRID_THUMB_PX))
      var variants = product.variants || []
      for (var vi = 0; vi < Math.min(variants.length, 4); vi++) {
        if (variants[vi] && variants[vi].image) {
          pushWarmUrl(thumbs, sizedShopifyImageUrl(variants[vi].image, MEMORIAL_GRID_THUMB_PX))
        }
      }
    }
    var blocks = (config && config.blocks) || []
    for (var i = 0; i < blocks.length; i++) {
      var block = blocks[i]
      if (!block || (block.type !== 'memorial_item' && block.type !== 'memorial_group')) continue
      var s = block.settings || {}
      var k
      for (k = 0; k < MEMORIAL_BLOCK_IMAGE_KEYS.length; k++) {
        var key = MEMORIAL_BLOCK_IMAGE_KEYS[k]
        var val = s[key]
        if (!val) continue
        if (MEMORIAL_BLOCK_ARTBOARD_KEYS[key]) {
          pushWarmUrl(full, sizedShopifyImageUrl(val, MEMORIAL_ARTBOARD_PX))
          pushWarmUrl(thumbs, sizedShopifyImageUrl(val, PHOTO_ADDON_THUMB_PX))
          // Personalise intro thumb uses MEMORIAL_GRID_THUMB_PX — warm that width too or
          // the browser fetches a second CDN derivative on step mount (cache miss).
          if (
            key === 'personalise_image' ||
            key === 'personalise_image_gold' ||
            key === 'personalise_image_silver'
          ) {
            pushWarmUrl(thumbs, sizedShopifyImageUrl(val, MEMORIAL_GRID_THUMB_PX))
          }
        } else {
          pushWarmUrl(thumbs, sizedShopifyImageUrl(val, MEMORIAL_GRID_THUMB_PX))
        }
      }
      var n
      for (n = 2; n <= 8; n++) {
        pushWarmUrl(thumbs, sizedShopifyImageUrl(s['colour_' + n + '_image'], MEMORIAL_GRID_THUMB_PX))
        pushWarmUrl(thumbs, sizedShopifyImageUrl(s['colour_' + n + '_card_image'], MEMORIAL_GRID_THUMB_PX))
        pushProductImage(s['colour_' + n + '_product'])
      }
      for (n = 1; n <= 6; n++) {
        pushWarmUrl(thumbs, sizedShopifyImageUrl(s['variant_card_image_' + n], MEMORIAL_GRID_THUMB_PX))
      }
      pushProductImage(s.product)
      pushProductImage(s.included_product)
      // Collection-driven groups: warm first screen of product thumbs.
      var collLists = [s.collectionProducts, s.collectionSecondaryProducts]
      for (var ci = 0; ci < collLists.length; ci++) {
        var list = collLists[ci]
        if (!Array.isArray(list)) continue
        for (var pi = 0; pi < Math.min(list.length, 12); pi++) {
          pushProductImage(list[pi])
        }
      }
    }
    var portrait = config && config.memorials && config.memorials.portrait
    if (portrait) {
      pushWarmUrl(thumbs, sizedShopifyImageUrl(portrait.cardImage, MEMORIAL_GRID_THUMB_PX))
      pushProductImage(portrait.product)
    }
    return { thumbs: thumbs, full: full }
  }

  function collectGoodbyeRoomUrls(config) {
    var out = []
    var g = (config && config.goodbyeRoomVisuals) || {}
    pushWarmUrl(out, sizedShopifyImageUrl(g.heroUrl, GOODBYE_VISUAL_PX))
    pushWarmUrl(out, sizedShopifyImageUrl(g.secondaryUrl, GOODBYE_VISUAL_PX))
    return out
  }

  function collectNameplateBlankUrls(config) {
    var out = []
    pushWarmUrl(out, sizedShopifyImageUrl(config && config.nameplateBlankGold, MEMORIAL_ARTBOARD_PX))
    pushWarmUrl(out, sizedShopifyImageUrl(config && config.nameplateBlankSilver, MEMORIAL_ARTBOARD_PX))
    return out
  }

  /**
   * Gold + Silver personalise step images (and blank plates) — both metals must
   * be warm before the nameplate step mounts, or colour toggle waits on CDN.
   * Display uses MEMORIAL_GRID_THUMB_PX; blanks / dock plate use artboard width.
   */
  function collectPersonaliseStepWarmUrls(config) {
    var display = []
    var artboard = []
    function pushMetalPair(shared, gold, silver) {
      ;[shared, gold, silver].forEach(function (raw) {
        if (!raw) return
        pushWarmUrl(display, sizedShopifyImageUrl(raw, MEMORIAL_GRID_THUMB_PX))
        pushWarmUrl(artboard, sizedShopifyImageUrl(raw, MEMORIAL_ARTBOARD_PX))
      })
    }
    var blanks = collectNameplateBlankUrls(config)
    for (var bi = 0; bi < blanks.length; bi++) pushWarmUrl(artboard, blanks[bi])
    var blocks = (config && config.blocks) || []
    for (var i = 0; i < blocks.length; i++) {
      var block = blocks[i]
      if (!block || (block.type !== 'memorial_item' && block.type !== 'memorial_group')) continue
      var s = block.settings || {}
      pushMetalPair(s.personalise_image, s.personalise_image_gold, s.personalise_image_silver)
      pushMetalPair(null, s.nameplate_preview_gold, s.nameplate_preview_silver)
      pushMetalPair(s.card_image, s.card_image_gold, s.card_image_silver)
    }
    return { display: display, artboard: artboard }
  }

  /** Prefer personalise / metal URLs ahead of generic grid thumbs in a warm batch. */
  function prioritizePersonaliseUrls(urls, personaliseFirst) {
    if (!urls || !urls.length) return urls || []
    if (!personaliseFirst || !personaliseFirst.length) return urls.slice()
    var seen = Object.create(null)
    var out = []
    var i
    for (i = 0; i < personaliseFirst.length; i++) {
      var a = personaliseFirst[i]
      if (!a || seen[a]) continue
      seen[a] = 1
      out.push(a)
    }
    for (i = 0; i < urls.length; i++) {
      var b = urls[i]
      if (!b || seen[b]) continue
      seen[b] = 1
      out.push(b)
    }
    return out
  }

  /**
   * Warm critical wizard images without blocking first paint.
   * phase: 'boot' | 'arrival' | 'memorial' | 'personalise' | 'all'
   * opts.high — bump fetchPriority for images about to show (e.g. Goodbye Room).
   */
  function warmWizardImages(config, phase, opts) {
    opts = opts || {}
    var high = !!opts.high
    var p = phase || 'boot'
    var personaliseWarm = collectPersonaliseStepWarmUrls(config)
    if (p === 'boot' || p === 'arrival' || p === 'all') {
      prefetchImageUrls(collectGoodbyeRoomUrls(config), { high: high || p === 'arrival' })
    }
    if (p === 'boot' || p === 'personalise' || p === 'all') {
      prefetchImageUrls(collectNameplateBlankUrls(config), {
        high: high || p === 'personalise',
      })
      var eng = (config && config.engravingExampleVisuals) || {}
      prefetchImageUrl(sizedShopifyImageUrl(eng.imageUrl, MEMORIAL_ARTBOARD_PX), { high: false })
    }
    // Always warm Gold + Silver personalise step images as a pair — colour toggle
    // must not cold-fetch the alternate metal on first visit.
    if (p === 'boot' || p === 'memorial' || p === 'personalise' || p === 'all') {
      var metalHigh = high || p === 'personalise' || p === 'memorial'
      prefetchImageUrls(personaliseWarm.display, { high: metalHigh })
      prefetchImageUrls(personaliseWarm.artboard, {
        high: p === 'personalise' ? high : false,
      })
    }
    if (p === 'boot' || p === 'memorial' || p === 'personalise' || p === 'all') {
      var parts = collectMemorialBlockWarmParts(config)
      var thumbs = prioritizePersonaliseUrls(parts.thumbs, personaliseWarm.display)
      var full = prioritizePersonaliseUrls(parts.full, personaliseWarm.artboard)
      if (p === 'boot') {
        prefetchImageUrls(thumbs.slice(0, 24), { high: false })
      } else if (p === 'memorial') {
        prefetchImageUrls(thumbs.slice(0, 24), { high: high })
        prefetchImageUrls(thumbs.slice(24), { high: false })
        prefetchImageUrls(full.slice(0, 16), { high: false })
      } else {
        prefetchImageUrls(thumbs, { high: false })
        prefetchImageUrls(full, { high: high })
      }
    }
  }

  /**
   * Predictive warm based on the current funnel step.
   * Call when the step id changes (not on every keystroke re-render).
   */
  var lastWarmedStepKey = null
  function warmImagesForStep(config, state, stepId) {
    var id = String(stepId || '')
    var catId = state && state.transport && state.transport.categoryId
    var warmKey = id + '|' + String(catId || '')
    if (warmKey && warmKey === lastWarmedStepKey) return
    lastWarmedStepKey = warmKey

    // Goodbye Room: warm early; bump priority once Lonsdale is chosen or on that step.
    if (
      id === 'transport-category' ||
      id === 'transport-main' ||
      id === 'transport-sub' ||
      id === 'cremation-type' ||
      id === 'cremation-weight' ||
      id === 'pet'
    ) {
      var lonsdaleLikely = catId === 'lonsdale' || id === 'transport-sub'
      if (lonsdaleLikely) {
        warmWizardImages(config, 'arrival', { high: true })
      } else {
        runWhenIdle(function () { warmWizardImages(config, 'arrival', { high: false }) }, 200)
      }
      warmWizardImages(config, 'memorial', { high: false })
      // Start Gold/Silver personalise heroes during early funnel so memorial →
      // nameplate does not wait on CDN for either metal.
      runWhenIdle(function () { warmWizardImages(config, 'personalise', { high: false }) }, 400)
    }

    if (id.indexOf('memorial:') === 0 || id === 'transport-details' || id === 'return-speed') {
      warmWizardImages(config, 'memorial', { high: true })
      // Next screen is often nameplate personalise — warm Gold + Silver step images
      // now (not idle-delayed) so both metals are cached before Continue.
      warmWizardImages(config, 'personalise', { high: false })
    }

    if (id.indexOf(':addon:') !== -1) {
      // On the personalise step itself — high priority, no idle delay.
      // Nameplate colour tiles swap personalise_image_gold ↔ _silver; both must
      // already be in flight at fetchPriority=high on first paint.
      warmWizardImages(config, 'personalise', { high: true })
    }

    if (id === 'review' || id === 'return-method') {
      runWhenIdle(function () { warmWizardImages(config, 'personalise', { high: false }) }, 200)
    }
  }

  function scheduleWizardImageWarm(config, state, stepId) {
    warmWizardImages(config, 'boot', { high: false })
    warmImagesForStep(config, state, stepId)
  }

  /**
   * Calm memorial names on the card. Custom Theme Editor labels that are not
   * in this map are left unchanged.
   */
  var MEMORIAL_DISPLAY_TITLES = {
    'clay impression': 'Paw Clay Impression',
    'photo urn': 'Photo Memorial Urn',
    'teddy with clipping': 'Memorial Teddy with Fur Clipping',
    'teddy with fur clipping': 'Memorial Teddy with Fur Clipping',
    'teddy with a clipping': 'Memorial Teddy with Fur Clipping',
  }

  function refineMemorialDisplayTitle(title) {
    var raw = String(title || '').replace(/\s+/g, ' ').trim()
    if (!raw) return raw
    return MEMORIAL_DISPLAY_TITLES[raw.toLowerCase()] || raw
  }

  var CAPACITY_ROLE_FULL = 'full'
  var CAPACITY_ROLE_KEEPSAKE = 'keepsake'
  var CAPACITY_ROLE_SCATTER = 'scatter'

  var KEEPSAKE_CAPACITY_DEFAULTS = {
    kg: 1,
    label: 'Holds a small portion',
    detail:
      'Designed to hold a small portion of ashes. It may hold all the ashes of a pet up to approximately 1 kg.',
  }

  var LEGACY_KEEPSAKE_CAPACITY_LABELS = {
    'Keepsake \u00b7 holds a small portion': true,
    'Keepsake · holds a small portion': true,
    'Keepsake \u00b7 holds a small portion.': true,
    'Keepsake · holds a small portion.': true,
  }

  var KEEPSAKE_NOTICE_DEFAULTS = {
    bodyOnePouch:
      'Holds a small portion of {pet}\u2019s ashes. Remaining ashes go in a satin pouch \u2014 or add another urn for the balance.',
    bodyManyPouch:
      'Each holds a small portion of {pet}\u2019s ashes. Remaining ashes go in a satin pouch \u2014 or add another urn for the balance.',
    bodyOneUrn:
      'Holds a small portion of {pet}\u2019s ashes. Remaining ashes go in the urn you choose.',
    bodyManyUrn:
      'Each holds a small portion of {pet}\u2019s ashes. Remaining ashes go in the urn you choose.',
    bodySplit:
      'We\u2019ll divide {pet}\u2019s ashes between {keepsakes}.',
  }

  /** Last catalog from buildCatalog — used when selection helpers omit catalog. */
  var lastBuiltCatalog = null

  var MINI_HEART_STYLE_TITLE = 'Mini Heart Keepsake'

  var LEGACY_KEEPSAKE_NOTICE_BODIES = {
    'It holds a small portion of {pet}\u2019s ashes. The remaining ashes will be returned in the included Satin Pouch unless you choose a full-capacity urn.':
      KEEPSAKE_NOTICE_DEFAULTS.bodyOnePouch,
    'Each keepsake holds a small portion of {pet}\u2019s ashes. The remaining ashes will be returned in the included Satin Pouch unless you choose a full-capacity urn.':
      KEEPSAKE_NOTICE_DEFAULTS.bodyManyPouch,
    'Holds a small portion of {pet}\u2019s ashes. If you don\u2019t choose another urn, the rest is returned in the Satin Pouch.':
      KEEPSAKE_NOTICE_DEFAULTS.bodyOnePouch,
    "Holds a small portion of {pet}'s ashes. If you don't choose another urn, the rest is returned in the Satin Pouch.":
      KEEPSAKE_NOTICE_DEFAULTS.bodyOnePouch,
    'Each holds a small portion of {pet}\u2019s ashes. If you don\u2019t choose another urn, the rest is returned in the Satin Pouch.':
      KEEPSAKE_NOTICE_DEFAULTS.bodyManyPouch,
    "Each holds a small portion of {pet}'s ashes. If you don't choose another urn, the rest is returned in the Satin Pouch.":
      KEEPSAKE_NOTICE_DEFAULTS.bodyManyPouch,
    'It will hold a small portion of {pet}\u2019s ashes. The remaining ashes will be placed according to the urn arrangement you choose.':
      KEEPSAKE_NOTICE_DEFAULTS.bodyOneUrn,
    'Each keepsake will hold a small portion of {pet}\u2019s ashes. The remaining ashes will be placed according to the urn arrangement you choose.':
      KEEPSAKE_NOTICE_DEFAULTS.bodyManyUrn,
    'Holds a small portion of {pet}\u2019s ashes. Choose another urn for the rest, or it can be returned in the Satin Pouch.':
      KEEPSAKE_NOTICE_DEFAULTS.bodyOnePouch,
    'Each holds a small portion of {pet}\u2019s ashes. Choose another urn for the rest, or it can be returned in the Satin Pouch.':
      KEEPSAKE_NOTICE_DEFAULTS.bodyManyPouch,
    'Holds a small portion of {pet}\u2019s ashes. Choose another urn for the remaining ashes, or they can be returned in the Satin Pouch.':
      KEEPSAKE_NOTICE_DEFAULTS.bodyOnePouch,
    'Each holds a small portion of {pet}\u2019s ashes. Choose another urn for the remaining ashes, or they can be returned in the Satin Pouch.':
      KEEPSAKE_NOTICE_DEFAULTS.bodyManyPouch,
    "Holds a small portion of {pet}'s ashes. Choose another urn for the remaining ashes, or they can be returned in the Satin Pouch.":
      KEEPSAKE_NOTICE_DEFAULTS.bodyOnePouch,
    "Each holds a small portion of {pet}'s ashes. Choose another urn for the remaining ashes, or they can be returned in the Satin Pouch.":
      KEEPSAKE_NOTICE_DEFAULTS.bodyManyPouch,
    'Holds a small portion of {pet}\u2019s ashes. The rest goes in the urn you choose.':
      KEEPSAKE_NOTICE_DEFAULTS.bodyOneUrn,
    'Each holds a small portion of {pet}\u2019s ashes. The rest goes in the urn you choose.':
      KEEPSAKE_NOTICE_DEFAULTS.bodyManyUrn,
    'Holds a small portion of {pet}\u2019s ashes. The remaining ashes go in the urn you choose.':
      KEEPSAKE_NOTICE_DEFAULTS.bodyOneUrn,
    'Each holds a small portion of {pet}\u2019s ashes. The remaining ashes go in the urn you choose.':
      KEEPSAKE_NOTICE_DEFAULTS.bodyManyUrn,
    'Holds a small portion of {pet}\u2019s ashes. Remaining can go in another urn or be returned.':
      KEEPSAKE_NOTICE_DEFAULTS.bodyOnePouch,
    "Holds a small portion of {pet}'s ashes. Remaining can go in another urn or be returned.":
      KEEPSAKE_NOTICE_DEFAULTS.bodyOnePouch,
    'Each holds a small portion of {pet}\u2019s ashes. Remaining can go in another urn or be returned.':
      KEEPSAKE_NOTICE_DEFAULTS.bodyManyPouch,
    "Each holds a small portion of {pet}'s ashes. Remaining can go in another urn or be returned.":
      KEEPSAKE_NOTICE_DEFAULTS.bodyManyPouch,
  }

  function isMiniHeartProduct(product) {
    var hay =
      String((product && product.title) || '') +
      ' ' +
      String((product && product.handle) || '')
    return /mini\s*heart/i.test(hay)
  }

  function isSatinPouchProduct(product) {
    var hay =
      String((product && product.title) || '') +
      ' ' +
      String((product && product.handle) || '')
    return /satin/i.test(hay) && /(pouch|tribute)/i.test(hay)
  }

  function normalizeCapacityRole(raw) {
    var v = String(raw == null ? '' : raw).trim().toLowerCase().replace(/[_-]+/g, ' ')
    if (!v) return ''
    if (v === 'full' || v === 'full capacity' || v === 'full capacity urn') return CAPACITY_ROLE_FULL
    if (v === 'keepsake' || v === 'keepsake portion') return CAPACITY_ROLE_KEEPSAKE
    if (v === 'scatter' || v === 'scatter container') return CAPACITY_ROLE_SCATTER
    return ''
  }

  function resolveCapacityRole(product, teSettings, included) {
    var te = teSettings || {}
    var explicit = normalizeCapacityRole(te.ashes_capacity_role)
    if (explicit) return explicit
    if (included) return CAPACITY_ROLE_SCATTER
    if (isMiniHeartProduct(product)) return CAPACITY_ROLE_KEEPSAKE
    if (isSatinPouchProduct(product)) return CAPACITY_ROLE_SCATTER
    return CAPACITY_ROLE_FULL
  }

  /**
   * Customer-facing wizard label. Style title is the single source of truth.
   * Fallback: Mini Heart heuristic, then label override, then Shopify title.
   * Cart / checkout / order lines still use the Shopify product title.
   */
  function getMemorialDisplayTitle(item) {
    if (!item) return 'Item'
    var style = String(item.styleTitle || item.style_title || '').trim()
    if (!style && item.settings) style = String(item.settings.style_title || '').trim()
    if (!style && item._swatchMeta) style = String(item._swatchMeta.styleTitle || '').trim()
    if (style) return style
    var product = item.product && typeof item.product === 'object' ? item.product : item
    if (isMiniHeartProduct(product) || isMiniHeartProduct(item)) return MINI_HEART_STYLE_TITLE
    var override = String(item.labelOverride || item.label_override || '').trim()
    if (!override && item.settings) override = String(item.settings.label_override || '').trim()
    if (override) return refineMemorialDisplayTitle(override)
    var title = (product && product.title) || item.title || ''
    return refineMemorialDisplayTitle(title) || 'Item'
  }

  /**
   * Customer-facing summary / review label. Style title, then ` × n` when the
   * family chose a quantity greater than one. Never a leading count, never a
   * pluralised title. Cart / checkout still use the Shopify product title.
   */
  function formatMemorialSummaryLabel(styleTitle, quantity, offersQuantity) {
    var title = String(styleTitle || '')
      .replace(/^\d+\s+/, '')
      .replace(/\s*[×x]\s*\d+\s*$/i, '')
      .replace(/\s+/g, ' ')
      .trim()
    if (!title) return ''
    if (!offersQuantity) return title
    var n = Math.floor(Number(quantity) || 0)
    if (n > 1) return title + ' \u00d7 ' + n
    return title
  }

  function styleTitleFromBlock(itemBlock, product) {
    var te = (itemBlock && itemBlock.settings) || {}
    var style = String(te.style_title || '').trim()
    if (style) return style
    if (isMiniHeartProduct(product)) return MINI_HEART_STYLE_TITLE
    return ''
  }

  function teCardImageFromBlock(itemBlockOrSettings) {
    var s = memorialSettingsObject(itemBlockOrSettings)
    return s ? teImageUrl(s.card_image) : null
  }

  /**
   * Theme Editor Card photo for one variant row on a single-product pack card.
   * Index 0 = Colour 1; index 1 = colour_2_card_image / Variant 2, etc.
   * Numbered quantity slots inherit the parent Card photo when their slot photo
   * is blank (same rule as makeColourSlotItemBlock).
   */
  function teCardImageForVariantIndex(itemBlockOrSettings, variantIndex) {
    var s = memorialSettingsObject(itemBlockOrSettings)
    if (!s) return null
    var idx = variantIndex == null ? 0 : Math.floor(Number(variantIndex))
    if (!isFinite(idx) || idx < 0) idx = 0
    var slot = idx + 1
    if (slot === 1) {
      var primary = teImageUrl(s.card_image)
      if (primary) return primary
      return teVariantCardImageFromSettings(s, idx)
    }
    if (slot > TE_SWATCH_COLOUR_SLOT_MAX) return null
    var prefix = 'colour_' + slot + '_'
    var slotCard = teImageUrl(s[prefix + 'card_image'])
    if (slotCard) return slotCard
    if (normalizeSwatchNumber(s[prefix + 'number'])) {
      var inherited = teImageUrl(s.card_image)
      if (inherited) return inherited
    }
    var fromVariantSlot = teVariantCardImageFromSettings(s, idx)
    if (fromVariantSlot) return fromVariantSlot
    return teImageUrl(s.card_image)
  }

  function variantIndexOnProduct(product, variant) {
    if (!product || !variant || !Array.isArray(product.variants)) return -1
    var id = String(variant.id)
    var i
    for (i = 0; i < product.variants.length; i++) {
      if (product.variants[i] && String(product.variants[i].id) === id) return i
    }
    return -1
  }

  /** Theme Editor “Variant N card photo” for Admin variant order (1-based slots). */
  function teVariantCardImageFromSettings(itemBlockOrSettings, variantIndex) {
    var s = memorialSettingsObject(itemBlockOrSettings)
    if (!s || variantIndex == null || variantIndex < 0) return null
    var n = variantIndex + 1
    if (n < 1 || n > VARIANT_MAX_KG_SLOT_COUNT) return null
    return teImageUrl(s['variant_card_image_' + n])
  }

  /**
   * Colour / quantity families keep one Card photo (or colour-slot Card photo).
   * Size is auto-picked silently — they are not Photo Tribute.
   */
  function productKeepsStableCardPhoto(itemBlockOrSettings) {
    var block = memorialItemBlock(itemBlockOrSettings)
    if (!block) return false
    if (teSwatchesEnabled(block) === true) return true
    var s = block.settings || {}
    return !!normalizeSwatchGroupKey(s.swatch_group)
  }

  /**
   * Photo Tribute naming / “Ask for photo upload”.
   * Do not treat Bio Scatter, Mini Heart, or other kg-cutoff urns as Photo Tribute
   * just because they auto-pick a size.
   */
  function looksLikePhotoTributeProduct(product, itemBlockOrSettings) {
    var s = memorialSettingsObject(itemBlockOrSettings) || {}
    if (truthy(s.needs_photo)) return true
    var style = String(s.style_title || '').replace(/\s+/g, ' ')
    var title = String((product && product.title) || '').replace(/\s+/g, ' ')
    var handle = String((product && product.handle) || '').toLowerCase()
    if (/\bphoto\s+(tribute|memorial(?:\s+urn)?|urn)\b/i.test(style + ' ' + title)) return true
    if (/photo-tribute|photo-memorial|photo-pet-urn|(?:^|-)photo-urn/.test(handle)) return true
    return false
  }

  /**
   * Cascade B gate. Two+ kg-cutoff size variants is not enough — Bio Scatter
   * also has those and must stay on cascade A (Card photo first).
   */
  function productHasWeightTierSizeImages(product, itemBlockOrSettings) {
    if (productKeepsStableCardPhoto(itemBlockOrSettings)) return false
    if (!looksLikePhotoTributeProduct(product, itemBlockOrSettings)) return false
    var n = product && Array.isArray(product.variants) ? product.variants.length : 0
    if (n < 2) return false
    var packed = variantMaxKgCutoffs(itemBlockOrSettings, product)
    return packed.cutoffs.length >= 2
  }

  /**
   * Grid photo — two cascades. Do not collapse into one global order.
   *
   *   A. Default (Rainbow, Forever Paws, Classic colours, Mini Heart, Bio Scatter,
   *      Paws Tealight, Ink Paw/Nose, frames, clay, quantity packs):
   *      Theme Editor Card photo (or colour-slot Card photo) → Shopify
   *      variant.image → product.image. Kg auto-pick may still choose a SKU;
   *      the grid photo does not follow that size.
   *
   *   B. Photo Tribute only (2+ kg-cutoff size variants, not a colour family):
   *      filled Variant N card photo → that variant’s Shopify image → Card photo
   *      → product.image. Size labels stay hidden.
   *
   * Do not freeze Photo Tribute on one Card mockup. Do not let Shopify beat
   * Card photo on cascade A items.
   */
  function resolveWeightTierDisplayImage(product, variant, itemBlockOrSettings, fallbacks) {
    fallbacks = fallbacks || {}
    var variantImg = teImageUrl(variant && variant.image)
    var teCard = fallbacks.teCard ? teImageUrl(fallbacks.teCard) : null
    if (!teCard) teCard = teCardImageFromBlock(itemBlockOrSettings)
    var colourSlot = fallbacks.colourSlotImage ? teImageUrl(fallbacks.colourSlotImage) : null
    var teFace = colourSlot || teCard
    var productImg = fallbacks.productImage ? teImageUrl(fallbacks.productImage) : null

    // Cascade B — Photo Tribute. Variant N photos are ignored on cascade A.
    if (productHasWeightTierSizeImages(product, itemBlockOrSettings)) {
      var idx = variantIndexOnProduct(product, variant)
      var teSlot = teVariantCardImageFromSettings(itemBlockOrSettings, idx)
      if (teSlot) return teSlot
      if (variantImg) return variantImg
      if (teCard) return teCard
      return productImg || null
    }

    // Cascade A — Card photo first for every other memorial card.
    if (teFace) return teFace
    if (variantImg) return variantImg
    return productImg || null
  }

  function applyCapacityToCard(card, itemBlock, included) {
    if (!card) return card
    var te = (itemBlock && itemBlock.settings) || {}
    var role = resolveCapacityRole(card.product, te, included)
    card.capacityRole = role
    card.capacityKg = numOrNull(te.ashes_capacity_kg)
    card.capacityLabel = String(te.ashes_capacity_label || '').trim()
    card.capacityDetail = String(te.ashes_capacity_detail || '').trim()
    if (LEGACY_KEEPSAKE_CAPACITY_LABELS[card.capacityLabel]) card.capacityLabel = ''
    if (role === CAPACITY_ROLE_KEEPSAKE) {
      // Explicit 0 = always-portion tiny keepsake; only default when unset.
      if (card.capacityKg == null) card.capacityKg = KEEPSAKE_CAPACITY_DEFAULTS.kg
      if (!card.capacityLabel) card.capacityLabel = KEEPSAKE_CAPACITY_DEFAULTS.label
      if (!card.capacityDetail) card.capacityDetail = KEEPSAKE_CAPACITY_DEFAULTS.detail
    }
    return card
  }

  function itemCapacityRole(it) {
    var role = normalizeCapacityRole(it && it.capacityRole)
    return role || CAPACITY_ROLE_FULL
  }

  function entryPlacementWeightKg(entry) {
    var n = Number(entry && entry._placementWeightKg)
    return isFinite(n) && n > 0 ? n : null
  }

  /** Bind pet weight used for keepsake portion vs full-hold placement. */
  function bindPlacementWeight(entry, stateOrWeight) {
    if (!entry) return null
    var w =
      stateOrWeight != null && typeof stateOrWeight === 'object'
        ? petWeightKg(stateOrWeight)
        : numOrNull(stateOrWeight)
    if (w != null && w > 0) entry._placementWeightKg = w
    return entryPlacementWeightKg(entry)
  }

  /**
   * Approximate capacity kg for a keepsake. Explicit 0 means always-portion
   * (no full-capacity intent). Unset defaults to Mini Heart’s 1 kg.
   */
  function keepsakeCapacityKg(it) {
    var n = numOrNull(it && it.capacityKg)
    if (n != null && n >= 0) return n
    return KEEPSAKE_CAPACITY_DEFAULTS.kg
  }

  /**
   * Keepsake with a positive capacity that can hold this pet’s ashes
   * (weight ≤ capacity). Candle holders / tealights with e.g. 6 kg use this.
   */
  function keepsakeActsAsFullCapacity(it, weightKg) {
    if (!it || itemCapacityRole(it) !== CAPACITY_ROLE_KEEPSAKE) return false
    var cap = keepsakeCapacityKg(it)
    if (!(cap > 0) || !(weightKg > 0)) return false
    return weightKg <= cap
  }

  /**
   * True when this configured keepsake should use portion / remaining-pouch
   * placement: capacity ≤ 0, unknown weight, or pet over capacity.
   */
  function keepsakeActsAsPortion(it, weightKg) {
    if (!it || itemCapacityRole(it) !== CAPACITY_ROLE_KEEPSAKE) return false
    return !keepsakeActsAsFullCapacity(it, weightKg)
  }

  /**
   * Hold limit for overflow vs full-ashes: keepsake TE kg, else the smaller of
   * ashes_capacity_kg and the auto-picked variant max kg.
   */
  function vesselCapacityKg(it) {
    if (!it) return null
    if (itemCapacityRole(it) === CAPACITY_ROLE_KEEPSAKE) return keepsakeCapacityKg(it)
    var a = numOrNull(it.capacityKg)
    var b = numOrNull(it.weightMaxKg)
    var caps = []
    if (a != null && a > 0) caps.push(a)
    if (b != null && b > 0) caps.push(b)
    if (!caps.length) return null
    return Math.min.apply(null, caps)
  }

  /** True when this vessel can hold the whole pet (unknown full urns still do). */
  function vesselHoldsAllAshes(it, weightKg) {
    if (!it) return false
    var role = itemCapacityRole(it)
    if (role === CAPACITY_ROLE_SCATTER) return false
    if (role === CAPACITY_ROLE_KEEPSAKE) return keepsakeActsAsFullCapacity(it, weightKg)
    var cap = vesselCapacityKg(it)
    if (cap == null || !(cap > 0)) return true
    if (!(weightKg > 0)) return true
    return weightKg <= cap
  }

  function isFullCapacityItem(it, weightKg) {
    return vesselHoldsAllAshes(it, weightKg)
  }

  function isKeepsakeItem(it, weightKg) {
    if (!it) return false
    if (itemCapacityRole(it) === CAPACITY_ROLE_SCATTER) return false
    return !vesselHoldsAllAshes(it, weightKg)
  }

  function configuredKeepsakeItems(entry) {
    return ((entry && entry.items) || []).filter(function (it) {
      return !!(it && itemCapacityRole(it) === CAPACITY_ROLE_KEEPSAKE)
    })
  }

  function fullCapacityItems(entry) {
    var weight = entryPlacementWeightKg(entry)
    return ((entry && entry.items) || []).filter(function (it) {
      return isFullCapacityItem(it, weight)
    })
  }

  function keepsakeItems(entry) {
    var weight = entryPlacementWeightKg(entry)
    var placementCount = urnRoleVesselCount(entry)
    return ((entry && entry.items) || []).filter(function (it) {
      if (!it) return false
      if (itemCapacityRole(it) === CAPACITY_ROLE_SCATTER) return false
      if (itemCapacityRole(it) === CAPACITY_ROLE_KEEPSAKE) {
        return isKeepsakeItem(it, weight)
      }
      // Over-capacity Classic etc. only auto-portion when they are the sole
      // urn. Two full-role urns must answer the ashes-placement question.
      if (placementCount >= 2 && isUrnRoleItem(it)) return false
      return isKeepsakeItem(it, weight)
    })
  }

  function isPouchLikeItem(it) {
    if (!it) return false
    if (it.included) return true
    if (itemCapacityRole(it) === CAPACITY_ROLE_SCATTER) return true
    return isSatinPouchProduct({
      title: it.label || it.styleTitle || it.shopifyTitle || '',
      handle: '',
    })
  }

  /** Selected vessels that can hold ashes (not the included satin pouch). */
  function ashHoldingItems(entry) {
    return ((entry && entry.items) || []).filter(function (it) {
      return it && !isPouchLikeItem(it)
    })
  }

  function keepsakeUnitCount(entry) {
    var n = 0
    var i
    var items = keepsakeItems(entry)
    for (i = 0; i < items.length; i++) {
      var q = Math.floor(Number(items[i].quantity) || 1)
      n += q > 0 ? q : 1
    }
    return n
  }

  function fullCapacityVesselCount(entry) {
    var items = fullCapacityItems(entry)
    var n = 0
    var i
    for (i = 0; i < items.length; i++) {
      var it = items[i]
      if (!it) continue
      if (it.included) {
        n += 1
        continue
      }
      var q = Math.floor(Number(it.quantity) || 1)
      n += q > 0 ? q : 1
    }
    return n
  }

  /** Full-role urns already in the selection, including over-capacity ones. */

  function isUrnRoleItem(it) {
    if (!it || it.included) return false
    var role = itemCapacityRole(it)
    return role !== CAPACITY_ROLE_KEEPSAKE && role !== CAPACITY_ROLE_SCATTER
  }

  function urnRoleVesselCount(entry) {
    var items = (entry && entry.items) || []
    var n = 0
    var i
    for (i = 0; i < items.length; i++) {
      var it = items[i]
      if (!isUrnRoleItem(it)) continue
      var q = Math.floor(Number(it.quantity) || 1)
      n += q > 0 ? q : 1
    }
    return n
  }

  function placementUrnItems(entry) {
    return ((entry && entry.items) || []).filter(isUrnRoleItem)
  }

  function placementUrnSelectionKey(entry) {
    return placementUrnItems(entry)
      .map(function (it) {
        return it && it.productId != null ? String(it.productId) : ''
      })
      .filter(Boolean)
      .sort()
      .join('|')
  }

  function needsAshesPlacementChoice(entry) {
    return urnRoleVesselCount(entry) >= 2
  }

  function fullCapacitySelectionKey(entry) {
    return fullCapacityItems(entry)
      .map(function (it) {
        return it && it.productId != null ? String(it.productId) : ''
      })
      .filter(Boolean)
      .sort()
      .join('|')
  }

  function selectionDisplayLabel(it) {
    if (!it) return 'Urn'
    var style = String(it.styleTitle || '').trim()
    if (style) return style
    var label = String(it.label || '').trim()
    return label || 'Urn'
  }

  function placementShortTitle(title) {
    var s = String(title || '').replace(/\s+/g, ' ').trim()
    if (!s) return s
    return s.replace(/\s+keepsake$/i, '').trim() || s
  }

  function productTitle(product, override) {
    return getMemorialDisplayTitle({
      product: product,
      labelOverride: override,
    })
  }

  /** Short label for personalise-step headings (pipe suffixes, brand prefix, length). */
  function personaliseItemLabel(label) {
    var s = String(label || '').replace(/\s+/g, ' ').trim()
    if (!s) return null
    if (s.indexOf('|') !== -1) s = s.split('|')[0].trim()
    s = s.replace(/^paws to heaven\s+/i, '')
    if (s.length > 48) {
      var cut = s.slice(0, 45)
      var sp = cut.lastIndexOf(' ')
      if (sp > 20) cut = cut.slice(0, sp)
      s = cut + '\u2026'
    }
    return s
  }

  function petWeightKg(state) {
    var w = state && state.pet && state.pet.weightKg
    var n = Number(w)
    return isFinite(n) && n > 0 ? n : null
  }

  /**
   * Variant metafield custom.max_pet_weight_kg → sparse list [{ index, maxKg }, …].
   * Last-resort fallback when TE slot and title parse are both blank for that variant.
   */
  function variantMetafieldMaxKgCutoffs(product) {
    if (!product || !Array.isArray(product.variants)) return []
    var out = []
    for (var i = 0; i < product.variants.length; i++) {
      var maxKg = numOrNull(product.variants[i] && product.variants[i].maxPetWeightKg)
      if (maxKg == null || maxKg < 0) continue
      out.push({ index: i, maxKg: maxKg })
    }
    return out
  }

  /** Theme Editor Variant 1–6 max kg slots (manual override; match Shopify Admin variant order). */
  function variantTeMaxKgCutoffs(itemBlockOrSettings) {
    var s = memorialSettingsObject(itemBlockOrSettings)
    if (!s) return []
    var out = []
    for (var i = 1; i <= VARIANT_MAX_KG_SLOT_COUNT; i++) {
      var maxKg = numOrNull(s['variant_max_kg_' + i])
      if (maxKg == null || maxKg < 0) continue
      out.push({ index: i - 1, maxKg: maxKg })
    }
    return out
  }

  /**
   * Parse max pet weight (kg) from a variant title / option label.
   * Examples:
   *   "Small - up to 12kg" → 12
   *   "Medium - up to 30 kg" → 30
   *   "Up to 20kg" → 20
   *   "0–10kg" / "Small (0-10 kg)" → 10 (range high end)
   */
  function parseMaxKgFromVariantLabel(label) {
    if (label == null || label === '') return null
    var s = String(label)

    // "up to 12kg" / "Up to 30 kg" (Bright Gold–style size names)
    var upTo = s.match(/up\s*to\s*(\d+(?:\.\d+)?)\s*kgs?\b/i)
    if (upTo) {
      var nUp = Number(upTo[1])
      return isFinite(nUp) && nUp >= 0 ? nUp : null
    }

    // "max 12kg" / "maximum 30 kg"
    var maxWord = s.match(/\bmax(?:imum)?\s*(\d+(?:\.\d+)?)\s*kgs?\b/i)
    if (maxWord) {
      var nMax = Number(maxWord[1])
      return isFinite(nMax) && nMax >= 0 ? nMax : null
    }

    // Range high end: "0-12kg", "0–30 kg", "1 to 40 kg"
    var range = s.match(/(\d+(?:\.\d+)?)\s*(?:-|–|—|to)\s*(\d+(?:\.\d+)?)\s*kgs?\b/i)
    if (range) {
      var hi = Number(range[2])
      return isFinite(hi) && hi >= 0 ? hi : null
    }

    return null
  }

  /** Join title + option fields (option1–3 if hydrated) for kg parsing. */
  function variantLabelForMaxKgParse(variant) {
    if (!variant) return ''
    var parts = []
    if (variant.title) parts.push(String(variant.title))
    if (variant.option1) parts.push(String(variant.option1))
    if (variant.option2) parts.push(String(variant.option2))
    if (variant.option3) parts.push(String(variant.option3))
    if (variant.name && String(variant.name) !== String(variant.title || '')) {
      parts.push(String(variant.name))
    }
    return parts.join(' ')
  }

  /** Sparse cutoffs parsed from each variant’s title/options. */
  function variantTitleMaxKgCutoffs(product) {
    if (!product || !Array.isArray(product.variants)) return []
    var out = []
    for (var i = 0; i < product.variants.length; i++) {
      var maxKg = parseMaxKgFromVariantLabel(variantLabelForMaxKgParse(product.variants[i]))
      if (maxKg == null || maxKg < 0) continue
      out.push({ index: i, maxKg: maxKg })
    }
    return out
  }

  /**
   * Per-variant max kg cutoffs. Priority for each variant index:
   *   1. Theme Editor Variant N max kg (manual override) when that slot is set
   *   2. Parsed from variant title/option (e.g. "up to 12kg")
   *   3. Metafield custom.max_pet_weight_kg
   * Returns { cutoffs: [{ index, maxKg, source }], source: 'te'|'title'|'metafield'|'mixed'|null }.
   */
  function variantMaxKgCutoffs(itemBlockOrSettings, product) {
    var teByIndex = {}
    variantTeMaxKgCutoffs(itemBlockOrSettings).forEach(function (c) {
      teByIndex[c.index] = c.maxKg
    })
    var titleByIndex = {}
    variantTitleMaxKgCutoffs(product).forEach(function (c) {
      titleByIndex[c.index] = c.maxKg
    })
    var metaByIndex = {}
    variantMetafieldMaxKgCutoffs(product).forEach(function (c) {
      metaByIndex[c.index] = c.maxKg
    })

    var n = product && Array.isArray(product.variants) ? product.variants.length : 0
    var out = []
    var sourceSeen = {}
    for (var i = 0; i < n; i++) {
      var maxKg = null
      var source = null
      if (teByIndex[i] != null) {
        maxKg = teByIndex[i]
        source = 'te'
      } else if (titleByIndex[i] != null) {
        maxKg = titleByIndex[i]
        source = 'title'
      } else if (metaByIndex[i] != null) {
        maxKg = metaByIndex[i]
        source = 'metafield'
      }
      if (maxKg == null || source == null) continue
      out.push({ index: i, maxKg: maxKg, source: source })
      sourceSeen[source] = true
    }

    var keys = Object.keys(sourceSeen)
    var overall = !keys.length ? null : keys.length === 1 ? keys[0] : 'mixed'
    return { cutoffs: out, source: overall }
  }

  /**
   * Pick a memorial product variant from kg cutoffs + pet weight.
   * Cutoffs merge TE override → title parse → metafield (see variantMaxKgCutoffs).
   * Algorithm: smallest max_kg >= weight among available variants with a cutoff.
   * When every available variant has a cutoff and weight exceeds all → exceedsAll.
   * A successful match sets autoSelected (safe to skip the Options dropdown).
   */
  function resolveVariantByWeightCutoffs(product, weightKg, itemBlockOrSettings) {
    var packed = variantMaxKgCutoffs(itemBlockOrSettings, product)
    var cutoffs = packed.cutoffs
    var cutoffSource = packed.source
    var result = {
      variant: null,
      autoSelected: false,
      exceedsAll: false,
      hasCutoffs: cutoffs.length > 0,
      note: '',
      source: null,
      maxKg: null,
    }
    if (!product || !Array.isArray(product.variants) || !product.variants.length) return result
    if (!cutoffs.length) return result

    var weight = Number(weightKg)
    var hasWeight = isFinite(weight) && weight > 0
    var availableIndexes = []
    for (var vi = 0; vi < product.variants.length; vi++) {
      var v = product.variants[vi]
      if (v && v.available !== false) availableIndexes.push(vi)
    }
    if (!availableIndexes.length) {
      for (var uj = 0; uj < product.variants.length; uj++) availableIndexes.push(uj)
    }

    var cutoffByIndex = {}
    var sourceByIndex = {}
    cutoffs.forEach(function (c) {
      cutoffByIndex[c.index] = c.maxKg
      sourceByIndex[c.index] = c.source || cutoffSource
    })

    var coveredAvailable = availableIndexes.every(function (idx) {
      return cutoffByIndex[idx] != null
    })

    if (hasWeight) {
      var best = null
      for (var ci = 0; ci < cutoffs.length; ci++) {
        var slot = cutoffs[ci]
        var variant = product.variants[slot.index]
        if (!variant || variant.available === false) continue
        if (slot.maxKg < weight) continue
        if (!best || slot.maxKg < best.maxKg) {
          best = {
            variant: variant,
            maxKg: slot.maxKg,
            source: slot.source || sourceByIndex[slot.index] || cutoffSource,
          }
        }
      }
      if (best) {
        result.variant = best.variant
        result.autoSelected = true
        result.source = best.source || cutoffSource
        result.maxKg = best.maxKg
        result.note = 'Suitable for pets up to ' + best.maxKg + ' kg.'
        return result
      }
      if (coveredAvailable) {
        var maxConfigured = 0
        availableIndexes.forEach(function (idx) {
          var m = cutoffByIndex[idx]
          if (m != null && m > maxConfigured) maxConfigured = m
        })
        result.exceedsAll = true
        result.note = maxConfigured > 0
          ? 'Suitable for pets up to ' + maxConfigured + ' kg.'
          : 'Not suitable for this pet’s weight.'
        // Prefer the largest available cutoff variant for display / messaging.
        var largest = null
        availableIndexes.forEach(function (idx) {
          var max = cutoffByIndex[idx]
          var lv = product.variants[idx]
          if (max == null || !lv) return
          if (!largest || max > largest.maxKg) {
            largest = {
              variant: lv,
              maxKg: max,
              source: sourceByIndex[idx] || cutoffSource,
            }
          }
        })
        if (largest) {
          result.variant = largest.variant
          result.maxKg = largest.maxKg
          var src = largest.source || cutoffSource
          result.source = src ? String(src) + '_exceeded' : 'exceeded'
        }
        return result
      }
    }

    return result
  }

  /**
   * Resolve the variant for a memorial card from merged cutoffs (TE → title →
   * metafield), else first available. `autoSelected` is true only for a
   * successful cutoff match (safe to skip the Options dropdown).
   */
  function resolveMemorialVariant(product, weightKg, itemBlockOrSettings) {
    var cutoff = resolveVariantByWeightCutoffs(product, weightKg, itemBlockOrSettings)
    if (cutoff.autoSelected || cutoff.exceedsAll) return cutoff
    var first = firstAvailableVariant(product)
    return {
      variant: first,
      autoSelected: false,
      exceedsAll: false,
      hasCutoffs: cutoff.hasCutoffs,
      note: cutoff.note || '',
      source: first ? 'first' : null,
      maxKg: cutoff.maxKg != null ? cutoff.maxKg : null,
    }
  }

  function interpolatePet(text, state) {
    var raw = state && state.pet && state.pet.name
      ? String(state.pet.name).replace(/\s+/g, ' ').trim().split(' ')[0]
      : ''
    // Match wizard personalize(): ignore numeric-only / non-letter names (e.g. "22").
    var name = raw && /[A-Za-zÀ-ÖØ-öø-ÿ]/.test(raw) ? raw : 'your pet'
    // Use {pet} like the rest of the wizard. Avoid {{ … }} / [pet_name] in Theme
    // Editor schema defaults — Shopify flags those as invalid dynamic sources.
    return String(text || '')
      .replace(/\{pet\}/gi, name)
      .replace(/\[\s*pet_name\s*\]/gi, name)
      .replace(/\{\{\s*pet_name\s*\}\}/gi, name)
  }

  /** Rewrite Mini Heart–hardcoded TE headings so the notice uses the selected style. */
  function rewriteLegacyKeepsakeNoticeHeading(heading, many) {
    var h = String(heading || '').trim()
    var next = many ? '{keepsakes}' : '{keepsake}'
    if (!h) return next
    if (/^About the Mini Heart( Keepsakes?)?$/i.test(h)) return next
    if (/^About the \{keepsakes\}$/i.test(h)) return next
    if (/^About the \{keepsake\}$/i.test(h)) return next
    return h
  }

  function rewriteLegacyKeepsakeNoticeBody(body, fallback) {
    var b = String(body || '').trim()
    if (!b) return fallback
    if (LEGACY_KEEPSAKE_NOTICE_BODIES[b]) return LEGACY_KEEPSAKE_NOTICE_BODIES[b]
    return b
  }

  function uniqueStyleTitlesFromItems(items) {
    var seen = {}
    var names = []
    var i
    for (i = 0; i < (items || []).length; i++) {
      var name = placementShortTitle(selectionDisplayLabel(items[i]))
      if (!name || seen[name]) continue
      seen[name] = true
      names.push(name)
    }
    return names
  }

  function uniqueKeepsakeStyleTitles(entry) {
    return uniqueStyleTitlesFromItems(keepsakeItems(entry))
  }

  function uniquePlacementUrnStyleTitles(entry) {
    return uniqueStyleTitlesFromItems(placementUrnItems(entry))
  }

  function joinEnglishList(names) {
    if (!names || !names.length) return 'keepsake'
    if (names.length === 1) return names[0]
    if (names.length === 2) return names[0] + ' and ' + names[1]
    return names.slice(0, -1).join(', ') + ', and ' + names[names.length - 1]
  }

  function interpolateKeepsakeNotice(text, state, names) {
    var keepsake = (names && names[0]) || 'keepsake'
    var keepsakes = joinEnglishList(names)
    return interpolatePet(text, state)
      .replace(/\{keepsakes\}/gi, keepsakes)
      .replace(/\{keepsake\}/gi, keepsake)
  }

  function isTimeSensitive(product) {
    var b = product && product.booking
    if (!b) return false
    return truthy(b.timeSensitiveDecision) ||
      truthy(b.requiresAshes) ||
      truthy(b.requiresFur) ||
      truthy(b.requiresPawPrint) ||
      truthy(b.requiresNosePrint) ||
      truthy(b.requiresImpression)
  }

  function weightStatus(product, weightKg, hideIncompatible) {
    var b = product && product.booking
    if (!b || weightKg == null) return { ok: true, note: '' }
    var min = numOrNull(b.minimumPetWeightKg)
    var max = numOrNull(b.maximumPetWeightKg)
    if (min != null && weightKg < min) {
      return {
        ok: false,
        note: 'Suitable for pets from ' + min + ' kg.',
        hide: !!hideIncompatible,
        bound: 'min',
      }
    }
    if (max != null && weightKg > max) {
      return {
        ok: false,
        note: 'Suitable for pets up to ' + max + ' kg.',
        hide: !!hideIncompatible,
        bound: 'max',
        maxKg: max,
      }
    }
    return { ok: true, note: max != null ? 'Suitable for pets up to ' + max + ' kg.' : '' }
  }

  /**
   * Normalise pet-type tokens from TE / metafields / tags / pet fields.
   * @returns {'cat'|'dog'|'bird'|'any'|null}
   */
  function normalizePetTypeToken(value) {
    var v = String(value == null ? '' : value).trim().toLowerCase()
    if (!v) return null
    if (v === 'cat' || v === 'cats' || v === 'feline' || v === 'kitten' || v === 'kittens') return 'cat'
    if (v === 'dog' || v === 'dogs' || v === 'canine' || v === 'puppy' || v === 'puppies' || v === 'pup') {
      return 'dog'
    }
    if (
      v === 'bird' ||
      v === 'birds' ||
      v === 'avian' ||
      v === 'poultry' ||
      v === 'parrot'
    ) {
      return 'bird'
    }
    if (
      v === 'any' ||
      v === 'all' ||
      v === 'universal' ||
      v === 'both' ||
      v === 'either' ||
      v === 'all_pets' ||
      v === 'all-pets'
    ) {
      return 'any'
    }
    return null
  }

  /** Common cat breed / type keywords (word-boundary matched against breed text). */
  var CAT_BREED_KEYWORDS = [
    'persian', 'siamese', 'maine coon', 'ragdoll', 'bengal', 'british shorthair',
    'british longhair', 'russian blue', 'scottish fold', 'sphynx', 'abyssinian',
    'birman', 'burmese', 'devon rex', 'cornish rex', 'himalayan', 'tonkinese',
    'oriental', 'norwegian forest', 'savannah', 'manx', 'turkish angora',
    'turkish van', 'bombay', 'chartreux', 'egyptian mau', 'exotic shorthair',
    'munchkin', 'ragamuffin', 'snowshoe', 'american shorthair', 'american curl',
    'domestic shorthair', 'domestic longhair', 'domestic medium hair',
    'domestic short hair', 'domestic long hair', 'domestic mediumhair',
    // AU / common abbreviations
    'dsh', 'dlh', 'dmh', 'bsh', 'blh', 'moggy', 'moggie', 'moggies',
    'tuxedo cat', 'tabby', 'calico', 'tortoiseshell', 'tortie', 'torbie',
    'ginger cat', 'fluffy cat', 'house cat', 'rescue cat',
  ]

  /** Common dog breed / type keywords (word-boundary matched against breed text). */
  var DOG_BREED_KEYWORDS = [
    'labrador', 'labrador retriever', 'golden retriever', 'retriever', 'german shepherd',
    'shepherd', 'bulldog', 'french bulldog', 'english bulldog', 'poodle', 'beagle',
    'rottweiler', 'yorkshire terrier', 'terrier', 'boxer', 'dachshund', 'sausage dog',
    'siberian husky', 'husky', 'great dane', 'doberman', 'dobermann', 'shih tzu',
    'australian shepherd', 'cavalier', 'king charles', 'border collie', 'collie',
    'corgi', 'pembroke', 'staffordshire', 'pit bull', 'pitbull', 'maltese',
    'chihuahua', 'pug', 'samoyed', 'akita', 'spaniel', 'cocker spaniel', 'springer',
    'whippet', 'greyhound', 'mastiff', 'bernese', 'newfoundland', 'malamute',
    'kelpie', 'cattle dog', 'heeler', 'blue heeler', 'red heeler', 'jack russell',
    'fox terrier', 'malinois', 'pomeranian', 'ridgeback', 'pointer', 'setter',
    'cavoodle', 'spoodle', 'labradoodle', 'groodle', 'moodle', 'cavachon',
    'cockapoo', 'goldendoodle', 'mixed breed dog', 'crossbreed dog', 'mongrel',
    // AU / common abbreviations (word-boundary; “Lab.” normalised before match)
    'lab', 'labs', 'gsd', 'gs', 'staffy', 'staffie', 'staffies', 'amstaff',
    'jrt', 'yorkie', 'pom', 'dane', 'rottie', 'cav', 'cavvie', 'cavvy',
    'border', 'bullie', 'bully', 'frenchie', 'doodle', 'rescue dog',
  ]

  /** Common AU pet / backyard bird keywords (word-boundary matched against breed text). */
  var BIRD_BREED_KEYWORDS = [
    'budgie', 'budgerigar', 'cockatiel', 'parrot', 'cockatoo', 'macaw', 'canary',
    'finch', 'lovebird', 'conure', 'african grey', 'african gray', 'dove', 'pigeon',
    'chicken', 'hen', 'rooster', 'duck', 'goose', 'gosling', 'owl', 'eagle', 'hawk',
    'galah', 'corella', 'lorikeet', 'rainbow lorikeet', 'rosella', 'quaker',
    'eclectus', 'amazon parrot', 'senegal', 'caique', 'pionus', 'parrotlet',
    'budgerigars', 'parakeet', 'parakeets', 'cockatiels', 'cockatoos', 'macaws',
    'canaries', 'finches', 'lovebirds', 'conures', 'doves', 'pigeons', 'chickens',
    'ducks', 'geese', 'turkey', 'peahen', 'peacock', 'guinea fowl', 'quail',
    'magpie', 'kookaburra', 'sulphur crested', 'sulfur crested', 'umbrella cockatoo',
    'rescue bird', 'pet bird',
  ]

  /** Normalise breed text for keyword matching (trim, lower, strip abbrev punctuation). */
  function normalizeBreedText(raw) {
    return String(raw == null ? '' : raw)
      .trim()
      .toLowerCase()
      .replace(/[.,/#+]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  }

  function breedTextMatchesKeyword(breedNorm, keyword) {
    if (!breedNorm || !keyword) return false
    var k = String(keyword).toLowerCase()
    if (k.indexOf(' ') !== -1) {
      return breedNorm.indexOf(k) !== -1
    }
    try {
      // Word-boundary match; short abbrevs like “lab” must not match “labrador”
      // as a substring without boundaries (\\blab\\b fails on “labrador” — correct).
      return new RegExp('\\b' + k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b', 'i').test(breedNorm)
    } catch (err) {
      return breedNorm.indexOf(k) !== -1
    }
  }

  /**
   * Infer pet species from breed text (and optional species field).
   * Uses state.pet.breed (same value shown in summary, e.g. “Lab”).
   * @returns {'cat'|'dog'|'bird'|'unknown'}
   */
  function inferPetSpecies(state) {
    var pet = (state && state.pet) || {}
    var fromField = normalizePetTypeToken(pet.species || pet.petType || pet.type)
    if (fromField === 'cat' || fromField === 'dog' || fromField === 'bird') return fromField

    var breed = normalizeBreedText(pet.breed)
    if (!breed) return 'unknown'
    if (/\b(cats?|feline|kittens?)\b/.test(breed)) return 'cat'
    if (/\b(dogs?|canine|puppies|puppy|pup)\b/.test(breed)) return 'dog'
    if (/\b(birds?|avian|poultry|fowl)\b/.test(breed)) return 'bird'

    var i
    for (i = 0; i < CAT_BREED_KEYWORDS.length; i++) {
      if (breedTextMatchesKeyword(breed, CAT_BREED_KEYWORDS[i])) return 'cat'
    }
    for (i = 0; i < DOG_BREED_KEYWORDS.length; i++) {
      if (breedTextMatchesKeyword(breed, DOG_BREED_KEYWORDS[i])) return 'dog'
    }
    for (i = 0; i < BIRD_BREED_KEYWORDS.length; i++) {
      if (breedTextMatchesKeyword(breed, BIRD_BREED_KEYWORDS[i])) return 'bird'
    }
    return 'unknown'
  }

  /** Default inclusive TE phrasing for the fur / feather memorial category. */
  var FUR_FEATHER_COPY = {
    feather: {
      title: 'Would you like a small feather clipping to keep?',
      reviewLabel: 'Feather',
      skipLabel: 'Continue without a feather clipping',
    },
    fur: {
      title: 'Would you like a small fur clipping to keep?',
      reviewLabel: 'Fur',
      skipLabel: 'Continue without a fur clipping',
    },
    inclusive: {
      title: 'Would you like a small fur or feather clipping to keep?',
      reviewLabel: 'Fur or feather',
      skipLabel: 'Continue without a clipping',
    },
  }

  /**
   * Fur/feather step when group_key is fur_feather, or TE title / review label
   * mentions fur or feather (so custom keys still get species-aware copy).
   */
  function isFurFeatherMemorialCategory(cat) {
    if (!cat) return false
    if (String(cat.key || '') === 'fur_feather') return true
    var hay = String(cat.reviewLabel || '') + ' ' + String(cat.title || '')
    return /\bfur\b/i.test(hay) || /\bfeather\b/i.test(hay)
  }

  /**
   * Shared species kind for fur/feather and paw/nose/clay copy.
   * bird → feather; cat/dog → fur; blank breed → inclusive;
   * unrecognised non-empty breed → fur (typical pet mammal default).
   * @returns {'feather'|'fur'|'inclusive'}
   */
  function resolvePetClippingKind(state) {
    var pet = (state && state.pet) || {}
    var breed = normalizeBreedText(pet.breed)
    var species = inferPetSpecies(state)
    if (species === 'bird') return 'feather'
    if (species === 'cat' || species === 'dog') return 'fur'
    if (!breed) return 'inclusive'
    return 'fur'
  }

  /** @deprecated Prefer resolvePetClippingKind — kept as alias for existing call sites/tests. */
  function resolveFurFeatherKind(state) {
    return resolvePetClippingKind(state)
  }

  /** Legacy Theme Editor headlines — rewrite so merchants needn’t re-save. */
  function shouldOverrideFurFeatherTitle(text) {
    var t = String(text == null ? '' : text).trim()
    if (!t) return true
    return /^Would you like us to keep a (fur or feather|fur|feather) clipping\?$/i.test(t)
  }

  /** Quiet “Optional — …” helper is redundant once the skip link is on the step. */
  function isGenericOptionalMemorialHelper(text) {
    var t = String(text == null ? '' : text).trim()
    if (!t) return true
    return /^optional\b/i.test(t)
  }

  /**
   * One-line card description for fur/feather styles when Theme Editor /
   * metafield copy is blank. Matched from the customer-facing title.
   */
  function defaultFurFeatherCardDescription(title) {
    var t = String(title || '').toLowerCase()
    var hasTeddy = /\bteddy\b/.test(t)
    var hasTin = /\btins?\b/.test(t)
    var hasPouch = /\b(organza|pouch|bag)\b/.test(t)
    var hasSet = /\bset\b/.test(t)
    if (hasTeddy && (hasTin || hasSet)) return 'Teddy, organza pouch and windowed tin'
    if (hasTeddy) return 'Teddy with clipping in an organza pouch'
    if (hasTin) return 'Clipping displayed in a windowed tin'
    if (hasPouch) return 'Clipping kept in a soft drawstring pouch'
    return ''
  }

  /**
   * Nested Booking Summary labels for packaging included with a memorial style
   * (e.g. Organza Pouch under Keepsake Teddy; Teddy, Pouch & Tin under Keepsake
   * Set). Uses card title + supporting copy — the same signals as
   * defaultFurFeatherCardDescription. No invented prices; callers mark these
   * as included / hidePrice.
   */
  function includedMemorialPackagingLabels(styleTitle, supporting) {
    var title = String(styleTitle || '').trim()
    if (!title) return []
    var desc = String(supporting || '').trim()
    if (!desc) desc = defaultFurFeatherCardDescription(title)
    var t = title.toLowerCase()
    var d = desc.toLowerCase()
    var labels = []
    var titleTeddy = /\bteddy\b/.test(t)
    var titleSet = /\bset\b/.test(t) || (titleTeddy && /\btins?\b/.test(t))
    var descTeddy = /\bteddy\b/.test(d)
    var descPouch = /\b(organza|pouch)\b/.test(d)
    var descTin = /\btins?\b/.test(d)
    // Keepsake Set contents (title has set/tin, or set title + set-like supporting).
    if (
      (titleTeddy && titleSet) ||
      (/\bset\b/.test(t) && descTeddy && descPouch && descTin)
    ) {
      labels.push('Teddy, Pouch & Tin')
      return labels
    }
    // Solo teddy + pouch packaging. Not the standalone pouch SKU.
    if (titleTeddy && /\borganza\b/.test(d) && /\bpouch\b/.test(d)) {
      labels.push('Organza Pouch')
    }
    return labels
  }

  /**
   * Species-aware title / helper / sidebar label for the fur or feather step.
   * Inclusive TE defaults stay when breed is blank; bird/fur override when known.
   */
  function resolveFurFeatherPresentation(cat, state) {
    var kind = resolvePetClippingKind(state)
    var defaults = FUR_FEATHER_COPY[kind] || FUR_FEATHER_COPY.inclusive
    var teTitle = cat && cat.title ? String(cat.title).trim() : ''
    var teSupporting = cat && cat.supportingCopy != null ? String(cat.supportingCopy).trim() : ''
    var teReview = cat && cat.reviewLabel ? String(cat.reviewLabel).trim() : ''

    if (kind === 'inclusive') {
      return {
        kind: kind,
        title: shouldOverrideFurFeatherTitle(teTitle) ? defaults.title : teTitle,
        supportingCopy: teSupporting,
        reviewLabel: teReview || defaults.reviewLabel,
        skipLabel: defaults.skipLabel,
      }
    }

    return {
      kind: kind,
      title: defaults.title,
      supportingCopy: teSupporting,
      reviewLabel: defaults.reviewLabel,
      skipLabel: defaults.skipLabel,
    }
  }

  /** Bird-friendly defaults for the paw / nose / clay (ink_clay) memorial category. */
  var PAW_NOSE_CLAY_BIRD_COPY = {
    title: 'Would you like a print or clay keepsake?',
    reviewLabel: 'Prints & clay',
  }

  /**
   * Paw/nose/clay step when group_key is ink_clay (or contains paw/nose/clay/ink),
   * or TE title / review label mentions those terms.
   */
  function isPawNoseClayMemorialCategory(cat) {
    if (!cat) return false
    var key = String(cat.key || '').toLowerCase()
    if (key === 'ink_clay') return true
    if (/paw|nose|clay|ink/.test(key)) return true
    var hay = String(cat.reviewLabel || '') + ' ' + String(cat.title || '')
    return (
      /\bpaw\b/i.test(hay) ||
      /\bnose\b/i.test(hay) ||
      /\bclay\b/i.test(hay) ||
      /\bink\b/i.test(hay)
    )
  }

  /** True when TE copy still uses paw/nose phrasing (or is blank for title). */
  function looksLikePawNoseClayDefaultCopy(text) {
    var t = String(text == null ? '' : text).trim()
    if (!t) return true
    var lower = t.toLowerCase()
    return /\bpaw\b/.test(lower) || /\bnose\b/.test(lower)
  }

  /**
   * Species-aware title / helper / sidebar label for paw, nose & clay.
   * Birds soften paw/nose phrasing; cat/dog/blank keep TE defaults.
   */
  function resolvePawNoseClayPresentation(cat, state) {
    var kind = resolvePetClippingKind(state)
    var teTitle = cat && cat.title ? String(cat.title).trim() : ''
    var teSupporting = cat && cat.supportingCopy != null ? String(cat.supportingCopy).trim() : ''
    var teReview = cat && cat.reviewLabel ? String(cat.reviewLabel).trim() : ''
    var bird = PAW_NOSE_CLAY_BIRD_COPY

    if (kind !== 'feather') {
      return {
        kind: kind,
        title: teTitle,
        supportingCopy: teSupporting,
        reviewLabel: teReview,
        skipLabel: 'Continue without paw prints',
      }
    }

    return {
      kind: kind,
      title: looksLikePawNoseClayDefaultCopy(teTitle) ? bird.title : teTitle,
      supportingCopy: teSupporting,
      reviewLabel: bird.reviewLabel,
      skipLabel: 'Continue without paw prints',
    }
  }

  /**
   * Product orientation for species filtering.
   * Priority: TE pet_type → booking.petType / booking.species metafield →
   * tags (pet-type-cat / cat-urn) → title/handle “cat” heuristic → any.
   * @returns {'cat'|'dog'|'any'}
   */
  function resolveProductPetType(product, itemBlock) {
    var te = itemBlock && itemBlock.settings && itemBlock.settings.pet_type
    var n = normalizePetTypeToken(te)
    if (n) return n

    var b = (product && product.booking) || {}
    n = normalizePetTypeToken(b.petType || b.species)
    if (n) return n

    var tags = (product && product.tags) || []
    var ti
    for (ti = 0; ti < tags.length; ti++) {
      var tag = String(tags[ti] || '').trim().toLowerCase()
      if (!tag) continue
      n = normalizePetTypeToken(tag)
      if (n === 'cat' || n === 'dog') return n
      if (
        tag === 'pet-type-cat' ||
        tag === 'pet_type_cat' ||
        tag === 'cat-urn' ||
        tag === 'cat_urn' ||
        tag === 'cats-only' ||
        tag === 'for-cats'
      ) {
        return 'cat'
      }
      if (
        tag === 'pet-type-dog' ||
        tag === 'pet_type_dog' ||
        tag === 'dog-urn' ||
        tag === 'dog_urn' ||
        tag === 'dogs-only' ||
        tag === 'for-dogs'
      ) {
        return 'dog'
      }
    }

    var title = String((product && product.title) || '')
    var handle = String((product && product.handle) || '')
    var hay = (title + ' ' + handle).toLowerCase()
    // Word-boundary “cat” — avoids false hits like “scatter”.
    if (/\bcat\b/.test(hay) || /(^|-)cat(-|$)/.test(handle.toLowerCase())) return 'cat'
    if (/\bdog\b/.test(hay) || /(^|-)dog(-|$)/.test(handle.toLowerCase())) return 'dog'
    return 'any'
  }

  /**
   * Species filter for memorial products.
   * Cat-only (TE “Cats only”, metafield, tags, title heuristic):
   *   show for cat; show when breed is blank/empty (may still be a cat —
   *   weight filter still applies); hide for dog and for unrecognised
   *   non-empty breed text (safer default for dog-majority bookings).
   * Dog-only hide for cats; still show for dogs and unknown/blank.
   * Satin pouch / untagged universal products always pass.
   */
  function petTypeAllowsProduct(product, state, itemBlock) {
    var productType = resolveProductPetType(product, itemBlock)
    if (productType === 'any') return true
    var species = inferPetSpecies(state)
    if (productType === 'cat') {
      if (species === 'cat') return true
      if (species === 'dog' || species === 'bird') return false
      // unknown: blank breed may be a cat — show; unrecognised non-empty hide
      var pet = (state && state.pet) || {}
      return !normalizeBreedText(pet.breed)
    }
    if (productType === 'dog') return species !== 'cat'
    return true
  }

  function productEligible(product, config, state, category, itemBlock) {
    if (!product || !product.id) return { ok: false, hide: true, note: '' }
    var b = product.booking || {}
    if (b.enabled === false || b.enabled === 'false') return { ok: false, hide: true, note: '' }
    var flow = config.flow === 'shared' ? 'shared' : 'individual'
    if (flow === 'individual' && b.individualEligible === false) return { ok: false, hide: true, note: '' }
    if (flow === 'shared' && b.sharedEligible === false) return { ok: false, hide: true, note: '' }
    if (!petTypeAllowsProduct(product, state, itemBlock || null)) {
      return { ok: false, hide: true, note: '' }
    }
    if (category && category.timeSensitiveOnly && !isTimeSensitive(product)) {
      return { ok: false, hide: true, note: '' }
    }
    var hideUnavail = config.memorials && config.memorials.hideUnavailable !== false
    var weightKg = petWeightKg(state)
    var resolved = resolveMemorialVariant(product, weightKg, itemBlock || null)
    var variant = resolved.variant || firstAvailableVariant(product)
    if (hideUnavail && (!variant || variant.available === false) && product.available === false) {
      return { ok: false, hide: true, note: '' }
    }
    if (hideUnavail && variant && variant.available === false) {
      return { ok: false, hide: true, note: '' }
    }
    var hideWeight = !config.memorials || config.memorials.hideWeightIncompatible !== false
    var capacityRole = resolveCapacityRole(
      product,
      (itemBlock && itemBlock.settings) || {},
      false
    )
    var skipWeightGate = capacityRole === CAPACITY_ROLE_KEEPSAKE
    var productPetType = resolveProductPetType(product, itemBlock || null)
    var hideSpeciesOverWeight =
      !!hideWeight &&
      (productPetType === 'cat' || productPetType === 'dog' || productPetType === 'bird')
    function overflowEligible(note, maxKg) {
      return {
        ok: true,
        hide: false,
        overCapacity: true,
        note: '',
        weightNote: note || '',
        maxKg: maxKg != null ? maxKg : null,
        weightAutoSelected: false,
        resolvedVariant: resolved,
      }
    }
    function hideOverWeight(note, maxKg) {
      return {
        ok: false,
        hide: true,
        note: note || '',
        weightNote: note || '',
        maxKg: maxKg != null ? maxKg : null,
        weightAutoSelected: false,
        resolvedVariant: resolved,
      }
    }
    if (resolved.exceedsAll && !skipWeightGate) {
      var overNote = resolved.note || 'Not suitable for this pet’s weight.'
      if (hideSpeciesOverWeight) return hideOverWeight(overNote, resolved.maxKg)
      return overflowEligible(overNote, resolved.maxKg)
    }
    var ws = skipWeightGate ? { ok: true, note: '' } : weightStatus(product, weightKg, hideWeight)
    if (!ws.ok) {
      if (ws.bound === 'max') {
        var maxNote = ws.note || ''
        var maxKg = ws.maxKg != null ? ws.maxKg : resolved.maxKg
        if (hideSpeciesOverWeight) return hideOverWeight(maxNote, maxKg)
        return overflowEligible(maxNote, maxKg)
      }
      return { ok: false, hide: !!ws.hide, note: ws.note, weightNote: ws.note }
    }
    return {
      ok: true,
      hide: false,
      note: skipWeightGate ? '' : (resolved.note || ws.note || ''),
      weightAutoSelected: !!resolved.autoSelected,
      resolvedVariant: resolved,
    }
  }

  function normalizeMemorialSettings(raw) {
    var m = (raw && raw.memorials) || {}
    var ashes = m.ashesPlacement || {}
    return {
      enabled: m.enabled !== false,
      heading: m.heading || 'Choose any memorial keepsakes for {pet}',
      supportingCopy: m.supportingCopy || 'Everything here is optional, and you can review your choices before checkout.',
      skipLabel: m.skipLabel || 'Continue without optional keepsakes',
      viewAllLabel: m.viewAllLabel || 'View all',
      emptyReviewLabel: m.emptyReviewLabel || 'No additional keepsakes selected',
      mobileColumns: Math.max(2, Math.min(2, Number(m.mobileColumns) || 2)),
      desktopColumns: Math.max(2, Math.min(8, Number(m.desktopColumns) || 3)),
      featuredLimitMobile: Math.max(1, Number(m.featuredLimitMobile) || 3),
      featuredLimitDesktop: Math.max(1, Number(m.featuredLimitDesktop) || 5),
      hideUnavailable: m.hideUnavailable !== false,
      hideWeightIncompatible: m.hideWeightIncompatible !== false,
      enableDetailSheets: m.enableDetailSheets !== false,
      keepsakeNotice: (function () {
        var kn = m.keepsakeNotice || {}
        return {
          headingOne: rewriteLegacyKeepsakeNoticeHeading(kn.headingOne, false),
          headingMany: rewriteLegacyKeepsakeNoticeHeading(kn.headingMany, true),
          bodyOnePouch: rewriteLegacyKeepsakeNoticeBody(
            kn.bodyOnePouch,
            KEEPSAKE_NOTICE_DEFAULTS.bodyOnePouch
          ),
          bodyManyPouch: rewriteLegacyKeepsakeNoticeBody(
            kn.bodyManyPouch,
            KEEPSAKE_NOTICE_DEFAULTS.bodyManyPouch
          ),
          bodyOneUrn: rewriteLegacyKeepsakeNoticeBody(
            kn.bodyOneUrn,
            KEEPSAKE_NOTICE_DEFAULTS.bodyOneUrn
          ),
          bodyManyUrn: rewriteLegacyKeepsakeNoticeBody(
            kn.bodyManyUrn,
            KEEPSAKE_NOTICE_DEFAULTS.bodyManyUrn
          ),
        }
      })(),
      ashesPlacement: {
        question: ashes.question || 'How would you like us to place {pet}\u2019s ashes?',
        divideTitleBoth: ashes.divideTitleBoth || 'Between both urns',
        divideTitleAll: ashes.divideTitleAll || 'Between all {count} urns',
        divideSupport: ashes.divideSupport || 'We\u2019ll carefully divide the ashes between them.',
        // Rewrite exact legacy TE defaults so merchants needn’t re-save the section.
        singleTitle: (function () {
          var t = ashes.singleTitle || 'All in one urn'
          return t === 'Together in one urn' ? 'All in one urn' : t
        })(),
        singleSupport: (function () {
          var s = ashes.singleSupport || 'Choose which urn should hold them.'
          return s === 'You can choose which urn should hold them.'
            ? 'Choose which urn should hold them.'
            : s
        })(),
        destinationQuestion: ashes.destinationQuestion || 'Which urn should hold {pet}\u2019s ashes?',
        emptyUrnSingular: ashes.emptyUrnSingular || 'We\u2019ll return the other urn empty.',
        emptyUrnPlural: ashes.emptyUrnPlural || 'We\u2019ll return the other urns empty.',
        modeError: ashes.modeError || 'Please tell us how you would like the ashes placed.',
        destinationError: ashes.destinationError || 'Please choose which urn should hold the ashes.',
      },
      portrait: {
        enabled: !!(m.portrait && m.portrait.enabled),
        product: (m.portrait && m.portrait.product) || null,
        heading: (m.portrait && m.portrait.heading) || 'You might like this',
        copy: (m.portrait && m.portrait.copy) || '',
        ctaLabel: (m.portrait && m.portrait.ctaLabel) || 'Upload photo',
        cardImage: (m.portrait && m.portrait.cardImage) || null,
      },
    }
  }

  function isMultiSelectMode(mode) {
    return mode === 'optional_multiple' || mode === 'required_multiple'
  }

  function isRequiredSelectionMode(mode) {
    return mode === 'required_single' || mode === 'required_multiple'
  }

  function selectionModeFromGroup(settings) {
    var mode = settings && settings.selection_mode
    var key = settings && String(settings.group_key || '').trim()
    if (
      mode === 'required_single' ||
      mode === 'optional_single' ||
      mode === 'optional_multiple' ||
      mode === 'required_multiple'
    ) {
      // Urns: multi-select so families can pick the included pouch and/or paid
      // urns. Ashes placement (and destination when single_urn) opens in a modal.
      if (key === 'urn' && mode === 'required_single') return 'required_multiple'
      return mode
    }
    if (key === 'urn') return 'required_multiple'
    return 'optional_single'
  }

  function mergeProducts(list, seen) {
    var out = []
    ;(list || []).forEach(function (p) {
      if (!p || !p.id) return
      var id = String(p.id)
      if (seen[id]) return
      seen[id] = true
      out.push(p)
    })
    return out
  }

  /**
   * Lowest variant price for collection price sorts (cents). Missing → null.
   */
  function productSortPrice(product) {
    if (!product) return null
    if (product.price != null && product.price !== '') {
      var top = Number(product.price)
      if (isFinite(top)) return top
    }
    var variants = product.variants || []
    var min = null
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i]
      if (!v || v.price == null || v.price === '') continue
      var n = Number(v.price)
      if (!isFinite(n)) continue
      if (min == null || n < min) min = n
    }
    return min
  }

  function normalizeCollectionSort(raw) {
    var s = String(raw || '').trim()
    if (s === 'price_asc' || s === 'price_desc' || s === 'best_selling') return s
    return 'theme_order'
  }

  /**
   * Stamp Liquid/collection emission order so best_selling can preserve it
   * without title / featured re-sort. Always rewrite — list order is the
   * source of truth for this collection pass.
   */
  function stampCollectionIndex(list) {
    ;(list || []).forEach(function (p, i) {
      if (!p) return
      p._collectionIndex = i
    })
    return list
  }

  /**
   * Copy Liquid collection order onto Theme Editor Product-block products so
   * customer “Most popular” can rank curated SKUs. Collection Admin sort
   * (Best selling) is the source of `_collectionIndex`.
   */
  function stampCollectionIndexOntoProducts(targets, collections) {
    var idxById = {}
    ;(collections || []).forEach(function (p) {
      if (!p || p.id == null) return
      var idx = numOrNull(p._collectionIndex)
      if (idx == null) return
      idxById[String(p.id)] = idx
    })
    ;(targets || []).forEach(function (p) {
      if (!p || p.id == null || p._collectionIndex != null) return
      var idx = idxById[String(p.id)]
      if (idx != null) p._collectionIndex = idx
    })
  }

  /**
   * Order products for a memorial category grid.
   * Theme Editor Product-block order (_teOrder) wins when present — merchants
   * drag blocks in the sidebar and expect the same grid / colour-family
   * positions. Collection-only products (no _teOrder) follow collection_sort:
   *   theme_order   — displayOrder → featured → title (legacy default)
   *   price_asc/desc — min variant price
   *   best_selling  — Liquid/_collectionIndex order (set collection Admin
   *                   sort to Best selling for real sales ranking)
   */
  function sortProducts(products, collectionSort) {
    var mode = normalizeCollectionSort(collectionSort)
    return products.slice().sort(function (a, b) {
      var ate = numOrNull(a && a._teOrder)
      var bte = numOrNull(b && b._teOrder)
      if (ate != null && bte != null) {
        // Same TE Product block (colour slots) → keep relative/slot order (stable).
        if (ate !== bte) return ate - bte
        return 0
      }
      if (ate != null && bte == null) return -1
      if (ate == null && bte != null) return 1

      // Collection-sourced (or unmatched) products — honour Category sort.
      if (mode === 'price_asc' || mode === 'price_desc') {
        var ap = productSortPrice(a)
        var bp = productSortPrice(b)
        if (ap != null && bp != null && ap !== bp) {
          return mode === 'price_asc' ? ap - bp : bp - ap
        }
        if (ap != null && bp == null) return -1
        if (ap == null && bp != null) return 1
        return String((a && a.title) || '').localeCompare(String((b && b.title) || ''))
      }
      if (mode === 'best_selling') {
        var ai = numOrNull(a && a._collectionIndex)
        var bi = numOrNull(b && b._collectionIndex)
        if (ai != null && bi != null && ai !== bi) return ai - bi
        if (ai != null && bi == null) return -1
        if (ai == null && bi != null) return 1
        return 0
      }

      var ao = numOrNull(a.booking && a.booking.displayOrder)
      var bo = numOrNull(b.booking && b.booking.displayOrder)
      if (ao != null && bo != null && ao !== bo) return ao - bo
      if (ao != null && bo == null) return -1
      if (ao == null && bo != null) return 1
      var af = truthy(a.booking && a.booking.featured) ? 0 : 1
      var bf = truthy(b.booking && b.booking.featured) ? 0 : 1
      if (af !== bf) return af - bf
      return String(a.title || '').localeCompare(String(b.title || ''))
    })
  }

  /**
   * Colour swatch families — each colour is its own Shopify product (2 size
   * variants). Grouping sources:
   *   • Theme Editor Product block with “Show colour swatches” ON — Colour 1
   *     is the block’s Memorial product; Colours 2–8 are progressive product
   *     slots on the same block (siblings, same group). Group auto-fills from
   *     style title, metafield, Classic Urn parse, block id, or handle.
   *   • Legacy: multiple Product blocks with the same Swatch group key still
   *     merge into one family (backward compatible).
   *   • Theme Editor switch OFF — always a solo card; Colour 2–8 slot products
   *     are ignored (opts out of Classic Urn auto-group for that block)
   *   • No Product block (collection-only) — metafields, then Classic Urn
   *     auto-group from titles like “* Classic Urn”
   * Included / satin pouch is never grouped. Selection stays keyed by productId.
   */
  var TE_SWATCH_COLOUR_SLOT_MAX = 8

  function normalizeSwatchGroupKey(raw) {
    var s = String(raw || '').trim().toLowerCase()
    if (!s) return ''
    return s.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
  }

  function normalizeSwatchHex(raw) {
    var s = String(raw || '').trim()
    if (!s) return ''
    // Shopify color settings may return rgba()/rgb(); keep hex when possible.
    if (/^rgba?\(/i.test(s)) return s
    if (s.charAt(0) !== '#') s = '#' + s
    if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(s)) return s.toLowerCase()
    return ''
  }

  /**
   * Pack-size digit for number swatches (1–99). Blank / 0 / invalid → unused
   * (falls through to image → colour → product thumb → label).
   */
  function normalizeSwatchNumber(raw) {
    if (raw == null || raw === '') return null
    var n = typeof raw === 'number' ? raw : parseInt(String(raw).trim(), 10)
    if (!isFinite(n) || n <= 0 || n > 99) return null
    return Math.floor(n)
  }

  /** True when any Colour 2–8 slot has a resolvable product (id or handle). */
  function teHasColourSlotProducts(itemBlock) {
    var s = itemBlock && itemBlock.settings
    if (!s) return false
    for (var n = 2; n <= TE_SWATCH_COLOUR_SLOT_MAX; n++) {
      var p = s['colour_' + n + '_product']
      if (p && (p.id || p.handle)) return true
      var handle = s['colour_' + n + '_handle']
      if (handle && String(handle).trim()) return true
    }
    return false
  }

  /**
   * Tri-state Theme Editor “Show colour swatches”:
   *   true  — explicit ON (or inferred when Colour 2+ is filled and switch unset)
   *   false — explicit OFF (solo card; Colour 2–8 ignored)
   *   null  — no Product-block switch (collection auto-group / metafields OK)
   *
   * Important: do NOT coerce null → false via `!!`. Liquid emits null for an
   * unset checkbox; treating that as OFF opted Classic Urn products out of
   * auto-group and skipped Colour 2–8 hydration.
   */
  function teSwatchesEnabled(itemBlock) {
    if (!itemBlock || !itemBlock.settings) return null
    var v = itemBlock.settings.swatches_enabled
    if (truthy(v)) return true
    if (v === false || v === 'false' || v === 0 || v === '0') return false
    // Unset / null: Colour 2+ slots mean the merchant intended a family
    // (common after schema changes or when the checkbox didn’t persist).
    if (teHasColourSlotProducts(itemBlock)) return true
    return null
  }

  /** “Bright Gold Classic Urn” → { groupKey, styleTitle, colourLabel }. */
  function parseClassicUrnSwatchTitle(title) {
    var t = String(title || '').replace(/\s+/g, ' ').trim()
    if (!t) return null
    if (/\b(satin|pouch|tribute|scatter)\b/i.test(t)) return null
    var m = t.match(/^(.*?)\s+Classic\s+Urn\b/i)
    if (!m) return null
    var colour = String(m[1] || '').replace(/[-–—|:]\s*$/, '').trim()
    if (!colour) return null
    return {
      groupKey: 'classic-urn',
      styleTitle: 'Classic Urn',
      colourLabel: colour,
    }
  }

  /** Shared family key for Colour 1 + progressive slots on one Product block. */
  function ensureTeFamilySwatchGroup(parentBlock) {
    var s = (parentBlock && parentBlock.settings) || {}
    var explicit = normalizeSwatchGroupKey(s.swatch_group)
    if (explicit) return explicit
    var fromStyle = normalizeSwatchGroupKey(s.style_title)
    if (fromStyle) return fromStyle
    var auto = parseClassicUrnSwatchTitle(s.product && s.product.title)
    if (auto && auto.groupKey) return auto.groupKey
    if (parentBlock && parentBlock.id) {
      var fromBlock = normalizeSwatchGroupKey('te-' + parentBlock.id)
      if (fromBlock) return fromBlock
    }
    return 'colour-family'
  }

  /**
   * Hydrate a Colour N product from TE JSON. Prefers the full product object;
   * falls back to handle match against collection / already-seen products when
   * Theme Editor emits a handle-only stub or null product + colour_N_handle.
   */
  function resolveTeSlotProduct(rawProduct, handleHint, pools) {
    if (rawProduct && rawProduct.id) return rawProduct
    var handle = ''
    if (typeof rawProduct === 'string') handle = String(rawProduct).trim()
    else if (rawProduct && rawProduct.handle) handle = String(rawProduct.handle).trim()
    if (!handle && handleHint) handle = String(handleHint).trim()
    if (!handle) return rawProduct && rawProduct.id ? rawProduct : null
    var lists = pools || []
    for (var i = 0; i < lists.length; i++) {
      var list = lists[i] || []
      for (var j = 0; j < list.length; j++) {
        var cand = list[j]
        if (cand && cand.id && String(cand.handle || '') === handle) return cand
      }
    }
    return rawProduct && rawProduct.id ? rawProduct : null
  }

  function teColourSlotLabels(itemBlock) {
    var s = (itemBlock && itemBlock.settings) || {}
    var out = [String(s.colour_label || '').trim()]
    var n
    for (n = 2; n <= TE_SWATCH_COLOUR_SLOT_MAX; n++) {
      out.push(String(s['colour_' + n + '_label'] || '').trim())
    }
    return out
  }

  /**
   * Remap Colour N (2–8) slot settings onto the same shape as Colour 1 so
   * resolveCardSwatchMeta / addonFlags can treat each sibling uniformly.
   * Variant max-kg TE overrides are copied onto every colour slot — capacity is
   * a style-level setting on the Product block, not per colour SKU.
   */
  function makeColourSlotItemBlock(parentBlock, slotIndex, resolvedProduct) {
    var parentSettings = (parentBlock && parentBlock.settings) || {}
    if (!slotIndex || slotIndex <= 1) return parentBlock
    var prefix = 'colour_' + slotIndex + '_'
    var settings = {
      group_key: parentSettings.group_key,
      product: resolvedProduct || parentSettings[prefix + 'product'] || null,
      label_override: '',
      featured: parentSettings.featured,
      pet_type: parentSettings.pet_type,
      swatches_enabled: true,
      // Stamp the parent family key so slot siblings always collapse together.
      swatch_group: ensureTeFamilySwatchGroup(parentBlock),
      style_title: parentSettings.style_title,
      colour_label: parentSettings[prefix + 'label'] || '',
      swatch_color: parentSettings[prefix + 'color'] || '',
      swatch_image: parentSettings[prefix + 'image'] || null,
      swatch_number: parentSettings[prefix + 'number'],
      // Colour slots: blank Card photo = that colour’s Shopify photo (do not inherit).
      // Quantity slots (3 prints / 5 prints): inherit the parent Card photo so
      // Ink Paw / Nose don’t load a huge Shopify pack shot when only Colour 1
      // has a Theme Editor thumb.
      card_image:
        parentSettings[prefix + 'card_image'] ||
        (normalizeSwatchNumber(parentSettings[prefix + 'number'])
          ? parentSettings.card_image
          : null) ||
        null,
      card_image_gold: parentSettings.card_image_gold || null,
      card_image_silver: parentSettings.card_image_silver || null,
      // Quantity noun is a style-level label — every slot shares it.
      quantity_unit: parentSettings.quantity_unit,
      card_description: parentSettings.card_description,
      // Style-level ashes capacity — every colour shares the parent Product block.
      ashes_capacity_role: parentSettings.ashes_capacity_role,
      ashes_capacity_kg: parentSettings.ashes_capacity_kg,
      ashes_capacity_label: parentSettings.ashes_capacity_label,
      ashes_capacity_detail: parentSettings.ashes_capacity_detail,
      needs_photo: parentSettings.needs_photo,
      photo_fee_product: parentSettings.photo_fee_product,
      photo_print_size: parentSettings.photo_print_size,
      photo_orientation: parentSettings.photo_orientation,
      photo_orientation_default: parentSettings.photo_orientation_default,
      photo_card_title: parentSettings.photo_card_title,
      photo_card_subtitle: parentSettings.photo_card_subtitle,
      photo_mockup_frame: parentSettings.photo_mockup_frame || null,
      needs_engraving: parentSettings.needs_engraving,
      engraving_fee_product: parentSettings.engraving_fee_product,
      engraving_card_title: parentSettings.engraving_card_title,
      engraving_card_subtitle: parentSettings.engraving_card_subtitle,
      needs_nameplate: parentSettings.needs_nameplate,
      nameplate_included: parentSettings.nameplate_included,
      nameplate_product_gold: parentSettings.nameplate_product_gold,
      nameplate_product_silver: parentSettings.nameplate_product_silver,
      nameplate_gold_label: parentSettings.nameplate_gold_label,
      nameplate_silver_label: parentSettings.nameplate_silver_label,
      nameplate_preview_gold: parentSettings.nameplate_preview_gold,
      nameplate_preview_silver: parentSettings.nameplate_preview_silver,
      personalise_image: parentSettings.personalise_image || null,
      personalise_image_gold: parentSettings.personalise_image_gold || null,
      personalise_image_silver: parentSettings.personalise_image_silver || null,
      review_blank_card_image: parentSettings.review_blank_card_image || null,
      needs_nameplate_text: parentSettings.needs_nameplate_text,
    }
    // Style-level capacity overrides apply to every colour in the family.
    for (var vi = 1; vi <= VARIANT_MAX_KG_SLOT_COUNT; vi++) {
      var kgKey = 'variant_max_kg_' + vi
      if (parentSettings[kgKey] != null && parentSettings[kgKey] !== '') {
        settings[kgKey] = parentSettings[kgKey]
      }
      var imgKey = 'variant_card_image_' + vi
      if (parentSettings[imgKey]) settings[imgKey] = parentSettings[imgKey]
    }
    return {
      id: parentBlock.id,
      type: parentBlock.type || 'memorial_item',
      settings: settings,
      _swatchSlotIndex: slotIndex,
      _swatchParentBlockId: parentBlock.id,
    }
  }

  /**
   * Progressive TE colour slots on one Product block.
   * Explicit OFF → empty (caller uses Memorial product alone, no slots).
   * ON (or inferred from Colour 2+) → Colour 1 + Colours 2–8 (skips blank slots).
   * @param {object} itemBlock
   * @param {object[]} [productPools] optional lists to resolve handle-only slots
   */
  function teSwatchSlotEntries(itemBlock, productPools) {
    var s = itemBlock && itemBlock.settings
    if (!s) return []
    if (teSwatchesEnabled(itemBlock) !== true) return []
    var pools = productPools || []
    var out = []
    var colour1 = resolveTeSlotProduct(s.product, null, pools)
    if (colour1 && colour1.id) {
      out.push({ slot: 1, product: colour1, itemBlock: itemBlock })
    }
    for (var n = 2; n <= TE_SWATCH_COLOUR_SLOT_MAX; n++) {
      var p = resolveTeSlotProduct(
        s['colour_' + n + '_product'],
        s['colour_' + n + '_handle'],
        pools
      )
      if (!p || !p.id) continue
      out.push({
        slot: n,
        product: p,
        itemBlock: makeColourSlotItemBlock(itemBlock, n, p),
      })
    }
    return out
  }

  function autoSwatchGroupFromTe(te, product, auto, itemBlock) {
    var fromStyle = normalizeSwatchGroupKey(te && te.style_title)
    if (fromStyle) return fromStyle
    if (auto && auto.groupKey) return auto.groupKey
    // Stable per-block family when slots are configured without a style title.
    if (itemBlock && itemBlock.id && teSwatchSlotEntries(itemBlock).length > 1) {
      var fromBlock = normalizeSwatchGroupKey('te-' + itemBlock.id)
      if (fromBlock) return fromBlock
    }
    var handle = product && product.handle
    var fromHandle = normalizeSwatchGroupKey(handle)
    if (fromHandle) {
      // Strip a trailing colour-ish suffix when the handle is colour-style.
      var stripped = fromHandle
        .replace(/-(bright-gold|matte-black|rose-gold|antique-bronze|silver|gold|black|white|bronze)$/i, '')
        .replace(/^-+|-+$/g, '')
      return stripped || fromHandle
    }
    return 'colour-family'
  }

  function resolveCardSwatchMeta(card, itemBlock) {
    var product = card && card.product
    var booking = (product && product.booking) || {}
    var te = (itemBlock && itemBlock.settings) || {}
    var auto = parseClassicUrnSwatchTitle(product && product.title)
    var switchOn = teSwatchesEnabled(itemBlock)

    // Explicit TE opt-out: solo card even when title matches Classic Urn.
    if (itemBlock && switchOn === false) {
      return {
        groupKey: '',
        colourLabel: '',
        swatchColor: '',
        swatchImage: null,
        swatchNumber: null,
        variantImage: teCardImageFromBlock(itemBlock),
        optionUnit: String(te.quantity_unit || '').trim(),
        labelOverride: String(te.colour_label || '').trim(),
        optionLabels: teColourSlotLabels(itemBlock),
        styleTitle: '',
        source: 'te_disabled',
        enabled: false,
      }
    }

    var teOn = itemBlock && switchOn === true
    var styleTitle =
      String(te.style_title || '').trim() ||
      (auto ? auto.styleTitle : '') ||
      ''

    var group = ''
    var source = ''
    if (teOn) {
      group =
        normalizeSwatchGroupKey(te.swatch_group) ||
        normalizeSwatchGroupKey(booking.swatchGroup) ||
        autoSwatchGroupFromTe(te, product, auto, itemBlock)
      source = normalizeSwatchGroupKey(te.swatch_group)
        ? 'theme_editor'
        : normalizeSwatchGroupKey(booking.swatchGroup)
          ? 'metafield'
          : auto && group === auto.groupKey
            ? 'auto_classic_urn'
            : 'theme_editor_auto'
    } else {
      // Collection-only / no Product-block switch: metafield then Classic Urn auto.
      group =
        normalizeSwatchGroupKey(booking.swatchGroup) ||
        (auto && auto.groupKey) ||
        ''
      source = normalizeSwatchGroupKey(booking.swatchGroup)
        ? 'metafield'
        : auto && group === auto.groupKey
          ? 'auto_classic_urn'
          : ''
    }

    var colourLabel = teOn
      ? String(te.colour_label || '').trim() ||
        String(booking.colourLabel || '').trim() ||
        (auto && auto.colourLabel) ||
        ''
      : String(booking.colourLabel || '').trim() ||
        (auto && auto.colourLabel) ||
        ''

    var swatchColor = teOn
      ? normalizeSwatchHex(te.swatch_color) || normalizeSwatchHex(booking.swatchColor) || ''
      : normalizeSwatchHex(booking.swatchColor) || ''

    var swatchImage = null
    if (teOn && te.swatch_image) {
      swatchImage = String(te.swatch_image).trim() || null
    }

    var swatchNumber = teOn ? normalizeSwatchNumber(te.swatch_number) : null

    // Card-stage image for this option (Theme Editor “Card image”). Falls back to
    // the product/variant image in buildSwatchOption when unset.
    var variantImage = teOn ? teCardImageFromBlock(itemBlock) : null

    // Merchant override for the quantity noun (e.g. “prints”, “Tin/s”).
    // Not gated on Show colour swatches — pack-size variants still need it.
    var optionUnit = String(te.quantity_unit || '').trim()
    var labelOverride = String(te.colour_label || '').trim()
    var optionLabels = teColourSlotLabels(itemBlock)

    if (!styleTitle && auto && group === auto.groupKey) {
      styleTitle = auto.styleTitle
    }

    return {
      groupKey: group,
      colourLabel: colourLabel,
      swatchColor: swatchColor,
      swatchImage: swatchImage,
      swatchNumber: swatchNumber,
      variantImage: variantImage,
      optionUnit: optionUnit,
      labelOverride: labelOverride,
      optionLabels: optionLabels,
      styleTitle: styleTitle,
      source: source,
      enabled: !!group,
    }
  }

  function familyStyleTitle(members, groupKey) {
    var explicit = ''
    for (var i = 0; i < members.length; i++) {
      var st = members[i] && members[i]._swatchMeta && members[i]._swatchMeta.styleTitle
      if (st && String(st).trim()) {
        explicit = String(st).trim()
        break
      }
    }
    if (explicit) return refineMemorialDisplayTitle(explicit)

    var shorts = []
    members.forEach(function (m) {
      var s = m && m.product && m.product.booking && m.product.booking.shortTitle
      if (s && String(s).trim()) shorts.push(String(s).trim())
    })
    if (shorts.length === members.length && shorts.every(function (s) { return s === shorts[0] })) {
      return refineMemorialDisplayTitle(shorts[0])
    }

    if (groupKey === 'classic-urn') return 'Classic Urn'

    var first = members[0]
    var colour = first && first._swatchMeta && first._swatchMeta.colourLabel
    var raw = (first && first.product && first.product.title) || (first && first.title) || 'Item'
    if (colour) {
      var stripped = String(raw).replace(new RegExp('^' + colour.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s+', 'i'), '').trim()
      if (stripped && stripped !== String(raw).trim()) return refineMemorialDisplayTitle(stripped)
    }
    return refineMemorialDisplayTitle(String(raw).trim() || 'Item')
  }

  function unionFamilyAddonFlags(members) {
    var union = {
      needsPhoto: false,
      needsNameplate: false,
      needsEngraving: false,
      needsNameplateText: false,
      nameplateIncluded: false,
      photoFeeProduct: null,
      engravingFeeProduct: null,
      nameplateProductGold: null,
      nameplateProductSilver: null,
      nameplateGoldLabel: '',
      nameplateSilverLabel: '',
      nameplatePreviewImageGold: null,
      nameplatePreviewImageSilver: null,
      personaliseImage: null,
      personaliseImageGold: null,
      personaliseImageSilver: null,
      cardImageGold: null,
      cardImageSilver: null,
      reviewBlankCardImage: null,
      photoPrintSize: '4x6',
      photoOrientation: 'portrait',
      photoOrientationDefault: 'landscape',
      photoMockupFrame: null,
      photoCardTitle: '',
      photoCardSubtitle: '',
      engravingCardTitle: '',
      engravingCardSubtitle: '',
    }
    ;(members || []).forEach(function (c) {
      if (!c) return
      if (c.needsPhoto) {
        union.needsPhoto = true
        if (!union.photoFeeProduct && c.photoFeeProduct) union.photoFeeProduct = c.photoFeeProduct
        if (!union.photoCardTitle && c.photoCardTitle) union.photoCardTitle = c.photoCardTitle
        if (!union.photoCardSubtitle && c.photoCardSubtitle) union.photoCardSubtitle = c.photoCardSubtitle
        if (!union.photoMockupFrame && c.photoMockupFrame) union.photoMockupFrame = c.photoMockupFrame
        union.photoPrintSize = normalizePhotoPrintSize(c.photoPrintSize || union.photoPrintSize)
        union.photoOrientation = normalizePhotoOrientation(c.photoOrientation || union.photoOrientation)
        union.photoOrientationDefault = normalizePhotoOrientationDefault(
          c.photoOrientationDefault || union.photoOrientationDefault
        )
      }
      if (c.needsEngraving) {
        union.needsEngraving = true
        if (!union.engravingFeeProduct && c.engravingFeeProduct) {
          union.engravingFeeProduct = c.engravingFeeProduct
        }
        if (!union.engravingCardTitle && c.engravingCardTitle) {
          union.engravingCardTitle = c.engravingCardTitle
        }
        if (!union.engravingCardSubtitle && c.engravingCardSubtitle) {
          union.engravingCardSubtitle = c.engravingCardSubtitle
        }
      }
      if (c.needsNameplate) {
        union.needsNameplate = true
        if (c.nameplateIncluded) union.nameplateIncluded = true
        if (!union.nameplateProductGold && c.nameplateProductGold) {
          union.nameplateProductGold = c.nameplateProductGold
        }
        if (!union.nameplateProductSilver && c.nameplateProductSilver) {
          union.nameplateProductSilver = c.nameplateProductSilver
        }
        if (!union.nameplateGoldLabel && c.nameplateGoldLabel) {
          union.nameplateGoldLabel = c.nameplateGoldLabel
        }
        if (!union.nameplateSilverLabel && c.nameplateSilverLabel) {
          union.nameplateSilverLabel = c.nameplateSilverLabel
        }
        if (!union.nameplatePreviewImageGold && c.nameplatePreviewImageGold) {
          union.nameplatePreviewImageGold = c.nameplatePreviewImageGold
        }
        if (!union.nameplatePreviewImageSilver && c.nameplatePreviewImageSilver) {
          union.nameplatePreviewImageSilver = c.nameplatePreviewImageSilver
        }
      }
      if (!union.personaliseImage && c.personaliseImage) {
        union.personaliseImage = c.personaliseImage
      }
      if (!union.personaliseImageGold && c.personaliseImageGold) {
        union.personaliseImageGold = c.personaliseImageGold
      }
      if (!union.personaliseImageSilver && c.personaliseImageSilver) {
        union.personaliseImageSilver = c.personaliseImageSilver
      }
      if (!union.cardImageGold && c.cardImageGold) {
        union.cardImageGold = c.cardImageGold
      }
      if (!union.cardImageSilver && c.cardImageSilver) {
        union.cardImageSilver = c.cardImageSilver
      }
      if (!union.reviewBlankCardImage && c.reviewBlankCardImage) {
        union.reviewBlankCardImage = c.reviewBlankCardImage
      }
      if (c.needsNameplateText) union.needsNameplateText = true
    })
    if (union.needsEngraving) union.needsNameplateText = false
    return union
  }

  function applyUnionAddonFlagsToCard(card, union) {
    if (!card || !union) return
    card.needsPhoto = !!union.needsPhoto
    card.needsNameplate = !!union.needsNameplate
    card.needsEngraving = !!union.needsEngraving
    card.needsNameplateText = !!union.needsNameplateText
    card.nameplateIncluded = !!union.nameplateIncluded
    card.photoFeeProduct = union.photoFeeProduct || null
    card.engravingFeeProduct = union.engravingFeeProduct || null
    card.nameplateProductGold = union.nameplateProductGold || null
    card.nameplateProductSilver = union.nameplateProductSilver || null
    card.nameplateGoldLabel = union.nameplateGoldLabel || ''
    card.nameplateSilverLabel = union.nameplateSilverLabel || ''
    card.nameplatePreviewImageGold = union.nameplatePreviewImageGold || null
    card.nameplatePreviewImageSilver = union.nameplatePreviewImageSilver || null
    card.personaliseImage = union.personaliseImage || null
    card.personaliseImageGold = union.personaliseImageGold || null
    card.personaliseImageSilver = union.personaliseImageSilver || null
    card.cardImageGold = union.cardImageGold || null
    card.cardImageSilver = union.cardImageSilver || null
    card.reviewBlankCardImage = union.reviewBlankCardImage || null
    card.photoPrintSize = normalizePhotoPrintSize(union.photoPrintSize)
    card.photoOrientation = normalizePhotoOrientation(union.photoOrientation)
    card.photoOrientationDefault = normalizePhotoOrientationDefault(union.photoOrientationDefault)
    card.photoMockupFrame = union.photoMockupFrame || null
    card.photoCardTitle = union.photoCardTitle || ''
    card.photoCardSubtitle = union.photoCardSubtitle || ''
    card.engravingCardTitle = union.engravingCardTitle || ''
    card.engravingCardSubtitle = union.engravingCardSubtitle || ''
  }

  function pickActiveSwatchMember(members, entry, groupKey) {
    var list = members || []
    var i
    for (i = 0; i < list.length; i++) {
      if (list[i] && isSelected(entry, list[i].productId)) return list[i]
    }
    var pref =
      entry &&
      entry.swatchActiveByFamily &&
      groupKey &&
      entry.swatchActiveByFamily[groupKey]
    if (pref != null && pref !== '') {
      for (i = 0; i < list.length; i++) {
        if (list[i] && String(list[i].productId) === String(pref)) return list[i]
      }
    }
    for (i = 0; i < list.length; i++) {
      if (list[i] && list[i].selectable) return list[i]
    }
    return list[0] || null
  }

  /* --------------------------- generic card options --------------------------- */

  /**
   * Card option types. Every catalog card carries one so the storefront renders
   * a single card component with no product-specific branching.
   *  none     — solo product, no tray
   *  colour   — colour / finish siblings (Classic Urn: Bright Gold, Matte Black…)
   *  quantity — number-driven siblings (print packs: 3, 5…)
   */
  var CARD_OPTION_NONE = 'none'
  var CARD_OPTION_COLOUR = 'colour'
  var CARD_OPTION_QUANTITY = 'quantity'

  /** Words that describe the packaging rather than the thing being counted. */
  var QUANTITY_PACKAGING_WORDS = /\b(sets?|packs?|bundles?|boxes|box|kits?)\b/gi

  function pluraliseUnit(word) {
    var w = String(word || '').trim()
    if (!w) return w
    if (/s$/i.test(w) && !/ss$/i.test(w)) return w
    if (/(s|x|z|ch|sh)$/i.test(w)) return w + 'es'
    if (/[^aeiou]y$/i.test(w)) return w.slice(0, -1) + 'ies'
    return w + 's'
  }

  function singulariseUnit(word) {
    var w = String(word || '').trim()
    if (!w) return w
    if (/ies$/i.test(w)) return w.slice(0, -3) + 'y'
    if (/(ches|shes|xes|zes|sses)$/i.test(w)) return w.slice(0, -2)
    if (/s$/i.test(w) && !/ss$/i.test(w)) return w.slice(0, -1)
    return w
  }

  /**
   * Theme Editor “Quantity unit” may be written Tin/s or Tin(s).
   * Returns the plural noun used by formatQuantityLabel, or the original string.
   */
  function normalizeQuantityUnitHint(raw) {
    var s = String(raw || '').trim()
    if (!s) return ''
    var compact = s.replace(/\s+/g, '')
    var m = compact.match(/^([A-Za-z]+)\/s$/i) || compact.match(/^([A-Za-z]+)\(s\)$/i)
    if (m) return pluraliseUnit(m[1].toLowerCase())
    return s
  }

  /**
   * Noun for the quantity meta line — “5 prints”. Theme Editor “Quantity unit”
   * wins; otherwise derive from the style title by dropping packaging words
   * (“Nose Print Set” → prints, “Paw Prints” → prints).
   */
  function deriveQuantityUnit(styleTitle, explicitUnit) {
    var te = normalizeQuantityUnitHint(explicitUnit)
    if (te) return te
    var t = String(styleTitle || '').replace(/\s+/g, ' ').trim()
    t = t
      .replace(/\b(set|pack|bundle|box|kit)\s+of\s+\d+\b/gi, ' ')
      .replace(QUANTITY_PACKAGING_WORDS, ' ')
      .replace(/\s+/g, ' ')
      .trim()
    var words = t.split(' ').filter(Boolean)
    var last = words.length ? words[words.length - 1].replace(/[^A-Za-z]/g, '') : ''
    if (!last) return 'items'
    return pluraliseUnit(last.toLowerCase())
  }

  function formatQuantityLabel(quantity, unit) {
    var n = Number(quantity)
    if (!(n > 0)) return ''
    var u = String(unit || 'items').trim() || 'items'
    return n === 1 ? '1 ' + singulariseUnit(u) : n + ' ' + u
  }

  /**
   * Face descriptor for one option — the storefront renders `type` verbatim.
   * Priority: pack number → uploaded swatch image → colour fill → product thumb → label.
   */
  function optionSwatchFace(option) {
    if (Number(option.swatchNumber) > 0) {
      var n = Math.floor(Number(option.swatchNumber))
      return {
        type: 'quantity',
        value: n,
        label: option.label || formatQuantityLabel(n, option.unit),
      }
    }
    if (option.swatchImage) return { type: 'image', value: option.swatchImage }
    if (option.swatchColor) return { type: 'colour', value: option.swatchColor }
    if (option.thumb) return { type: 'thumb', value: option.thumb }
    return { type: 'label', value: option.colourLabel || '' }
  }

  function buildSwatchOption(member, styleTitle) {
    var meta = (member && member._swatchMeta) || {}
    var colourLabel = meta.colourLabel || ''
    if (!colourLabel && member && member.product && member.product.title && styleTitle) {
      colourLabel = String(member.product.title)
        .replace(new RegExp('\\s*' + String(styleTitle).replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s*$', 'i'), '')
        .trim()
    }
    if (!colourLabel) colourLabel = (member && member.title) || 'Colour'
    var thumb =
      (member && member.product && member.product.imageThumb) ||
      (member && member.image) ||
      (member && member.product && member.product.image) ||
      null
    var stageImage =
      meta.variantImage ||
      (member && member.image) ||
      (member && member.baseImage) ||
      (member && member.product && member.product.image) ||
      null
    return {
      productId: member.productId,
      colourLabel: colourLabel,
      // Face meta — see optionSwatchFace for display priority.
      swatchNumber: meta.swatchNumber || null,
      swatchImage: meta.swatchImage || null,
      swatchColor: meta.swatchColor || '',
      labelOverride: String(meta.labelOverride || '').trim(),
      thumb: thumb,
      // Card-stage image: Theme Editor “Card image” must win for swatch families
      // (e.g. Forever Paws, Ink Paw). Fallbacks keep legacy behaviour when TE is blank.
      image: stageImage,
      variantId: (member && member.variantId) || null,
      price: member && member.price != null ? member.price : null,
      selectable: !!(member && member.selectable),
    }
  }

  /**
   * Finalise a swatch/option list into the generic card option model. Mutates the
   * option objects in place so `card.options` and the legacy `card.swatches`
   * alias stay one array (single source of truth for the tray).
   */
  function buildCardOptionModel(options, styleTitle, unitHint) {
    var list = options || []
    if (!list.length) {
      return { optionType: CARD_OPTION_NONE, options: [], optionLabel: '', quantityUnit: '' }
    }
    var numberDriven = list.some(function (o) {
      return o && Number(o.swatchNumber) > 0
    })
    var type = numberDriven ? CARD_OPTION_QUANTITY : CARD_OPTION_COLOUR
    var unit = type === CARD_OPTION_QUANTITY ? deriveQuantityUnit(styleTitle, unitHint) : ''
    var basePrice = null
    list.forEach(function (o) {
      if (!o || basePrice != null || o.price == null) return
      basePrice = o.price
    })
    list.forEach(function (o, i) {
      if (!o) return
      var qty = Number(o.swatchNumber) > 0 ? Math.floor(Number(o.swatchNumber)) : null
      o.id = String(o.id || o.optionId || o.productId || o.variantId)
      o.optionType = type
      o.quantity = type === CARD_OPTION_QUANTITY ? qty : null
      o.unit = type === CARD_OPTION_QUANTITY ? unit : ''
      var explicitLabel = String(o.labelOverride || '').trim()
      o.label = explicitLabel
        ? explicitLabel
        : type === CARD_OPTION_QUANTITY && qty
          ? formatQuantityLabel(qty, unit)
          : o.colourLabel || 'Option ' + (i + 1)
      o.value = type === CARD_OPTION_QUANTITY && qty ? qty : o.colourLabel || o.label
      o.swatch = optionSwatchFace(o)
      o.priceDelta = o.price != null && basePrice != null ? o.price - basePrice : null
    })
    return {
      optionType: type,
      options: list,
      optionLabel: type === CARD_OPTION_QUANTITY ? 'Quantity' : 'Colour',
      quantityUnit: unit,
    }
  }

  /** Style-level quantity-unit override from any member's Product block. */
  function familyOptionUnit(members) {
    for (var i = 0; i < (members || []).length; i++) {
      var u = members[i] && members[i]._swatchMeta && members[i]._swatchMeta.optionUnit
      if (u && String(u).trim()) return String(u).trim()
    }
    return ''
  }

  /** Solo cards still expose the option contract so the card renderer is uniform. */
  function applyNoOptionModel(card) {
    if (!card) return card
    card.optionType = CARD_OPTION_NONE
    card.optionKind = 'none'
    card.options = []
    card.optionLabel = ''
    card.quantityUnit = ''
    card.activeOptionId = card.productId != null ? String(card.productId) : ''
    card.baseImage = card.baseImage || card.image || null
    // Always clear — colour / pack labels live on family cards, not here.
    // Weight-tier size is shown via the variant photo, not a “Medium” line.
    card.variantMeta = soloVariantMeta(card)
    return card
  }

  function availableProductVariants(product) {
    var list = ((product && product.variants) || []).filter(function (v) {
      return v && v.available !== false
    })
    if (!list.length) list = (product && product.variants) || []
    return list
  }

  /** True when variants are pet-weight sizes (auto-picked) — never a card tray. */
  function productHasWeightSizingVariants(card) {
    if (!card) return false
    if (card.hasWeightCutoffs || card.weightAutoSelected) return true
    if (variantTeMaxKgCutoffs(card._variantCutoffSettings).length) return true
    var variants = (card.product && card.product.variants) || []
    var i
    for (i = 0; i < variants.length; i++) {
      var v = variants[i]
      if (!v) continue
      if (parseMaxKgFromVariantLabel(variantLabelForMaxKgParse(v)) != null) return true
      if (numOrNull(v.maxPetWeightKg) != null) return true
    }
    return false
  }

  /**
   * Calm size name for an auto-picked weight-tier variant.
   * “Small - Up to 20kg” → “Small”; “Default Title” → “”.
   */
  function shortWeightSizeLabel(variant) {
    if (!variant) return ''
    var title = String(variant.title || variant.option1 || '').replace(/\s+/g, ' ').trim()
    if (!title || /^default title$/i.test(title)) return ''
    var cut = title.split(/\s*[—–-]\s*up\s*to\b/i)[0]
    cut = cut.replace(/\s*\([^)]*\b\d+(?:\.\d+)?\s*kgs?\b[^)]*\)\s*/i, ' ').trim()
    cut = cut.split(/\s*\/\s*/)[0].trim()
    if (!cut || /^default title$/i.test(cut) || /^up\s*to\b/i.test(cut)) {
      var maxKg = parseMaxKgFromVariantLabel(title)
      return maxKg != null ? 'Up to ' + maxKg + ' kg' : ''
    }
    return cut
  }

  function parsePackQuantityFromVariantTitle(title) {
    var s = String(title || '').replace(/\s+/g, ' ').trim()
    if (!s) return null
    var m =
      s.match(/^(\d+)\s*[x×]/i) ||
      s.match(/\bpack(?:s)?\s+of\s+(\d+)\b/i) ||
      s.match(/^(\d+)\b/)
    if (!m) return null
    var n = Number(m[1])
    return isFinite(n) && n > 0 ? n : null
  }

  function parsePackUnitFromVariantTitle(title) {
    var s = String(title || '').replace(/\s+/g, ' ').trim()
    if (!s) return ''
    s = s
      .replace(/^(\d+)\s*[x×]\s*/i, '')
      .replace(/^pack(?:s)?\s+of\s+\d+\s*/i, '')
      .replace(/\s*[—–-].*$/, '')
      .trim()
    var word = s.split(/\s+/)[0] || ''
    word = word.replace(/[^A-Za-z]/g, '')
    return word ? singulariseUnit(word.toLowerCase()) : ''
  }

  function variantPackQuantity(variant) {
    if (!variant) return null
    var fields = [variant.title, variant.option1, variant.option2, variant.option3]
    var i
    for (i = 0; i < fields.length; i++) {
      var qty = parsePackQuantityFromVariantTitle(fields[i])
      if (qty != null) return qty
    }
    return null
  }

  /**
   * Gold / Silver (incl. Brushed / Bright / Matte) as a whole option value.
   * “3 Prints / Brushed Gold” is not a metal-only label — pack qty still wins.
   */
  function nameplateMetalTokenFromText(text) {
    var raw = String(text || '').replace(/\s+/g, ' ').trim()
    if (!raw || /^default title$/i.test(raw)) return null
    var exact = normalizeNameplateMetal(raw)
    if (exact) return exact
    var m = raw.match(/^(?:brushed|bright|matte|polished|antique|satin)\s+(gold|silver)$/i)
    return m ? String(m[1]).toLowerCase() : null
  }

  function nameplateMetalFromVariant(variant) {
    if (!variant) return null
    var parts = [variant.title, variant.name, variant.option1, variant.option2, variant.option3]
    var i
    for (i = 0; i < parts.length; i++) {
      var metal = nameplateMetalTokenFromText(parts[i])
      if (metal) return metal
    }
    return null
  }

  /** Shopify option axis named Nameplate or Metal — values are Gold/Silver. */
  function productHasNameplateMetalOption(product) {
    var names = (product && (product.optionNames || product.options)) || []
    var i
    for (i = 0; i < names.length; i++) {
      var n = typeof names[i] === 'string' ? names[i] : names[i] && names[i].name
      if (n && /nameplate|\bmetal\b/i.test(String(n))) return true
    }
    return false
  }

  /**
   * Nameplate Gold/Silver is chosen later (pills), not as a memorial card tray.
   * Pack-size variants (3 Prints / 1x) stay eligible for the tray.
   */
  function isNameplateMetalVariant(variant, product) {
    if (!variant) return false
    if (variantPackQuantity(variant) != null) return false
    if (nameplateMetalFromVariant(variant)) return true
    return !!(product && productHasNameplateMetalOption(product))
  }

  function preferGoldThenFirst(variants) {
    var list = variants || []
    var i
    for (i = 0; i < list.length; i++) {
      if (nameplateMetalFromVariant(list[i]) === 'gold') return list[i]
    }
    return list[0] || null
  }

  /** One tray option per pack quantity; Gold preferred when metals share a pack. */
  function uniqueVariantsByPackQuantity(variants) {
    var byQty = {}
    var order = []
    var i
    for (i = 0; i < (variants || []).length; i++) {
      var v = variants[i]
      if (!v) continue
      var key = String(variantPackQuantity(v))
      if (!byQty[key]) {
        byQty[key] = []
        order.push(key)
      }
      byQty[key].push(v)
    }
    return order
      .map(function (key) {
        return preferGoldThenFirst(byQty[key])
      })
      .filter(Boolean)
  }

  /**
   * Variants that may become card chips. Weight sizes never reach here.
   * Nameplate metals are excluded. Pack sizes are kept (deduped if mixed
   * with Gold/Silver on the same product).
   */
  function variantsForInlineCardTray(card) {
    var product = card && card.product
    var variants = availableProductVariants(product)
    var pack = []
    var other = []
    var i
    for (i = 0; i < variants.length; i++) {
      var v = variants[i]
      if (!v) continue
      if (variantPackQuantity(v) != null) {
        pack.push(v)
        continue
      }
      if (isNameplateMetalVariant(v, product)) continue
      other.push(v)
    }
    if (pack.length) return uniqueVariantsByPackQuantity(pack)
    return other
  }

  /**
   * Detail-sheet Options list: same as the card tray. Never Gold/Silver
   * (nameplate step owns that). Pack sizes remain. Metal-only → [].
   */
  function variantsForDetailPicker(card) {
    return variantsForInlineCardTray(card)
  }

  /**
   * Memorial line variant for Gold/Silver products. Nameplate colour wins;
   * skipped / unset colour defaults to Gold.
   */
  function variantForNameplateColour(card, colour, currentVariant) {
    var metal = normalizeNameplateMetal(colour) || 'gold'
    var currentId = currentVariant && currentVariant.id != null ? currentVariant.id : null
    var nextId = variantIdForNameplateMetal(card, metal, currentId)
    var variants = (card && card.product && card.product.variants) || []
    var i
    if (nextId != null) {
      for (i = 0; i < variants.length; i++) {
        if (variants[i] && String(variants[i].id) === String(nextId)) return variants[i]
      }
    }
    return currentVariant || preferGoldThenFirst(variants)
  }

  function shouldInlineProductVariants(card) {
    if (!card || card.included) return false
    if (productHasWeightSizingVariants(card)) return false
    return variantsForInlineCardTray(card).length > 1
  }

  function activeInlineVariantId(card, entry) {
    var pid = card && card.productId != null ? String(card.productId) : ''
    var selected = selectedItemForCard(entry, card)
    if (selected && selected.variantId != null) return String(selected.variantId)
    if (
      entry &&
      entry.variantActiveByProduct &&
      pid &&
      entry.variantActiveByProduct[pid]
    ) {
      return String(entry.variantActiveByProduct[pid])
    }
    if (card && card.variantId != null) return String(card.variantId)
    return ''
  }

  /**
   * Unit for pack-size variants on one product.
   * Theme Editor “Quantity unit” wins; Shopify titles like “1 x Jar” are a
   * fallback. “Jar” is ignored when the style is a tin.
   */
  function resolveInlineQuantityUnit(card, styleTitle, variantUnit) {
    var teUnit = ''
    if (card && card._swatchMeta && card._swatchMeta.optionUnit) {
      teUnit = String(card._swatchMeta.optionUnit).trim()
    }
    if (teUnit) return teUnit
    var variant = String(variantUnit || '').trim()
    if (/^jars?$/i.test(variant) && /\btins?\b/i.test(String(styleTitle || ''))) {
      return ''
    }
    if (variant && /^[A-Za-z]+$/.test(variant)) return pluraliseUnit(variant.toLowerCase())
    return variant
  }

  /**
   * Pack-size / finish variants on one Shopify product → card tray.
   * Weight-tier urn sizes and nameplate Gold/Silver are excluded
   * (see productHasWeightSizingVariants / variantsForInlineCardTray).
   */
  function applyInlineVariantOptions(card, entry) {
    if (!card) return card
    var variants = variantsForInlineCardTray(card)
    var styleTitle =
      (card._swatchMeta && card._swatchMeta.styleTitle) ||
      card.styleTitle ||
      card.title ||
      ''
    var labels = (card._swatchMeta && card._swatchMeta.optionLabels) || []
    var parsedUnit = ''
    var teCard =
      teCardImageForVariantIndex(card._variantCutoffSettings, 0) ||
      teCardImageFromBlock(card._variantCutoffSettings) ||
      teImageUrl(card.baseImage) ||
      null
    var options = variants.map(function (v, i) {
      var qty = parsePackQuantityFromVariantTitle(v.title)
      var unit = parsePackUnitFromVariantTitle(v.title)
      if (unit && !parsedUnit) parsedUnit = unit
      var teLabel = String(labels[i] || '').trim()
      var optionTeCard = teCardImageForVariantIndex(card._variantCutoffSettings, i)
      var optionImage =
        optionTeCard || teImageUrl(v.image) || teImageUrl(card.image) || null
      return {
        id: String(v.id),
        productId: card.productId,
        variantId: v.id,
        colourLabel: teLabel || String(v.title || '').trim() || 'Option',
        labelOverride: teLabel,
        swatchNumber: qty,
        swatchImage: null,
        swatchColor: '',
        thumb: optionImage,
        image: optionImage,
        price: v.price,
        selectable: v.available !== false,
      }
    })
    var model = buildCardOptionModel(
      options,
      styleTitle,
      resolveInlineQuantityUnit(card, styleTitle, parsedUnit)
    )
    var activeId = activeInlineVariantId(card, entry)
    var active = findCardOption({ options: model.options }, activeId) || model.options[0]
    var variant =
      (active &&
        variants.filter(function (v) {
          return String(v.id) === String(active.id)
        })[0]) ||
      variants[0]
    card.optionKind = 'variant'
    card.optionType = model.optionType
    card.options = model.options
    card.optionLabel = model.optionLabel
    card.quantityUnit = model.quantityUnit
    card.swatches = model.options
    card.swatchMembers = [card]
    card.swatchGroup = 'variant:' + String(card.productId)
    card.activeOptionId = active ? String(active.id) : ''
    if (variant) {
      card.variant = variant
      card.variantId = variant.id
      card.price = variant.price
      if (variant.compareAtPrice != null) card.compareAtPrice = variant.compareAtPrice
    }
    card.image = (active && active.image) || teCard || teImageUrl(variant && variant.image) || card.image
    card.variantMeta = active && active.label ? active.label : ''
    card.baseImage = teCard || card.baseImage || card.image || null
    return card
  }

  function applySoloCardOptions(card, entry) {
    if (!card) return card
    if (shouldInlineProductVariants(card)) return applyInlineVariantOptions(card, entry)
    return applyNoOptionModel(card)
  }

  function ensureVariantActiveMap(entry) {
    if (!entry.variantActiveByProduct || typeof entry.variantActiveByProduct !== 'object') {
      entry.variantActiveByProduct = {}
    }
    return entry.variantActiveByProduct
  }

  /**
   * Switch pack-size / finish variant on a single-product card. Same productId;
   * only variantId / price / image change. Weight-tier products never reach here.
   */
  function selectInlineVariant(entry, card, optionId, mode) {
    if (!entry || !card || optionId == null || optionId === '') return null
    var opt = findCardOption(card, optionId)
    if (!opt) return null
    var variant = ((card.product && card.product.variants) || []).filter(function (v) {
      return v && String(v.id) === String(optionId)
    })[0]
    if (!variant) return null
    mode = mode || entry.mode
    ensureVariantActiveMap(entry)[String(card.productId)] = String(variant.id)
    card.activeOptionId = String(variant.id)
    card.variant = variant
    card.variantId = variant.id
    card.price = variant.price
    var variantIdx = variantIndexOnProduct(card.product, variant)
    card.image =
      opt.image ||
      teCardImageForVariantIndex(card._variantCutoffSettings, variantIdx) ||
      teCardImageFromBlock(card._variantCutoffSettings) ||
      variant.image ||
      card.image
    card.variantMeta = opt.label || ''
    var existing = selectedItemForCard(entry, card)
    if (existing) {
      existing.variantId = variant.id
      existing.price = card.included ? 0 : variant.price
      existing.image = card.image
      existing.variantMeta = card.variantMeta
    }
    return card
  }

  /**
   * True for auto-picked weight-tier captions we never show (Small / Medium /
   * “Up to 25 kg”). Colour names and pack labels stay visible.
   */
  function isWeightSizeVariantMeta(label) {
    var s = String(label || '').replace(/\s+/g, ' ').trim()
    if (!s) return false
    if (/^(small|medium|large|regular|mini)$/i.test(s)) return true
    if (/^up to\s+\d/i.test(s)) return true
    if (/\bup to\s+\d+(?:\.\d+)?\s*kgs?\b/i.test(s) && /^(small|medium|large|regular)\b/i.test(s)) {
      return true
    }
    return false
  }

  /**
   * Meta line for a solo (optionType none) card. Weight-tier size is auto-picked
   * and shown by the variant photo — never a “Medium” caption. Colour / quantity
   * families set variantMeta from the active option label instead.
   */
  function soloVariantMeta(_card) {
    return ''
  }

  function findCardOption(card, optionId) {
    var list = (card && card.options) || []
    for (var i = 0; i < list.length; i++) {
      if (list[i] && String(list[i].id) === String(optionId)) return list[i]
    }
    return null
  }

  /**
   * Stage image for a card, optionally for a specific option.
   * Re-runs cascade A/B so Theme Editor Card photos still win if catalog
   * `card.image` was overwritten by a Shopify variant shot.
   * Colour / quantity option.image (already cascade-resolved) wins on cascade A
   * so swatch taps can show that slot’s Card photo.
   */
  function resolveCardImage(card, optionId) {
    if (!card) return null
    var id = optionId != null && optionId !== '' ? optionId : card.activeOptionId
    var option = findCardOption(card, id)
    var followSize = productHasWeightTierSizeImages(card.product, card._variantCutoffSettings)
    var variant = card.variant
    if (option && option.variantId && card.product && Array.isArray(card.product.variants)) {
      var i
      for (i = 0; i < card.product.variants.length; i++) {
        var v = card.product.variants[i]
        if (v && String(v.id) === String(option.variantId)) {
          variant = v
          break
        }
      }
    }
    var resolved = resolveWeightTierDisplayImage(card.product, variant, card._variantCutoffSettings, {
      teCard: teCardImageFromBlock(card._variantCutoffSettings),
      productImage: (card.product && card.product.image) || null,
    })
    if (followSize && resolved) return resolved
    if (option && option.image) return option.image
    if (resolved) return resolved
    return card.baseImage || card.image || null
  }

  function collapseSwatchFamilies(cards, entry) {
    var list = cards || []
    if (!list.length) return list

    var groups = {}
    var order = []
    list.forEach(function (card, idx) {
      if (!card || card.included) {
        order.push({ type: 'single', card: card, idx: idx })
        return
      }
      var meta = card._swatchMeta || resolveCardSwatchMeta(card, null)
      card._swatchMeta = meta
      if (!meta.groupKey) {
        order.push({ type: 'single', card: card, idx: idx })
        return
      }
      if (!groups[meta.groupKey]) {
        groups[meta.groupKey] = []
        order.push({ type: 'family', groupKey: meta.groupKey, idx: idx })
      }
      groups[meta.groupKey].push(card)
    })

    var out = []
    order.forEach(function (slot) {
      if (slot.type === 'single') {
        if (slot.card) {
          slot.card.swatches = null
          slot.card.swatchGroup = ''
          slot.card.selectionLabel = slot.card.selectionLabel || slot.card.title
          applySoloCardOptions(slot.card, entry)
        }
        out.push(slot.card)
        return
      }
      var members = groups[slot.groupKey] || []
      if (members.length < 2) {
        members.forEach(function (m) {
          if (!m) return
          // Solo after collapse — honour weight/stock hide that swatch keep-alive deferred.
          if (m._eligibilityHide) return
          m.swatches = null
          m.swatchGroup = ''
          m.selectionLabel = m.selectionLabel || m.title
          applySoloCardOptions(m, entry)
          out.push(m)
        })
        return
      }

      // Family capacity: if every colour would be hidden (e.g. all max 10 kg vs
      // a 20 kg pet), drop the whole style — don't keep a disabled swatch shell.
      var anySelectable = false
      var allWouldHide = true
      members.forEach(function (m) {
        if (m && m.selectable) anySelectable = true
        if (!m || !m._eligibilityHide) allWouldHide = false
      })
      if (!anySelectable && allWouldHide) return

      var styleTitle = familyStyleTitle(members, slot.groupKey)
      var union = unionFamilyAddonFlags(members)
      members.forEach(function (m) {
        applyUnionAddonFlagsToCard(m, union)
        m.selectionLabel = m.title || styleTitle
        m.swatchGroup = slot.groupKey
      })

      var active = pickActiveSwatchMember(members, entry, slot.groupKey) || members[0]
      var model = buildCardOptionModel(
        members.map(function (m) {
          return buildSwatchOption(m, styleTitle)
        }),
        styleTitle,
        familyOptionUnit(members)
      )
      var familyTitle = getMemorialDisplayTitle({
        styleTitle: styleTitle,
        product: active && active.product,
      })
      var familyCard = Object.assign({}, active, {
        title: familyTitle,
        styleTitle: styleTitle || familyTitle,
        shopifyTitle: (active.product && active.product.title) || familyTitle,
        selectionLabel: familyTitle,
        swatchGroup: slot.groupKey,
        optionType: model.optionType,
        optionKind: 'family',
        options: model.options,
        optionLabel: model.optionLabel,
        quantityUnit: model.quantityUnit,
        // Legacy alias — same array, so the tray has one source of truth.
        swatches: model.options,
        swatchMembers: members,
        activeOptionId: String(active.productId),
        baseImage: active.baseImage || active.image || (active.product && active.product.image) || null,
        supporting:
          String((active && active.supporting) || '').trim() ||
          defaultFurFeatherCardDescription(familyTitle),
      })
      var familyRank = cardCollectionIndex(familyCard)
      if (familyRank != null) familyCard._collectionIndex = familyRank
      var activeOption = findCardOption(familyCard, familyCard.activeOptionId)
      // One-tap variant swap: the active option's image wins, base image otherwise.
      familyCard.image = (activeOption && activeOption.image) || familyCard.baseImage || familyCard.image
      familyCard.variantMeta = activeOption ? activeOption.label : ''
      // Family card is choosable only when at least one colour fits.
      if (!anySelectable) {
        familyCard.selectable = false
        if (!familyCard.eligibilityNote) {
          var noteFrom = members.find(function (m) {
            return m && m.eligibilityNote
          })
          if (noteFrom) familyCard.eligibilityNote = noteFrom.eligibilityNote
        }
      }
      out.push(familyCard)
    })
    return out
  }

  function findCardInCategory(cat, productId) {
    if (!cat || productId == null || productId === '') return null
    var pid = String(productId)
    if (cat.includedCard && String(cat.includedCard.productId) === pid) {
      return cat.includedCard
    }
    if (cat.cardByProductId && cat.cardByProductId[pid]) {
      return cat.cardByProductId[pid]
    }
    var cards = cat.allCards || []
    for (var i = 0; i < cards.length; i++) {
      var c = cards[i]
      if (!c) continue
      if (String(c.productId) === pid) return c
      if (c.swatchMembers) {
        for (var j = 0; j < c.swatchMembers.length; j++) {
          if (c.swatchMembers[j] && String(c.swatchMembers[j].productId) === pid) {
            return c.swatchMembers[j]
          }
        }
      }
    }
    return null
  }

  function findDisplayCardInCategory(cat, productId) {
    if (!cat || productId == null || productId === '') return null
    var pid = String(productId)
    var cards = cat.allCards || []
    var i
    var j
    for (i = 0; i < cards.length; i++) {
      var c = cards[i]
      if (!c) continue
      if (String(c.productId) === pid) return c
      if (c.swatchMembers) {
        for (j = 0; j < c.swatchMembers.length; j++) {
          if (c.swatchMembers[j] && String(c.swatchMembers[j].productId) === pid) return c
        }
      }
    }
    return findCardInCategory(cat, productId)
  }

  /** Customer-facing catalog sort. `suggested` is Theme Editor order.
   *  `best_selling` is the collection’s Shopify Admin order (set that
   *  collection to Best selling for real sales ranking). */
  function normalizeCatalogSort(value) {
    var v = String(value || '').trim()
    if (v === 'price_asc' || v === 'price_desc' || v === 'best_selling') return v
    return 'suggested'
  }

  function cardSortPrice(card) {
    if (!card || card.included) return 0
    var n = Number(card.price)
    return isFinite(n) ? n : null
  }

  function cardCollectionIndex(card) {
    if (!card) return null
    var idx = numOrNull(card._collectionIndex)
    if (idx != null) return idx
    var members = card.swatchMembers || []
    var best = null
    var i
    for (i = 0; i < members.length; i++) {
      var mi = numOrNull(members[i] && members[i]._collectionIndex)
      if (mi == null) continue
      if (best == null || mi < best) best = mi
    }
    return best
  }

  function sortCatalogCards(cards, mode) {
    var list = (cards || []).slice()
    var m = normalizeCatalogSort(mode)
    if (m === 'suggested') return list
    if (m === 'best_selling') {
      return list.sort(function (a, b) {
        var ai = cardCollectionIndex(a)
        var bi = cardCollectionIndex(b)
        if (ai != null && bi != null && ai !== bi) return ai - bi
        if (ai != null && bi == null) return -1
        if (ai == null && bi != null) return 1
        return String((a && a.title) || '').localeCompare(String((b && b.title) || ''))
      })
    }
    return list.sort(function (a, b) {
      var ap = cardSortPrice(a)
      var bp = cardSortPrice(b)
      if (ap != null && bp != null && ap !== bp) {
        return m === 'price_asc' ? ap - bp : bp - ap
      }
      if (ap != null && bp == null) return -1
      if (ap == null && bp != null) return 1
      return String((a && a.title) || '').localeCompare(String((b && b.title) || ''))
    })
  }

  /** Collection-sourced grids honour featured limits. Manual lists show everything
   *  except urns, which cap at the featured limit so See more can hide extras. */
  function categoryShowsFeaturedCap(category) {
    if (!category) return false
    if (category.productSource !== 'manual') return true
    return category.key === 'urn'
  }

  function featuredLimitForCategory(category, settings, isDesktop) {
    var memorials = settings || {}
    var lim = isDesktop
      ? (Number(category && category.featuredLimitDesktop) || memorials.featuredLimitDesktop || 8)
      : (Number(category && category.featuredLimitMobile) || memorials.featuredLimitMobile || 8)
    return Math.max(1, lim)
  }

  function catalogCardIsVisible(list, card) {
    if (!card || !list || !list.length) return false
    var pid = card.productId != null ? String(card.productId) : ''
    var i
    var j
    for (i = 0; i < list.length; i++) {
      var c = list[i]
      if (!c) continue
      if (pid && String(c.productId) === pid) return true
      var members = c.swatchMembers || []
      for (j = 0; j < members.length; j++) {
        if (members[j] && pid && String(members[j].productId) === pid) return true
      }
    }
    return false
  }

  function ensureSelectedCatalogCards(visible, category, entry, pool) {
    var out = (visible || []).slice()
    var source = pool || (category && category.allCards) || []
    ;((entry && entry.items) || []).forEach(function (it) {
      if (!it || it.included || it.productId == null) return
      var card = findDisplayCardInCategory(category, it.productId)
      if (!card || card.included || catalogCardIsVisible(out, card)) return
      var fromPool = source.filter(function (c) {
        return c && String(c.productId) === String(card.productId)
      })[0]
      out.push(fromPool || card)
    })
    return out
  }

  /**
   * Cards on the category step (not View all). Suggested = featured slice.
   * Price sorts the full catalog, then recaps, and always keeps a selected urn visible.
   */
  function visibleCategoryCards(category, entry, settings, isDesktop) {
    var all = (category && category.allCards) || []
    var sort = normalizeCatalogSort(entry && entry.catalogSort)
    if (sort === 'suggested') {
      var featured = isDesktop
        ? ((category && category.featuredDesktop) || all)
        : ((category && category.featuredMobile) || all)
      return ensureSelectedCatalogCards(featured, category, entry, all)
    }
    var ordered = sortCatalogCards(all, sort)
    if (!categoryShowsFeaturedCap(category)) {
      return ensureSelectedCatalogCards(ordered, category, entry, ordered)
    }
    var limit = featuredLimitForCategory(category, settings, isDesktop)
    return ensureSelectedCatalogCards(ordered.slice(0, limit), category, entry, ordered)
  }

  /**
   * Switch the active colour product on a swatch family. When the family is
   * already selected, migrates the ledger item + personalisation addons and
   * ashes-placement pointers to the destination productId.
   */
  function selectSwatchColour(entry, familyCard, colourProductId, mode) {
    if (familyCard && familyCard.optionKind === 'variant') {
      return selectInlineVariant(entry, familyCard, colourProductId, mode)
    }
    if (!entry || !familyCard || colourProductId == null || colourProductId === '') return null
    var toId = String(colourProductId)
    var members = familyCard.swatchMembers || []
    var toCard = null
    for (var i = 0; i < members.length; i++) {
      if (members[i] && String(members[i].productId) === toId) {
        toCard = members[i]
        break
      }
    }
    if (!toCard && String(familyCard.productId) === toId) toCard = familyCard
    if (!toCard || !toCard.selectable) return null

    var groupKey = familyCard.swatchGroup || (familyCard._swatchMeta && familyCard._swatchMeta.groupKey) || ''
    if (!entry.swatchActiveByFamily || typeof entry.swatchActiveByFamily !== 'object') {
      entry.swatchActiveByFamily = {}
    }
    if (groupKey) entry.swatchActiveByFamily[groupKey] = toId

    var fromId = null
    var fromIdx = -1
    ;(entry.items || []).forEach(function (it, idx) {
      if (!it || it.productId == null) return
      var pid = String(it.productId)
      if (pid === toId) {
        fromId = pid
        fromIdx = idx
        return
      }
      for (var m = 0; m < members.length; m++) {
        if (members[m] && String(members[m].productId) === pid) {
          fromId = pid
          fromIdx = idx
        }
      }
    })

    if (fromIdx < 0) {
      // Preview only — card re-render picks active from swatchActiveByFamily.
      return toCard
    }

    if (fromId === toId) {
      var sameQty = entry.items[fromIdx] && entry.items[fromIdx].quantity
      entry.items[fromIdx] = selectionFromCard(toCard, entry.items[fromIdx].properties)
      if (sameQty) entry.items[fromIdx].quantity = normalizeItemQuantity(sameQty)
      return toCard
    }

    ensureItemAddons(entry)
    var buckets = entry.itemAddons || {}
    if (buckets[fromId]) {
      buckets[toId] = buckets[fromId]
      delete buckets[fromId]
    }
    if (String(entry._focusedAddonProductId || '') === fromId) {
      entry._focusedAddonProductId = toId
      entry.addons = buckets[toId] || {}
    }

    if (String(entry.ashDestinationProductId || '') === fromId) {
      entry.ashDestinationProductId = toId
    }
    if (Array.isArray(entry.ashesReceivingProductIds)) {
      entry.ashesReceivingProductIds = entry.ashesReceivingProductIds.map(function (id) {
        return String(id) === fromId ? toId : id
      })
    }

    var migratedQty = entry.items[fromIdx] && entry.items[fromIdx].quantity
    entry.items[fromIdx] = selectionFromCard(toCard, entry.items[fromIdx].properties)
    if (migratedQty) entry.items[fromIdx].quantity = normalizeItemQuantity(migratedQty)
    pruneItemAddons(entry)
    syncAshesPlacement(entry)
    return toCard
  }

  function isFamilyMemberSelected(entry, familyCard) {
    if (!entry || !familyCard) return false
    if (isSelected(entry, familyCard.productId)) return true
    var members = familyCard.swatchMembers || []
    for (var i = 0; i < members.length; i++) {
      if (members[i] && isSelected(entry, members[i].productId)) return true
    }
    return false
  }

  /** Theme Editor photo print size — blocks saved before this setting existed emit null. */
  function normalizePhotoPrintSize(value) {
    return String(value || '').trim() === '5x7' ? '5x7' : '4x6'
  }

  /** Optional PNG overlay for the photo step (transparent window). Blank = 4×6 preview. */
  function normalizePhotoMockupFrame(value) {
    var s = String(value == null ? '' : value).trim()
    return s || null
  }

  /** `client_choice` defers portrait/landscape to the family on the photo step. */
  function normalizePhotoOrientation(value) {
    var v = String(value || '').trim()
    if (v === 'landscape') return 'landscape'
    if (v === 'client_choice') return 'client_choice'
    return 'portrait'
  }

  /** Theme Editor default side of the toggle when orientation is `client_choice`.
   *  Unconfigured / unknown → landscape (matches Theme Editor schema default). */
  function normalizePhotoOrientationDefault(value) {
    var v = String(value || '').trim()
    if (v === 'portrait') return 'portrait'
    return 'landscape'
  }

  /**
   * Theme Editor “2. Memorials — Product” block + optional product.booking metafields.
   * Fee products / nameplate colour source live on the Product block. Gold and Silver
   * are separate Shopify products (not colour variants) — set either or both.
   */
  function addonFlagsForProduct(product, itemBlock) {
    var flags = {
      needsPhoto: false,
      needsNameplate: false,
      needsEngraving: false,
      needsNameplateText: false,
      nameplateIncluded: false,
      photoFeeProduct: null,
      engravingFeeProduct: null,
      nameplateProductGold: null,
      nameplateProductSilver: null,
      nameplateGoldLabel: '',
      nameplateSilverLabel: '',
      nameplatePreviewImageGold: null,
      nameplatePreviewImageSilver: null,
      personaliseImage: null,
      personaliseImageGold: null,
      personaliseImageSilver: null,
      cardImageGold: null,
      cardImageSilver: null,
      reviewBlankCardImage: null,
      photoPrintSize: '4x6',
      photoOrientation: 'portrait',
      photoOrientationDefault: 'landscape',
      photoMockupFrame: null,
      photoCardTitle: '',
      photoCardSubtitle: '',
      engravingCardTitle: '',
      engravingCardSubtitle: '',
    }
    if (itemBlock && itemBlock.settings) {
      var s = itemBlock.settings
      flags.needsPhoto = !!s.needs_photo
      flags.needsNameplate = !!s.needs_nameplate
      flags.needsEngraving = !!s.needs_engraving
      flags.nameplateIncluded = !!s.nameplate_included && flags.needsNameplate
      flags.needsNameplateText = !!s.needs_nameplate_text && flags.needsNameplate && !flags.needsEngraving
      flags.photoFeeProduct = s.photo_fee_product || null
      flags.engravingFeeProduct = s.engraving_fee_product || null
      flags.nameplateProductGold = s.nameplate_product_gold || null
      flags.nameplateProductSilver = s.nameplate_product_silver || null
      flags.nameplateGoldLabel = String(s.nameplate_gold_label || '').trim()
      flags.nameplateSilverLabel = String(s.nameplate_silver_label || '').trim()
      flags.nameplatePreviewImageGold = s.nameplate_preview_gold || null
      flags.nameplatePreviewImageSilver = s.nameplate_preview_silver || null
      flags.personaliseImage = teImageUrl(s.personalise_image)
      flags.personaliseImageGold = teImageUrl(s.personalise_image_gold)
      flags.personaliseImageSilver = teImageUrl(s.personalise_image_silver)
      flags.cardImageGold = teImageUrl(s.card_image_gold)
      flags.cardImageSilver = teImageUrl(s.card_image_silver)
      flags.reviewBlankCardImage = teImageUrl(s.review_blank_card_image)
      flags.photoPrintSize = normalizePhotoPrintSize(s.photo_print_size)
      flags.photoOrientation = normalizePhotoOrientation(s.photo_orientation)
      flags.photoOrientationDefault = normalizePhotoOrientationDefault(s.photo_orientation_default)
      flags.photoMockupFrame = normalizePhotoMockupFrame(s.photo_mockup_frame)
      flags.photoCardTitle = String(s.photo_card_title || '').trim()
      flags.photoCardSubtitle = String(s.photo_card_subtitle || '').trim()
      flags.engravingCardTitle = String(s.engraving_card_title || '').trim()
      flags.engravingCardSubtitle = String(s.engraving_card_subtitle || '').trim()
    }
    var b = product && product.booking
    if (b) {
      if (!flags.needsPhoto && truthy(b.needsPhoto)) flags.needsPhoto = true
      if (!flags.needsNameplate && truthy(b.needsNameplate)) flags.needsNameplate = true
      if (!flags.needsEngraving && truthy(b.needsEngraving)) flags.needsEngraving = true
      if (!flags.nameplateIncluded && flags.needsNameplate && truthy(b.nameplateIncluded)) {
        flags.nameplateIncluded = true
      }
    }
    // Engraving lines cover inscription when engraving is on.
    if (flags.needsEngraving) flags.needsNameplateText = false
    else if (itemBlock && itemBlock.settings && itemBlock.settings.needs_nameplate_text && flags.needsNameplate) {
      flags.needsNameplateText = true
    }
    if (!flags.needsNameplate) flags.nameplateIncluded = false
    return flags
  }

  function applyAddonFlags(card, product, itemBlock) {
    var flags = addonFlagsForProduct(product, itemBlock)
    card.needsPhoto = flags.needsPhoto
    card.needsNameplate = flags.needsNameplate
    card.needsEngraving = flags.needsEngraving
    card.needsNameplateText = flags.needsNameplateText
    card.nameplateIncluded = flags.nameplateIncluded
    card.photoFeeProduct = flags.photoFeeProduct
    card.engravingFeeProduct = flags.engravingFeeProduct
    card.nameplateProductGold = flags.nameplateProductGold
    card.nameplateProductSilver = flags.nameplateProductSilver
    card.nameplateGoldLabel = flags.nameplateGoldLabel
    card.nameplateSilverLabel = flags.nameplateSilverLabel
    card.nameplatePreviewImageGold = flags.nameplatePreviewImageGold
    card.nameplatePreviewImageSilver = flags.nameplatePreviewImageSilver
    card.personaliseImage = flags.personaliseImage
    card.personaliseImageGold = flags.personaliseImageGold
    card.personaliseImageSilver = flags.personaliseImageSilver
    card.cardImageGold = flags.cardImageGold
    card.cardImageSilver = flags.cardImageSilver
    card.reviewBlankCardImage = flags.reviewBlankCardImage
    card.photoPrintSize = flags.photoPrintSize
    card.photoOrientation = flags.photoOrientation
    card.photoOrientationDefault = flags.photoOrientationDefault
    card.photoMockupFrame = flags.photoMockupFrame
    card.photoCardTitle = flags.photoCardTitle
    card.photoCardSubtitle = flags.photoCardSubtitle
    card.engravingCardTitle = flags.engravingCardTitle
    card.engravingCardSubtitle = flags.engravingCardSubtitle
    if (itemBlock && itemBlock.id) card.itemBlockId = itemBlock.id
    if (itemBlock && itemBlock.settings) card._variantCutoffSettings = itemBlock.settings
    else if (itemBlock) card._variantCutoffSettings = itemBlock
  }

  function enrichCard(product, overrideLabel, eligibility, category, included, itemBlock, state) {
    var resolved = (eligibility && eligibility.resolvedVariant) ||
      resolveMemorialVariant(product, petWeightKg(state), itemBlock || null)
    var variant = (resolved && resolved.variant) || firstAvailableVariant(product)
    if (
      variant &&
      variantsForDetailPicker({ product: product }).length === 0 &&
      (product.variants || []).length > 1
    ) {
      var metalEntry =
        state && state.memorials && category && category.key
          ? state.memorials[category.key]
          : null
      variant = variantForNameplateColour(
        { product: product },
        nameplateMetalForProduct(metalEntry, product.id) || 'gold',
        variant
      ) || variant
    }
    var badge = ''
    // Included is shown as the price line only (no image badge — product photos cover it).
    if (!included && category.showBadges !== false && product.booking && product.booking.cardBadge) {
      var cardBadge = String(product.booking.cardBadge).trim()
      if (cardBadge && !/^included$/i.test(cardBadge)) badge = cardBadge
    }
    var note = ''
    var styleTitle = styleTitleFromBlock(itemBlock, product)
    var displayTitle = getMemorialDisplayTitle({
      styleTitle: styleTitle,
      labelOverride: overrideLabel,
      product: product,
    })
    var supporting = ''
    var teDesc =
      itemBlock && itemBlock.settings && itemBlock.settings.card_description
        ? String(itemBlock.settings.card_description).trim()
        : ''
    if (teDesc) supporting = teDesc
    else if (category.showSupportingLabels !== false && product.booking && product.booking.shortDescription) {
      supporting = String(product.booking.shortDescription).trim()
    }
    if (!supporting && isFurFeatherMemorialCategory(category)) {
      supporting = defaultFurFeatherCardDescription(displayTitle)
    }
    if (!eligibility.ok && eligibility.note) note = eligibility.note
    else if (category.showEligibilityNotes && eligibility.note) note = eligibility.note
    var teImage = teCardImageFromBlock(itemBlock)
    var cutoffSettings = (itemBlock && itemBlock.settings) || itemBlock || null
    var weightImage = resolveWeightTierDisplayImage(product, variant, cutoffSettings, {
      teCard: teImage,
      productImage: product.image || null,
    })
    var card = {
      productId: product.id,
      product: product,
      variant: variant,
      variantId: variant ? variant.id : null,
      title: displayTitle,
      styleTitle: styleTitle || displayTitle,
      shopifyTitle: (product && product.title) || displayTitle,
      image: weightImage || teImage || teImageUrl(variant && variant.image) || teImageUrl(product.image),
      baseImage: teImage || teImageUrl(product.image) || teImageUrl(variant && variant.image) || null,
      price: variant ? variant.price : null,
      compareAtPrice: variant && variant.compareAtPrice != null ? variant.compareAtPrice : null,
      included: !!included,
      selectable: !!eligibility.ok && (!!variant || !!included),
      badge: badge,
      supporting: supporting,
      eligibilityNote: note,
      weightAutoSelected: !!(eligibility && eligibility.weightAutoSelected) || !!(resolved && resolved.autoSelected),
      hasWeightCutoffs: !!(resolved && resolved.hasCutoffs),
      weightMaxKg:
        resolved && resolved.maxKg != null
          ? resolved.maxKg
          : eligibility && eligibility.maxKg != null
            ? eligibility.maxKg
            : null,
      _variantCutoffSettings:
        (itemBlock && itemBlock.settings) || itemBlock || null,
      group: null,
      _collectionIndex: numOrNull(product && product._collectionIndex),
    }
    applyCapacityToCard(card, itemBlock, included)
    if (card.capacityRole === CAPACITY_ROLE_KEEPSAKE) {
      card.eligibilityNote = ''
    }
    // Every card ships the option contract; collapseSwatchFamilies upgrades
    // colour/quantity families in place.
    card.variantMeta = soloVariantMeta(card)
    return applyNoOptionModel(card)
  }

  function buildCatalog(config, state) {
    var memorials = config.memorials || normalizeMemorialSettings({})
    var groups = config.memorialGroups || []
    var itemsByGroup = config.memorialItemsByGroup || {}
    var categories = []
    var flow = config.flow === 'shared' ? 'shared' : 'individual'

    groups.forEach(function (block) {
      var s = block.settings || {}
      var key = String(s.group_key || '').trim()
      if (!key) return
      if (s.enabled === false || s.enabled === 'false') return
      if (!(s.flows === 'both' || s.flows === flow || !s.flows)) return

      var mode = selectionModeFromGroup(s)
      var layout = s.layout === 'dual_groups' ? 'dual_groups' : 'grid'
      var category = {
        key: key,
        blockId: block.id,
        enabled: true,
        title: s.title || 'Choose a keepsake',
        supportingCopy: String(s.supporting_copy || '').trim(),
        helperText: s.helper_text || '',
        // Explicit TE “Review summary label” only — summary/review subheads prefer this.
        // Callers fall back to title / friendly key labels when empty.
        reviewLabel: (s.review_label && String(s.review_label).trim()) || '',
        flows: s.flows || 'both',
        selectionMode: mode,
        layout: layout,
        groupAHeading: s.group_a_heading || 'Ink Print Keepsakes',
        groupBHeading: s.group_b_heading || 'Clay Impression Keepsakes',
        includedProduct: s.included_product || null,
        includedCardImage: String(s.included_card_image || '').trim() || null,
        includedCardDescription: String(s.included_card_description || '').trim() || '',
        featuredLimitMobile: Number(s.featured_limit_mobile) || 0,
        featuredLimitDesktop: Number(s.featured_limit_desktop) || 0,
        showViewAll: s.show_view_all !== false,
        viewAllLabel: (s.view_all_label && String(s.view_all_label).trim()) || memorials.viewAllLabel,
        showBadges: s.show_badges !== false,
        showSupportingLabels: s.show_supporting_labels !== false,
        showEligibilityNotes: s.show_eligibility_notes !== false,
        timeSensitiveOnly: !!s.time_sensitive_only,
        // Set after Product-block scan — may heal "manual" → "both" when only a collection is wired.
        productSource: 'both',
        collectionSort: normalizeCollectionSort(s.collection_sort),
        editStepId: 'memorial:' + key,
        emptyReason: '',
        emptyReasonMerchant: false,
      }

      // Resolve collections first so Colour N handle-only TE slots can hydrate
      // against collection products (Theme Editor sometimes omits full product JSON).
      var productSourceEarly = (s.product_source === 'manual' || s.product_source === 'collection')
        ? s.product_source
        : 'both'
      var collectionSort = category.collectionSort
      var collectionPrimaryRaw = stampCollectionIndex(s.collectionProducts || [])
      var collectionSecondaryRaw = stampCollectionIndex(s.collectionSecondaryProducts || [])
      var slotResolvePools = [collectionPrimaryRaw, collectionSecondaryRaw]

      var seen = {}
      var featuredOverrides = []
      var itemBlockProducts = []
      var overrideMeta = {}
      var teSwatchProductIds = {}
      // Stamp Theme Editor Product-block sequence so sortProducts / swatch
      // collapse keep merchant sidebar order (not title / featured sort).
      var teBlockOrder = 0
      ;(itemsByGroup[key] || []).forEach(function (itemBlock) {
        var blockTeOrder = teBlockOrder++
        var slotEntries = teSwatchSlotEntries(itemBlock, slotResolvePools)
        if (slotEntries.length) {
          // One Product block with “Show colour swatches” ON → Colour 1 + slots
          // become sibling products in the same swatch family.
          // Stamp family key whenever swatches are ON so collection duplicates
          // (same Classic Urn / style title) merge into this card, not extras.
          // Family grid position = this Product block’s TE index (all slots share it).
          var familyKey = ensureTeFamilySwatchGroup(itemBlock)
          slotEntries.forEach(function (entry) {
            var p = entry && entry.product
            if (!p || !p.id) return
            var id = String(p.id)
            var metaBlock = entry.itemBlock
            // Stamp shared family key on Colour 1 too so metafield/auto paths
            // cannot split progressive slots into different groups.
            if (familyKey && entry.slot === 1 && metaBlock && metaBlock.settings) {
              if (!normalizeSwatchGroupKey(metaBlock.settings.swatch_group)) {
                metaBlock = {
                  id: itemBlock.id,
                  type: itemBlock.type || 'memorial_item',
                  settings: Object.assign({}, metaBlock.settings, {
                    swatch_group: familyKey,
                  }),
                  _swatchSlotIndex: 1,
                  _swatchParentBlockId: itemBlock.id,
                }
              }
            }
            if (metaBlock && metaBlock.settings && familyKey) {
              teSwatchProductIds[id] = true
            }
            // Prefer Colour 1’s block meta when the same product appears twice.
            if (!overrideMeta[id] || entry.slot === 1) {
              overrideMeta[id] = metaBlock
            }
            if (!seen[id]) {
              p._teOrder = blockTeOrder
              itemBlockProducts.push(p)
              if (itemBlock.settings && itemBlock.settings.featured !== false) {
                featuredOverrides.push(p)
              }
            }
            mergeProducts([p], seen)
          })
          return
        }
        var p = itemBlock.settings && itemBlock.settings.product
        if (!p) return
        overrideMeta[String(p.id)] = itemBlock
        if (!seen[String(p.id)]) {
          p._teOrder = blockTeOrder
        }
        itemBlockProducts.push(p)
        if (itemBlock.settings.featured !== false) featuredOverrides.push(p)
        mergeProducts([p], seen)
      })

      stampCollectionIndexOntoProducts(
        itemBlockProducts,
        collectionPrimaryRaw.concat(collectionSecondaryRaw)
      )

      // manual = Product blocks only; collection = Shopify collection only; both = merge.
      // Default "both" when unset. Heal a common regression: schema/templates defaulted to
      // "manual" while the Category still has a collection (README’s typical urn setup) and
      // no Product blocks — ignoring the collection left the step empty with a TE tip.
      var productSource = productSourceEarly
      if (
        productSource === 'manual' &&
        !itemBlockProducts.length &&
        (collectionPrimaryRaw.length || collectionSecondaryRaw.length)
      ) {
        productSource = 'both'
      }
      category.productSource = productSource

      var useCollection = productSource !== 'manual'
      var useBlocks = productSource !== 'collection'
      var collectionPrimary = useCollection ? collectionPrimaryRaw : []
      var collectionSecondary = useCollection ? collectionSecondaryRaw : []
      var blockProducts = useBlocks ? itemBlockProducts : []

      // Copy TE Product-block order onto matching collection products so
      // dual_groups / “both” grids follow sidebar order for curated SKUs.
      var teOrderById = {}
      blockProducts.forEach(function (p) {
        if (!p || p.id == null || p._teOrder == null) return
        teOrderById[String(p.id)] = p._teOrder
      })
      function stampTeOrderOnList(list) {
        ;(list || []).forEach(function (p) {
          if (!p || p.id == null || p._teOrder != null) return
          var ord = teOrderById[String(p.id)]
          if (ord != null) p._teOrder = ord
        })
        return list
      }
      stampTeOrderOnList(collectionPrimary)
      stampTeOrderOnList(collectionSecondary)

      var primary = sortProducts(mergeProducts(collectionPrimary, {}), collectionSort)
      var secondary = sortProducts(mergeProducts(collectionSecondary, {}), collectionSort)

      // Two-group layout needs Group A/B collections. Product-block-only
      // categories (common Ink & Clay setup) have empty collections — fall
      // back to one TE-ordered grid so blocks aren’t dropped.
      if (layout === 'dual_groups' && !primary.length && !secondary.length && blockProducts.length) {
        layout = 'grid'
        category.layout = 'grid'
      }

      var allRaw
      if (layout === 'dual_groups') {
        allRaw = primary.concat(secondary.filter(function (p) {
          return !primary.some(function (x) { return String(x.id) === String(p.id) })
        }))
        if (useBlocks && blockProducts.length) {
          allRaw = sortProducts(mergeProducts(blockProducts.concat(allRaw), {}), collectionSort)
        }
      } else if (productSource === 'manual') {
        allRaw = sortProducts(mergeProducts(blockProducts, {}), collectionSort)
      } else if (productSource === 'collection') {
        allRaw = sortProducts(mergeProducts(collectionPrimary, {}), collectionSort)
      } else {
        allRaw = sortProducts(mergeProducts(blockProducts.concat(collectionPrimary), {}), collectionSort)
      }

      function mapList(products, groupTag) {
        var cards = []
        products.forEach(function (p) {
          var ov = overrideMeta[String(p.id)]
          var el = productEligible(p, config, state, category, ov || null)
          // TE swatch siblings stay in the flat list (as disabled swatches) so a
          // temporary unavailable / weight-hide on Colour 2 doesn’t collapse
          // the family back to a solo Bright Gold card with no swatches.
          // collapseSwatchFamilies drops the whole family when every sibling
          // would hide (e.g. style max 10 kg vs a 20 kg pet).
          var teSwatchSibling = !!(p && p.id && teSwatchProductIds[String(p.id)])
          if (el.hide && !teSwatchSibling) return
          var wouldHide = !!el.hide
          var elForCard =
            teSwatchSibling && el.hide
              ? {
                  ok: false,
                  note: el.note || '',
                  hide: false,
                  weightNote: el.weightNote || el.note || '',
                  weightAutoSelected: false,
                  resolvedVariant: el.resolvedVariant,
                }
              : el
          var card = enrichCard(
            p,
            ov && ov.settings && ov.settings.label_override,
            elForCard,
            category,
            false,
            ov || null,
            state
          )
          card.group = groupTag
          card._eligibilityHide = wouldHide
          applyAddonFlags(card, p, ov || null)
          card._swatchMeta = resolveCardSwatchMeta(card, ov || null)
          card.selectionLabel = card.title
          cards.push(card)
        })
        return cards
      }

      var includedCard = null
      if (category.includedProduct && category.includedProduct.id) {
        var incEl = productEligible(category.includedProduct, config, state, category, null)
        var includedBlockSettings = {}
        if (category.includedCardImage) includedBlockSettings.card_image = category.includedCardImage
        if (category.includedCardDescription) {
          includedBlockSettings.card_description = category.includedCardDescription
        }
        // Included scatter bag stays even if weight filters would hide paid urns
        includedCard = enrichCard(
          category.includedProduct,
          null,
          { ok: true, note: '' },
          category,
          true,
          Object.keys(includedBlockSettings).length ? { settings: includedBlockSettings } : null,
          state
        )
        includedCard.selectable = true
        includedCard.capacityRole = includedCard.capacityRole || CAPACITY_ROLE_SCATTER
        if (!incEl.ok && incEl.note) includedCard.eligibilityNote = ''
      }

      var includedId = includedCard && includedCard.productId != null
        ? String(includedCard.productId)
        : ''
      function withoutIncluded(list) {
        if (!includedId) return list
        return (list || []).filter(function (c) {
          return String(c.productId) !== includedId
        })
      }

      var groupAFlat = withoutIncluded(
        layout === 'dual_groups' ? mapList(primary, 'a') : mapList(allRaw, null)
      )
      var groupBFlat = withoutIncluded(
        layout === 'dual_groups' ? mapList(secondary, 'b') : []
      )
      var allCardsFlat = layout === 'dual_groups' ? groupAFlat.concat(groupBFlat) : groupAFlat

      var cardByProductId = {}
      allCardsFlat.forEach(function (c) {
        if (c && c.productId != null) cardByProductId[String(c.productId)] = c
      })
      if (includedCard && includedCard.productId != null) {
        cardByProductId[String(includedCard.productId)] = includedCard
      }

      var entryForSwatches = getEntry(state, key, mode)
      var groupA = collapseSwatchFamilies(groupAFlat, entryForSwatches)
      var groupB = collapseSwatchFamilies(groupBFlat, entryForSwatches)
      var allCards = layout === 'dual_groups' ? groupA.concat(groupB) : groupA

      // Keep enabled categories visible even when empty so Theme Editor “Enabled”
      // always surfaces a step (e.g. jewellery with time-sensitive filter hiding all).
      // Also tip when only the included pouch remains — families otherwise see a
      // lone full-bleed pouch and think the urn grid is broken.
      if (!allCards.length) {
        var rawCount = (allRaw && allRaw.length) || 0
        if (!includedCard) {
          if (rawCount && category.timeSensitiveOnly) {
            category.emptyReason =
              'No time-sensitive products matched. Turn off “Only time-sensitive products”, or mark products with booking time-sensitive metafields.'
            category.emptyReasonMerchant = true
          } else if (productSource === 'manual' && !blockProducts.length) {
            category.emptyReason =
              'Add “Memorials — Product” blocks for this Category, or change “Products from” to Collection.'
            category.emptyReasonMerchant = true
          } else if (!collectionPrimary.length && !collectionSecondary.length && !blockProducts.length) {
            category.emptyReason =
              'Choose a Product collection on this Category, or add Product blocks with the same Category.'
            category.emptyReasonMerchant = true
          } else {
            category.emptyReason =
              'No products are available for this booking (weight, pet type, flow, or availability filters).'
          }
        } else if (productSource === 'manual' && !blockProducts.length) {
          category.emptyReason =
            'Add “Memorials — Product” blocks for this Category (same Category as this screen), or set Products from to Collection.'
          category.emptyReasonMerchant = true
        } else if (rawCount || blockProducts.length) {
          category.emptyReason =
            'No special urns are available for this booking (weight, pet type, flow, or availability). You can continue with the included pouch.'
        }
      }

      var limM = category.featuredLimitMobile || memorials.featuredLimitMobile
      var limD = category.featuredLimitDesktop || memorials.featuredLimitDesktop

      function cardIsPinned(c) {
        if (!c) return false
        if (c.itemBlockId) return true
        if (truthy(c.product && c.product.booking && c.product.booking.featured)) return true
        if (c.swatchMembers && c.swatchMembers.length) {
          return c.swatchMembers.some(function (m) {
            return !!(
              m &&
              (m.itemBlockId ||
                truthy(m.product && m.product.booking && m.product.booking.featured))
            )
          })
        }
        return false
      }

      function featuredSlice(cards, limit) {
        if (!categoryShowsFeaturedCap(category)) {
          // Manual mode (except urns): show every Product block.
          return cards.slice()
        }
        var pinned = cards.filter(cardIsPinned)
        var rest = cards.filter(function (c) { return !cardIsPinned(c) })
        var ordered = pinned.concat(rest)
        // Never slice to 0 — TE featured_limit 0 must fall back (callers use ||)
        // but guard here so a lone included pouch is not the entire grid.
        var cap = Math.max(1, Math.floor(Number(limit)) || 1)
        return ordered.slice(0, cap)
      }

      category.includedCard = includedCard
      category.allCardsFlat = allCardsFlat
      category.cardByProductId = cardByProductId
      category.allCards = allCards
      category.groupACards = groupA
      category.groupBCards = groupB
      category.featuredMobile = featuredSlice(allCards, limM || memorials.featuredLimitMobile)
      category.featuredDesktop = featuredSlice(allCards, limD || memorials.featuredLimitDesktop)
      category.hasMoreThanFeatured =
        allCards.length > category.featuredMobile.length ||
        allCards.length > category.featuredDesktop.length
      category.hasCollectionRank = allCards.some(function (c) {
        return cardCollectionIndex(c) != null
      })

      categories.push(category)
    })

    var built = {
      settings: memorials,
      categories: categories,
      byKey: categories.reduce(function (acc, c) {
        acc[c.key] = c
        return acc
      }, {}),
    }
    lastBuiltCatalog = built
    return built
  }

  function emptyEntry(mode) {
    return {
      mode: mode || 'optional_single',
      items: [],
      addons: {},
      itemAddons: {},
      skippedOptional: false,
      _focusedAddonProductId: null,
      /** `divided` | `single_urn` | null — only when 2+ vessels are selected. */
      ashesPlacementMode: null,
      /** Legacy mirror of mode: `yes` (divided) | `no` (single_urn) | null. */
      ashSplit: null,
      /** Product id of the vessel that receives ashes when mode is `single_urn`. */
      ashDestinationProductId: null,
      /** Selected urn product ids that receive ashes when mode is `divided`. */
      ashesReceivingProductIds: null,
      /** Sorted product-id fingerprint the placement answer applies to. */
      ashesPlacementUrnIds: null,
      /** UI-only: `mode` | `destination` | null — set on Continue validation. */
      _ashesPlacementError: null,
      /** UI-only: ashes-placement modal open on the urn step. */
      _ashesPlacementModalOpen: false,
      /**
       * UI-only: pending second urn when exactly one is selected — family chooses
       * replace vs add. Shape: `{ productId, label, card? }` or null.
       */
      _urnChangeOrAddPending: null,
      /** Customer urn-grid sort: `suggested` | `price_asc` | `price_desc`. */
      catalogSort: 'suggested',
      /** Preview / active colour productId per swatch family key (e.g. classic-urn). */
      swatchActiveByFamily: {},
      /** Preview / active variantId per productId for inline pack-size options. */
      variantActiveByProduct: {},
      ashesKeepsakeProductIds: null,
      ashesRemainingKind: null,
      ashesRemainingProductId: null,
      ashesRemainingLabel: null,
    }
  }

  function urnSelectionKey(entry) {
    return (entry && entry.items ? entry.items : [])
      .map(function (it) {
        return it && it.productId != null ? String(it.productId) : ''
      })
      .filter(Boolean)
      .sort()
      .join('|')
  }

  /** Total vessel units — order qty, with included pouches counting as 1. */
  function urnVesselCount(entry) {
    var items = (entry && entry.items) || []
    var n = 0
    var i
    for (i = 0; i < items.length; i++) {
      var it = items[i]
      if (!it) continue
      if (it.included) {
        n += 1
        continue
      }
      var q = Math.floor(Number(it.quantity) || 1)
      n += q > 0 ? q : 1
    }
    return n
  }

  function urnDistinctCount(entry) {
    var items = (entry && entry.items) || []
    var n = 0
    var i
    for (i = 0; i < items.length; i++) {
      if (items[i] && items[i].productId != null) n += 1
    }
    return n
  }

  function onlyUrnProductId(entry) {
    var items = placementUrnItems(entry)
    if (items.length !== 1 || !items[0] || items[0].productId == null) return null
    return String(items[0].productId)
  }

  function clearAshesPlacement(entry) {
    if (!entry) return
    entry.ashesPlacementMode = null
    entry.ashSplit = null
    entry.ashDestinationProductId = null
    entry.ashesReceivingProductIds = null
    entry.ashesPlacementUrnIds = null
    entry._ashesPlacementError = null
    entry._ashesPlacementModalOpen = false
  }

  function clearKeepsakeAllocation(entry) {
    if (!entry) return
    entry.ashesKeepsakeProductIds = null
    entry.ashesRemainingKind = null
    entry.ashesRemainingProductId = null
    entry.ashesRemainingLabel = null
  }

  function pouchFallbackLabel(catalog) {
    var pouch = catalog && catalog.byKey && catalog.byKey.urn && catalog.byKey.urn.includedCard
    if (!pouch) return 'Satin Pouch'
    var t = pouch.title || getMemorialDisplayTitle(pouch) || ''
    if (/satin/i.test(t)) return 'Satin Pouch'
    return t || 'Satin Pouch'
  }

  function pouchFallbackId(catalog) {
    var pouch = catalog && catalog.byKey && catalog.byKey.urn && catalog.byKey.urn.includedCard
    return pouch && pouch.productId != null ? String(pouch.productId) : null
  }

  function catalogForPouch(catalog) {
    if (catalog && catalog.byKey && catalog.byKey.urn) return catalog
    return lastBuiltCatalog
  }

  function includedPouchCard(catalog) {
    var cat = catalogForPouch(catalog)
    return (cat && cat.byKey && cat.byKey.urn && cat.byKey.urn.includedCard) || null
  }

  function portionKeepsakeKey(entry) {
    return keepsakeItems(entry)
      .map(function (it) {
        return it && it.productId != null ? String(it.productId) : ''
      })
      .filter(Boolean)
      .sort()
      .join('|')
  }

  function selectionNeedsAutoPouch(entry) {
    return (
      !needsAshesPlacementChoice(entry) &&
      keepsakeItems(entry).length > 0 &&
      fullCapacityItems(entry).length === 0 &&
      ashHoldingItems(entry).length < 2
    )
  }

  /**
   * When a keepsake is over capacity and no full urn covers the remainder,
   * select the included satin pouch so the split is visible. Drop that auto
   * selection if they add a full-capacity urn for the balance. Do not auto
   * select on an empty urn step (families still click a vessel).
   */
  function syncAutoIncludedPouch(entry, catalog) {
    if (!entry) return
    var pouchCard = includedPouchCard(catalog)
    if (!pouchCard || pouchCard.productId == null) return
    var pouchId = String(pouchCard.productId)
    var key = portionKeepsakeKey(entry)
    if (entry._autoPouchKeepsakeKey != null && String(entry._autoPouchKeepsakeKey) !== key) {
      entry._autoIncludedPouchDismissed = false
    }
    entry._autoPouchKeepsakeKey = key
    var needs = selectionNeedsAutoPouch(entry)
    var selected = isSelected(entry, pouchId)
    if (needs) {
      if (entry._autoIncludedPouchDismissed) return
      if (!selected) {
        entry.items = (entry.items || []).concat([selectionFromCard(pouchCard)])
        entry._autoIncludedPouch = true
      }
      return
    }
    var remainderCovered =
      selected &&
      (needsAshesPlacementChoice(entry) ||
        (keepsakeItems(entry).length > 0 &&
          (fullCapacityItems(entry).length > 0 || ashHoldingItems(entry).length >= 2)))
    if ((entry._autoIncludedPouch && selected) || remainderCovered) {
      entry.items = (entry.items || []).filter(function (it) {
        return !it || String(it.productId) !== pouchId
      })
    }
    entry._autoIncludedPouch = false
    entry._autoIncludedPouchDismissed = false
  }

  function markAutoPouchDismissed(entry, productId, catalog) {
    if (!entry || productId == null || productId === '') return
    var pouchId = pouchFallbackId(catalogForPouch(catalog))
    if (!pouchId || String(productId) !== pouchId) return
    if (entry._autoIncludedPouch) {
      entry._autoIncludedPouchDismissed = true
      entry._autoIncludedPouch = false
    }
  }

  function syncKeepsakeAllocation(entry, catalog) {
    if (!entry) return
    var keepsakes = keepsakeItems(entry)
    if (!keepsakes.length) {
      clearKeepsakeAllocation(entry)
    } else {
      entry.ashesKeepsakeProductIds = keepsakes
        .map(function (it) {
          return it && it.productId != null ? String(it.productId) : ''
        })
        .filter(Boolean)
    }
    if (needsAshesPlacementChoice(entry)) {
      var placeMode = normalizeAshesPlacementMode(entry)
      if (!placeMode) {
        entry.ashesRemainingKind = 'pending'
        entry.ashesRemainingProductId = null
        entry.ashesRemainingLabel = null
        return
      }
      if (placeMode === 'divided') {
        entry.ashesRemainingKind = 'divided'
        entry.ashesRemainingProductId = null
        entry.ashesRemainingLabel = null
        return
      }
      if (placeMode === 'single_urn') {
        var placeDestId = entry.ashDestinationProductId
        var placeDest = placementUrnItems(entry).find(function (it) {
          return it && String(it.productId) === String(placeDestId)
        })
        entry.ashesRemainingKind = placeDest ? 'urn' : 'pending'
        entry.ashesRemainingProductId = placeDest ? String(placeDest.productId) : null
        entry.ashesRemainingLabel = placeDest ? selectionDisplayLabel(placeDest) : null
        return
      }
    }
    if (!keepsakes.length) return
    var full = fullCapacityItems(entry)
    if (!full.length) {
      if (ashHoldingItems(entry).length >= 2) {
        entry.ashesRemainingKind = 'divided'
        entry.ashesRemainingProductId = null
        entry.ashesRemainingLabel = null
        return
      }
      entry.ashesRemainingKind = 'pouch'
      entry.ashesRemainingProductId = pouchFallbackId(catalog)
      entry.ashesRemainingLabel = pouchFallbackLabel(catalog)
      return
    }
    if (full.length === 1) {
      entry.ashesRemainingKind = 'urn'
      entry.ashesRemainingProductId = String(full[0].productId)
      entry.ashesRemainingLabel = selectionDisplayLabel(full[0])
      return
    }
    var mode = normalizeAshesPlacementMode(entry)
    if (mode === 'divided') {
      entry.ashesRemainingKind = 'divided'
      entry.ashesRemainingProductId = null
      entry.ashesRemainingLabel = null
      return
    }
    if (mode === 'single_urn') {
      var destId = entry.ashDestinationProductId
      var destItem = full.find(function (it) {
        return it && String(it.productId) === String(destId)
      })
      entry.ashesRemainingKind = destItem ? 'urn' : 'pending'
      entry.ashesRemainingProductId = destItem ? String(destItem.productId) : null
      entry.ashesRemainingLabel = destItem ? selectionDisplayLabel(destItem) : null
      return
    }
    entry.ashesRemainingKind = 'pending'
    entry.ashesRemainingProductId = null
    entry.ashesRemainingLabel = null
  }

  /** Prefer `ashesPlacementMode`; map legacy `ashSplit` yes/no when mode unset. */
  function normalizeAshesPlacementMode(entry) {
    if (!entry) return null
    if (entry.ashesPlacementMode === 'divided' || entry.ashesPlacementMode === 'single_urn') {
      return entry.ashesPlacementMode
    }
    if (entry.ashSplit === 'yes') return 'divided'
    if (entry.ashSplit === 'no') return 'single_urn'
    return null
  }

  function mirrorAshSplitFromMode(entry) {
    if (!entry) return
    if (entry.ashesPlacementMode === 'divided') entry.ashSplit = 'yes'
    else if (entry.ashesPlacementMode === 'single_urn') entry.ashSplit = 'no'
    else entry.ashSplit = null
  }

  function receivingIdsForEntry(entry) {
    return placementUrnItems(entry)
      .map(function (it) {
        return it && it.productId != null ? String(it.productId) : ''
      })
      .filter(Boolean)
  }

  /**
   * Keep multi-urn ashes placement in sync with the selected urn-role set
   * (including over-capacity Classic). Configured keepsakes never enter the
   * divide question. Never preselect a mode — family must choose explicitly
   * when 2+ urns.
   */
  function syncAshesPlacement(entry, catalog, stateOrWeight) {
    if (!entry) return null
    bindPlacementWeight(entry, stateOrWeight)
    syncAutoIncludedPouch(entry, catalog)
    syncKeepsakeAllocation(entry, catalog)
    if (!needsAshesPlacementChoice(entry)) {
      clearAshesPlacement(entry)
      syncKeepsakeAllocation(entry, catalog)
      return null
    }
    if (entry.ashesPlacementMode !== 'divided' && entry.ashesPlacementMode !== 'single_urn') {
      var legacy = normalizeAshesPlacementMode(entry)
      entry.ashesPlacementMode = legacy
      if (legacy && entry.ashesPlacementUrnIds == null) {
        entry.ashesPlacementUrnIds = placementUrnSelectionKey(entry)
      }
    }
    var key = placementUrnSelectionKey(entry)
    if (
      entry.ashesPlacementMode &&
      entry.ashesPlacementUrnIds != null &&
      String(entry.ashesPlacementUrnIds) !== key
    ) {
      clearAshesPlacement(entry)
      syncKeepsakeAllocation(entry, catalog)
      return null
    }
    // Bind fingerprint on first sync after restore when mode exists but set was unset.
    if (entry.ashesPlacementMode && entry.ashesPlacementUrnIds == null) {
      entry.ashesPlacementUrnIds = key
    }
    mirrorAshSplitFromMode(entry)
    if (entry.ashesPlacementMode === 'divided') {
      entry.ashesReceivingProductIds = receivingIdsForEntry(entry)
      if (entry.ashDestinationProductId != null && !isSelected(entry, entry.ashDestinationProductId)) {
        entry.ashDestinationProductId = null
      }
    } else if (entry.ashesPlacementMode === 'single_urn') {
      entry.ashesReceivingProductIds = null
      syncAshDestination(entry)
    } else {
      entry.ashesReceivingProductIds = null
    }
    syncKeepsakeAllocation(entry, catalog)
    return entry.ashesPlacementMode
  }

  /** @deprecated Use syncAshesPlacement — kept as alias for callers. */
  function syncAshSplitChoice(entry) {
    var mode = syncAshesPlacement(entry)
    if (mode === 'divided') return 'yes'
    if (mode === 'single_urn') return 'no'
    return null
  }

  /** Legacy ops / cart display value: `Yes` | `No` | null. */
  function ashSplitDisplayValue(entry) {
    var mode = syncAshesPlacement(entry)
    if (mode === 'divided') return 'Yes'
    if (mode === 'single_urn') return 'No'
    return null
  }

  /**
   * Customer-facing ashes placement summary (rail / review / cart value).
   * Keepsake arrangements join with “; ”. Full-capacity-only stays
   * “Ashes divided” / “Ashes in {urn} only”.
   */
  function ashesPlacementDisplayValue(entry, catalog, stateOrWeight) {
    syncAshesPlacement(entry, catalog, stateOrWeight)
    var arranged = ashesArrangementPlainLines(entry, catalog, stateOrWeight)
    if (arranged && arranged.length) return arranged.join('; ')
    var mode = normalizeAshesPlacementMode(entry)
    if (!mode) return null
    if (mode === 'divided') return 'Ashes divided'
    var dest = ashDestinationDisplayValue(entry)
    return dest ? 'Ashes in ' + dest + ' only' : null
  }

  function remainingAshesTitle(entry, catalog) {
    if (!entry) return ''
    if (entry.ashesRemainingKind === 'pouch') {
      return entry.ashesRemainingLabel || pouchFallbackLabel(catalog)
    }
    if (entry.ashesRemainingKind === 'urn') {
      return entry.ashesRemainingLabel || ''
    }
    if (entry.ashesRemainingKind === 'divided') return ''
    return ''
  }

  function ashesArrangementPlainLines(entry, catalog, stateOrWeight) {
    if (!entry) return []
    bindPlacementWeight(entry, stateOrWeight)
    syncKeepsakeAllocation(entry, catalog)
    if (entry.ashesRemainingKind === 'pending') return []
    if (entry.ashesRemainingKind === 'divided' && needsAshesPlacementChoice(entry)) {
      var placeNames = uniquePlacementUrnStyleTitles(entry)
      if (placeNames.length) {
        return ['Ashes divided between ' + joinEnglishList(placeNames)]
      }
    }
    var keepsakes = keepsakeItems(entry)
    if (!keepsakes.length) return []
    if (entry.ashesRemainingKind === 'divided' && !fullCapacityItems(entry).length) {
      var splitNames = uniqueKeepsakeStyleTitles(entry)
      if (splitNames.length) {
        return ['Ashes divided between ' + joinEnglishList(splitNames)]
      }
    }
    var qty = keepsakeUnitCount(entry)
    var style = selectionDisplayLabel(keepsakes[0])
    var short = placementShortTitle(style)
    var lines = []
    lines.push(qty > 1 ? 'Small portion in each ' + short : 'Small portion in ' + short)
    if (entry.ashesRemainingKind === 'pouch') {
      lines.push('Remaining ashes in ' + (remainingAshesTitle(entry, catalog) || 'Satin Pouch'))
    } else if (entry.ashesRemainingKind === 'urn') {
      var destTitle = remainingAshesTitle(entry, catalog)
      if (destTitle) lines.push('Remaining ashes in ' + destTitle)
    } else if (entry.ashesRemainingKind === 'divided') {
      var names = fullCapacityItems(entry).map(selectionDisplayLabel).filter(Boolean)
      if (names.length) lines.push('Remaining ashes divided between ' + names.join(' and '))
    }
    return lines
  }

  function ashesArrangementSummaryLines(entry, catalog, stateOrWeight) {
    return ashesArrangementPlainLines(entry, catalog, stateOrWeight)
  }

  function ashesArrangementReviewLines(entry, catalog, stateOrWeight) {
    if (!entry) return []
    bindPlacementWeight(entry, stateOrWeight)
    syncKeepsakeAllocation(entry, catalog)
    if (entry.ashesRemainingKind === 'pending') return []
    if (entry.ashesRemainingKind === 'divided' && needsAshesPlacementChoice(entry)) {
      var placeNames = uniquePlacementUrnStyleTitles(entry)
      if (placeNames.length) {
        return ['Ashes divided between ' + joinEnglishList(placeNames)]
      }
    }
    var keepsakes = keepsakeItems(entry)
    if (!keepsakes.length) return []
    if (entry.ashesRemainingKind === 'divided' && !fullCapacityItems(entry).length) {
      var splitNames = uniqueKeepsakeStyleTitles(entry)
      if (splitNames.length) {
        return ['Ashes divided between ' + joinEnglishList(splitNames)]
      }
    }
    var qty = keepsakeUnitCount(entry)
    var style = selectionDisplayLabel(keepsakes[0])
    var lines = [style + ' \u2014 small portion' + (qty > 1 ? ' each' : '')]
    if (entry.ashesRemainingKind === 'pouch') {
      lines.push('Remaining ashes \u2014 ' + (remainingAshesTitle(entry, catalog) || 'Satin Pouch'))
    } else if (entry.ashesRemainingKind === 'urn') {
      var destTitle = remainingAshesTitle(entry, catalog)
      if (destTitle) lines.push('Remaining ashes \u2014 ' + destTitle)
    } else if (entry.ashesRemainingKind === 'divided') {
      var names = fullCapacityItems(entry).map(selectionDisplayLabel).filter(Boolean)
      if (names.length) lines.push('Remaining ashes \u2014 divided between ' + names.join(' and '))
    }
    return lines
  }

  /** Card line only when this keepsake is over its own capacity (e.g. pet > 1 kg). */
  function cardShowsCapacityLabel(card, state) {
    if (!card || card.capacityRole !== CAPACITY_ROLE_KEEPSAKE) return false
    if (!String(card.capacityLabel || '').trim()) return false
    var weight = petWeightKg(state)
    if (!(weight > 0)) return false
    var cap = keepsakeCapacityKg(card)
    if (!(cap > 0)) return true
    return weight > cap
  }

  /** Notice only when the pet is over a selected keepsake’s capacity (e.g. > 1 kg). */
  function petExceedsKeepsakeCapacity(entry, state) {
    var weight = petWeightKg(state)
    if (!(weight > 0)) return false
    bindPlacementWeight(entry, state)
    return keepsakeItems(entry).length > 0
  }

  function keepsakeNoticeCopy(entry, catalog, settings, state) {
    bindPlacementWeight(entry, state)
    if (!entry || !keepsakeUnitCount(entry)) return null
    if (!petExceedsKeepsakeCapacity(entry, state)) return null
    syncAshesPlacement(entry, catalog, state)
    if (needsAshesPlacementChoice(entry) && !normalizeAshesPlacementMode(entry)) return null
    var many = keepsakeUnitCount(entry) > 1
    var withUrn = fullCapacityVesselCount(entry) > 0
    var splitOverflow = !withUrn && ashHoldingItems(entry).length >= 2
    var kn = (settings && settings.keepsakeNotice) || {}
    var names = uniqueKeepsakeStyleTitles(entry)
    var heading = rewriteLegacyKeepsakeNoticeHeading(
      many ? kn.headingMany : kn.headingOne,
      many
    )
    var body
    if (withUrn) {
      body = many
        ? rewriteLegacyKeepsakeNoticeBody(kn.bodyManyUrn, KEEPSAKE_NOTICE_DEFAULTS.bodyManyUrn)
        : rewriteLegacyKeepsakeNoticeBody(kn.bodyOneUrn, KEEPSAKE_NOTICE_DEFAULTS.bodyOneUrn)
    } else if (splitOverflow) {
      body = rewriteLegacyKeepsakeNoticeBody(
        kn.bodySplit,
        KEEPSAKE_NOTICE_DEFAULTS.bodySplit
      )
    } else {
      body = many
        ? rewriteLegacyKeepsakeNoticeBody(kn.bodyManyPouch, KEEPSAKE_NOTICE_DEFAULTS.bodyManyPouch)
        : rewriteLegacyKeepsakeNoticeBody(kn.bodyOnePouch, KEEPSAKE_NOTICE_DEFAULTS.bodyOnePouch)
    }
    return {
      heading: interpolateKeepsakeNotice(heading, state, names),
      body: interpolateKeepsakeNotice(body, state, names),
    }
  }

  /**
   * When mode is single_urn, keep a destination that is still among selected urns.
   */
  function syncAshDestination(entry) {
    if (!entry) return null
    if (!needsAshesPlacementChoice(entry)) {
      entry.ashDestinationProductId = null
      return null
    }
    var dest = entry.ashDestinationProductId
    var destIsUrn = placementUrnItems(entry).some(function (it) {
      return it && String(it.productId) === String(dest)
    })
    if (dest == null || !destIsUrn) {
      var only = onlyUrnProductId(entry)
      if (only && normalizeAshesPlacementMode(entry) === 'single_urn') {
        entry.ashDestinationProductId = only
        return only
      }
      entry.ashDestinationProductId = null
      return null
    }
    if (normalizeAshesPlacementMode(entry) !== 'single_urn') return null
    return String(dest)
  }

  /** Label of the ash-destination vessel, or null when not applicable / unset. */
  function ashDestinationDisplayValue(entry) {
    var pid = syncAshDestination(entry)
    if (!pid) return null
    var item = (entry.items || []).find(function (it) {
      return it && String(it.productId) === String(pid)
    })
    return selectionDisplayLabel(item) || null
  }

  function setAshesPlacementMode(entry, mode) {
    if (!entry || !needsAshesPlacementChoice(entry)) return null
    if (mode !== 'divided' && mode !== 'single_urn') return null
    entry.ashesPlacementMode = mode
    entry.ashesPlacementUrnIds = placementUrnSelectionKey(entry)
    entry._ashesPlacementError = null
    mirrorAshSplitFromMode(entry)
    if (mode === 'divided') {
      entry.ashesReceivingProductIds = receivingIdsForEntry(entry)
    } else {
      entry.ashesReceivingProductIds = null
      syncAshDestination(entry)
    }
    syncKeepsakeAllocation(entry)
    return entry.ashesPlacementMode
  }

  function setAshDestination(entry, productId) {
    if (!entry) return null
    if (!needsAshesPlacementChoice(entry) || normalizeAshesPlacementMode(entry) !== 'single_urn') {
      return null
    }
    var destIsUrn = placementUrnItems(entry).some(function (it) {
      return it && String(it.productId) === String(productId)
    })
    if (productId == null || !destIsUrn) {
      entry.ashDestinationProductId = null
      return null
    }
    entry.ashDestinationProductId = String(productId)
    entry._ashesPlacementError = null
    entry.ashesPlacementUrnIds = placementUrnSelectionKey(entry)
    syncKeepsakeAllocation(entry)
    return entry.ashDestinationProductId
  }

  /** True when multi-urn placement (and destination, if needed) is complete. */
  function ashesPlacementReady(entry) {
    if (!entry || !needsAshesPlacementChoice(entry)) return true
    var mode = syncAshesPlacement(entry)
    if (mode !== 'divided' && mode !== 'single_urn') return false
    if (mode === 'single_urn') return !!syncAshDestination(entry)
    return true
  }

  /** Per-product personalisation buckets (photo / nameplate / engraving). */
  function ensureItemAddons(entry) {
    if (!entry) return {}
    if (!entry.itemAddons || typeof entry.itemAddons !== 'object') entry.itemAddons = {}
    return entry.itemAddons
  }

  function pruneItemAddons(entry) {
    if (!entry) return
    var buckets = ensureItemAddons(entry)
    var keep = {}
    ;(entry.items || []).forEach(function (it) {
      if (!it || it.productId == null) return
      keep[String(it.productId)] = true
    })
    Object.keys(buckets).forEach(function (pid) {
      if (!keep[pid]) delete buckets[pid]
    })
    if (entry._focusedAddonProductId && !keep[String(entry._focusedAddonProductId)]) {
      entry.addons = {}
      entry._focusedAddonProductId = null
    }
  }

  /**
   * Point `entry.addons` at the bucket for `productId` so existing photo/nameplate/
   * engraving step code can keep reading `entry.addons.photo` etc.
   *
   * Callers that mutate `entry.addons.*` from a click/async handler must re-focus
   * first: summary/ledger walks can leave `entry.addons` on another multi-select
   * vessel, so writes would land in the wrong bucket and the active step would
   * re-render unchanged (cards look dead).
   */
  function focusAddonProduct(entry, productId) {
    if (!entry) return null
    var buckets = ensureItemAddons(entry)
    migrateLegacyFlatAddons(entry)
    if (productId == null || productId === '') {
      entry.addons = entry.addons && typeof entry.addons === 'object' ? entry.addons : {}
      return entry.addons
    }
    var key = String(productId)
    if (!buckets[key] || typeof buckets[key] !== 'object') buckets[key] = {}
    entry.addons = buckets[key]
    entry._focusedAddonProductId = key
    return entry.addons
  }

  /** Older drafts stored one photo/nameplate/engraving on the category entry. */
  function migrateLegacyFlatAddons(entry) {
    if (!entry) return
    var buckets = ensureItemAddons(entry)
    if (Object.keys(buckets).length) return
    var flat = entry.addons
    if (!flat || typeof flat !== 'object') return
    var hasFlat = !!(flat.photo || flat.nameplate || flat.engraving)
    if (!hasFlat) return
    var target = null
    ;(entry.items || []).forEach(function (it) {
      if (!target && it && it.productId != null && !it.included) target = String(it.productId)
    })
    if (!target && entry.items && entry.items[0] && entry.items[0].productId != null) {
      target = String(entry.items[0].productId)
    }
    if (!target) return
    buckets[target] = {
      photo: flat.photo,
      nameplate: flat.nameplate,
      engraving: flat.engraving,
    }
    entry.addons = buckets[target]
    entry._focusedAddonProductId = target
  }

  function getEntry(state, groupKey, mode) {
    if (!state.memorials[groupKey]) {
      state.memorials[groupKey] = emptyEntry(mode)
    }
    var e = state.memorials[groupKey]
    if (!Array.isArray(e.items)) {
      // Migrate legacy { itemId, itemLabel, itemVariant }
      var items = []
      if (e.itemId && e.itemId !== '__none__' && e.itemVariant) {
        items.push({
          productId: e.itemVariant.productId || null,
          variantId: e.itemVariant.id,
          included: false,
          quantity: 1,
          properties: {},
          label: e.itemLabel || 'Item',
          image: null,
          price: e.itemVariant.price,
          itemBlockId: e.itemId,
        })
      }
      e.items = items
      e.mode = e.mode || mode || 'optional_single'
      if (!e.addons) e.addons = {}
      delete e.itemId
      delete e.itemLabel
      delete e.itemVariant
    }
    ensureItemAddons(e)
    migrateLegacyFlatAddons(e)
    pruneItemAddons(e)
    if (!e.swatchActiveByFamily || typeof e.swatchActiveByFamily !== 'object') {
      e.swatchActiveByFamily = {}
    }
    if (!e.variantActiveByProduct || typeof e.variantActiveByProduct !== 'object') {
      e.variantActiveByProduct = {}
    }
    return e
  }

  /**
   * Previously auto-selected Theme Editor “Included / default product” (e.g.
   * Satin Tribute Pouch) when the category was empty. Families must now click
   * to select — kept as a no-op so callers / drafts stay safe.
   */
  function applyIncludedDefault(/* cat, entry */) {
    return false
  }

  function ensureCategoryDefaults(catalog, state, config) {
    if (!catalog || !catalog.categories) return
    var flow = config.flow === 'shared' ? 'shared' : 'individual'
    catalog.categories.forEach(function (cat) {
      var entry = getEntry(state, cat.key, cat.selectionMode)
      entry.mode = cat.selectionMode
      // Individual-only categories (e.g. urn) never apply on the shared page.
      if (flow === 'shared' && (cat.flows === 'individual' || cat.key === 'urn')) {
        entry.items = []
        entry.addons = {}
        entry.itemAddons = {}
        entry._focusedAddonProductId = null
        clearAshesPlacement(entry)
        clearKeepsakeAllocation(entry)
        return
      }
      ;(entry.items || []).forEach(function (it) {
        if (!it || it.productId == null) return
        var card = findDisplayCardInCategory(cat, it.productId) || findCardInCategory(cat, it.productId)
        if (!card) return
        it.styleTitle = card.styleTitle || card.title || it.styleTitle
        it.label = card.title || it.label
        it.capacityRole = card.capacityRole || it.capacityRole
        it.capacityKg = card.capacityKg != null ? card.capacityKg : it.capacityKg
        it.weightMaxKg = card.weightMaxKg != null ? card.weightMaxKg : it.weightMaxKg
        it.capacityLabel = card.capacityLabel || it.capacityLabel || ''
        it.shopifyTitle = card.shopifyTitle || it.shopifyTitle || ''
      })
      syncAshesPlacement(entry, catalog, state)
      // Empty urn step stays empty. Over-capacity keepsakes auto-select the
      // included pouch via syncAutoIncludedPouch — not applyIncludedDefault.
    })
    // Drop individual-only memorial state entirely on shared bookings.
    if (flow === 'shared') {
      Object.keys(state.memorials).forEach(function (key) {
        var cat = catalog.byKey[key]
        if (cat && cat.flows === 'individual') {
          state.memorials[key] = emptyEntry(cat.selectionMode)
        }
      })
    }
  }

  var MEMORIAL_ORDER_QTY_MAX = 9

  function categoryAllowsOrderQuantity(cat) {
    if (!cat) return false
    if (cat.allowOrderQuantity === false) return false
    return true
  }

  function normalizeItemQuantity(qty) {
    var n = Math.floor(Number(qty) || 0)
    if (n < 1) return 1
    if (n > MEMORIAL_ORDER_QTY_MAX) return MEMORIAL_ORDER_QTY_MAX
    return n
  }

  function itemQuantity(item) {
    if (!item || item.included) return 1
    return normalizeItemQuantity(item.quantity)
  }

  function selectedItemForCard(entry, card) {
    if (!entry || !card) return null
    var items = entry.items || []
    var i
    var id = card.productId != null ? String(card.productId) : ''
    for (i = 0; i < items.length; i++) {
      if (items[i] && String(items[i].productId) === id) return items[i]
    }
    var members = card.swatchMembers || []
    var m
    for (m = 0; m < members.length; m++) {
      if (!members[m] || members[m].productId == null) continue
      var mid = String(members[m].productId)
      for (i = 0; i < items.length; i++) {
        if (items[i] && String(items[i].productId) === mid) return items[i]
      }
    }
    return null
  }

  function deselectCard(entry, card) {
    if (!entry || !card) return
    var item = selectedItemForCard(entry, card)
    if (!item || item.productId == null) return
    var id = String(item.productId)
    markAutoPouchDismissed(entry, id)
    ensureItemAddons(entry)
    entry.items = (entry.items || []).filter(function (it) {
      return !it || String(it.productId) !== id
    })
    if (entry.itemAddons) delete entry.itemAddons[id]
    if (String(entry._focusedAddonProductId || '') === id) {
      entry.addons = {}
      entry._focusedAddonProductId = null
    }
    pruneItemAddons(entry)
    syncAshesPlacement(entry)
  }

  function setItemQuantity(entry, card, qty, mode) {
    if (!entry || !card || !card.selectable) return
    mode = mode || entry.mode || 'optional_single'
    entry.skippedOptional = false
    clearUrnChangeOrAddPending(entry)
    qty = Math.floor(Number(qty) || 0)
    if (qty <= 0) {
      if (
        isRequiredSelectionMode(mode) &&
        (entry.items || []).length <= 1 &&
        selectedItemForCard(entry, card)
      ) {
        var keep = selectedItemForCard(entry, card)
        if (keep) keep.quantity = 1
        return
      }
      deselectCard(entry, card)
      return
    }
    qty = normalizeItemQuantity(qty)
    var existing = selectedItemForCard(entry, card)
    if (existing) {
      existing.quantity = qty
      syncAshesPlacement(entry)
      return
    }
    ensureItemAddons(entry)
    var next = selectionFromCard(card)
    next.quantity = qty
    if (isMultiSelectMode(mode)) {
      entry.items = (entry.items || []).concat([next])
    } else {
      entry.itemAddons = {}
      entry.addons = {}
      entry._focusedAddonProductId = null
      clearAshesPlacement(entry)
      entry.items = [next]
    }
    pruneItemAddons(entry)
    syncAshesPlacement(entry)
  }

  function selectionFromCard(card, properties) {
    var label =
      (card && card.selectionLabel) ||
      (card && card.title) ||
      (card && card.styleTitle) ||
      (card && card.product && card.product.title) ||
      'Item'
    return {
      productId: card.productId,
      variantId: card.included ? (card.variantId || null) : card.variantId,
      included: !!card.included,
      quantity: 1,
      properties: properties || {},
      label: label,
      styleTitle: (card && (card.styleTitle || card.title)) || label,
      shopifyTitle: (card && card.shopifyTitle) || (card && card.product && card.product.title) || label,
      capacityRole: (card && card.capacityRole) || CAPACITY_ROLE_FULL,
      capacityKg: card && card.capacityKg != null ? card.capacityKg : null,
      weightMaxKg: card && card.weightMaxKg != null ? card.weightMaxKg : null,
      capacityLabel: (card && card.capacityLabel) || '',
      image: card.image,
      price: card.included ? 0 : card.price,
      itemBlockId: card.itemBlockId || null,
      needsPhoto: !!card.needsPhoto,
      needsNameplate: !!card.needsNameplate,
      needsEngraving: !!card.needsEngraving,
      needsNameplateText: !!card.needsNameplateText,
      nameplateIncluded: !!card.nameplateIncluded,
      photoFeeProduct: card.photoFeeProduct || null,
      engravingFeeProduct: card.engravingFeeProduct || null,
      nameplateProductGold: card.nameplateProductGold || null,
      nameplateProductSilver: card.nameplateProductSilver || null,
      nameplateGoldLabel: card.nameplateGoldLabel || '',
      nameplateSilverLabel: card.nameplateSilverLabel || '',
      nameplatePreviewImageGold: card.nameplatePreviewImageGold || null,
      nameplatePreviewImageSilver: card.nameplatePreviewImageSilver || null,
      personaliseImage: card.personaliseImage || null,
      personaliseImageGold: card.personaliseImageGold || null,
      personaliseImageSilver: card.personaliseImageSilver || null,
      cardImageGold: card.cardImageGold || null,
      cardImageSilver: card.cardImageSilver || null,
      reviewBlankCardImage: card.reviewBlankCardImage || null,
      photoPrintSize: normalizePhotoPrintSize(card.photoPrintSize),
      photoOrientation: normalizePhotoOrientation(card.photoOrientation),
      photoOrientationDefault: normalizePhotoOrientationDefault(card.photoOrientationDefault),
      photoMockupFrame: card.photoMockupFrame || null,
      photoCardTitle: card.photoCardTitle || '',
      photoCardSubtitle: card.photoCardSubtitle || '',
      engravingCardTitle: card.engravingCardTitle || '',
      engravingCardSubtitle: card.engravingCardSubtitle || '',
      variantMeta: card.variantMeta || '',
    }
  }

  function isSelected(entry, productId) {
    return (entry.items || []).some(function (it) { return String(it.productId) === String(productId) })
  }

  /**
   * 1-based selection-order index for a product in entry.items, or 0 if not selected.
   * Used for multi-urn numbered badges (not ash-destination priority).
   */
  function selectionIndex(entry, productId) {
    if (!entry || productId == null || productId === '') return 0
    var items = entry.items || []
    for (var i = 0; i < items.length; i++) {
      if (items[i] && String(items[i].productId) === String(productId)) return i + 1
    }
    return 0
  }

  /**
   * Short per-urn ashes label for summary/review once placement is answered.
   * divided → “Ashes” on every selected vessel
   * single_urn → “Ashes” on destination, “Empty” on the others
   */
  function urnAshesTagForProduct(entry, productId) {
    if (!entry || productId == null || productId === '') return null
    var weight = entryPlacementWeightKg(entry)
    var item = (entry.items || []).find(function (it) {
      return it && String(it.productId) === String(productId)
    })
    if (item && !isUrnRoleItem(item)) return null
    if (!needsAshesPlacementChoice(entry)) return null
    var mode = syncAshesPlacement(entry)
    if (!mode) return null
    var pid = String(productId)
    if (mode === 'divided') return 'Ashes'
    if (mode === 'single_urn') {
      var dest = syncAshDestination(entry)
      if (!dest) return null
      return String(dest) === pid ? 'Ashes' : 'Empty'
    }
    return null
  }

  /**
   * True when clicking a different multi-select product while ≥1 is already
   * chosen — ambiguous “change mind” vs “add another” (urn N→N+1). Not for
   * deselect or the first selection from empty.
   * Full-role urns still prompt when over capacity (they show as a small
   * portion in the rail). Configured keepsakes / scatter never prompt.
   */
  function shouldPromptChangeOrAdd(entry, productId, mode, card) {
    if (!isMultiSelectMode(mode)) return false
    if (!entry || productId == null || productId === '') return false
    if (!(entry.items || []).length) return false
    if (isSelected(entry, productId)) return false
    // Configured keepsake role never opens change-or-add (even when under
    // capacity it can hold full ashes — adding a tealight is not “another urn”).
    if (card && itemCapacityRole(card) === CAPACITY_ROLE_KEEPSAKE) return false
    if (card && itemCapacityRole(card) === CAPACITY_ROLE_SCATTER) return false
    if (urnRoleVesselCount(entry) === 0 && fullCapacityVesselCount(entry) === 0) return false
    return true
  }

  function clearUrnChangeOrAddPending(entry) {
    if (entry) entry._urnChangeOrAddPending = null
  }

  /** Keep only personalisation the destination product still supports. */
  function migrateCompatibleItemAddons(fromBucket, toCard) {
    var out = {}
    if (!fromBucket || !toCard) return out
    if (fromBucket.photo && toCard.needsPhoto) out.photo = fromBucket.photo
    if (fromBucket.nameplate && toCard.needsNameplate) out.nameplate = fromBucket.nameplate
    if (fromBucket.engraving && toCard.needsEngraving) out.engraving = fromBucket.engraving
    return out
  }

  /**
   * Replace one selected product with another (same slot). Migrates compatible
   * photo/nameplate/engraving; drops the rest. Clears multi-urn ashes placement
   * when the set falls back to a single vessel.
   */
  function replaceSelect(entry, fromProductId, toCard) {
    if (!entry || !toCard || !toCard.selectable) return false
    entry.skippedOptional = false
    clearUrnChangeOrAddPending(entry)
    ensureItemAddons(entry)
    var fromId = String(fromProductId)
    var toId = String(toCard.productId)
    var items = entry.items || []
    var fromIdx = -1
    for (var i = 0; i < items.length; i++) {
      if (items[i] && String(items[i].productId) === fromId) {
        fromIdx = i
        break
      }
    }
    if (fromIdx < 0) {
      if (!isSelected(entry, toId)) items.push(selectionFromCard(toCard))
      entry.items = items
      pruneItemAddons(entry)
      syncAshesPlacement(entry)
      return true
    }
    if (fromId === toId) {
      entry.items[fromIdx] = selectionFromCard(toCard, entry.items[fromIdx].properties)
      pruneItemAddons(entry)
      syncAshesPlacement(entry)
      return true
    }

    var buckets = entry.itemAddons || {}
    var migrated = migrateCompatibleItemAddons(buckets[fromId], toCard)
    delete buckets[fromId]
    if (Object.keys(migrated).length) buckets[toId] = migrated
    else delete buckets[toId]

    if (String(entry._focusedAddonProductId || '') === fromId) {
      entry._focusedAddonProductId = Object.keys(migrated).length ? toId : null
      entry.addons = buckets[toId] || {}
    }

    entry.items[fromIdx] = selectionFromCard(toCard, entry.items[fromIdx].properties)
    pruneItemAddons(entry)
    syncAshesPlacement(entry)
    return true
  }

  /** Add a product without toggling off (multi-select add path). */
  function addSelect(entry, card) {
    if (!entry || !card || !card.selectable) return false
    entry.skippedOptional = false
    clearUrnChangeOrAddPending(entry)
    ensureItemAddons(entry)
    var id = String(card.productId)
    if (!isSelected(entry, id)) {
      entry.items = (entry.items || []).concat([selectionFromCard(card)])
    }
    pruneItemAddons(entry)
    syncAshesPlacement(entry)
    return true
  }

  function toggleSelect(entry, card, mode) {
    if (!card.selectable) return
    mode = mode || entry.mode || 'optional_single'
    entry.skippedOptional = false
    clearUrnChangeOrAddPending(entry)
    var id = String(card.productId)
    ensureItemAddons(entry)
    if (isMultiSelectMode(mode)) {
      if (isSelected(entry, id)) {
        markAutoPouchDismissed(entry, id)
        entry.items = entry.items.filter(function (it) { return String(it.productId) !== id })
        delete entry.itemAddons[id]
        if (String(entry._focusedAddonProductId || '') === id) {
          entry.addons = {}
          entry._focusedAddonProductId = null
        }
      } else {
        entry.items.push(selectionFromCard(card))
      }
      pruneItemAddons(entry)
      syncAshesPlacement(entry)
      return
    }
    // single — replacing the vessel clears personalisation for the prior choice
    entry.itemAddons = {}
    entry.addons = {}
    entry._focusedAddonProductId = null
    clearAshesPlacement(entry)
    if (isSelected(entry, id) && mode === 'optional_single' && !card.included) {
      entry.items = []
      return
    }
    entry.items = [selectionFromCard(card)]
  }

  /**
   * Skip one optional memorial step only. Later optional steps (fur after prints)
   * stay in the flow. Required steps are left untouched.
   */
  function skipOptionalKeepsakes(catalog, state, groupKey) {
    if (!catalog || !groupKey) return
    var cat =
      (catalog.byKey && catalog.byKey[groupKey]) ||
      (catalog.categories || []).filter(function (c) {
        return c && c.key === groupKey
      })[0]
    if (!cat || isRequiredSelectionMode(cat.selectionMode)) return
    var entry = getEntry(state, cat.key, cat.selectionMode)
    entry.items = []
    entry.addons = {}
    entry.itemAddons = {}
    entry._focusedAddonProductId = null
    clearAshesPlacement(entry)
    entry.skippedOptional = true
  }

  function validateSelections(catalog, state) {
    var removed = false
    if (!catalog) return removed
    catalog.categories.forEach(function (cat) {
      var entry = getEntry(state, cat.key, cat.selectionMode)
      var next = []
      ;(entry.items || []).forEach(function (it) {
        var card = findCardInCategory(cat, it.productId)
        if (!card || (!card.selectable && !card.included)) {
          removed = true
          return
        }
        // Refresh label/price/image from catalog (included pouch too — drafts
        // often restore with a null image while the grid card still has one).
        // Swatch families: keep the colour product title in the summary ledger.
        it.label =
          card.selectionLabel ||
          (card.product && card.product.title) ||
          card.title
        it.image = card.image
        it.price = card.included || it.included ? 0 : card.price
        it.included = !!card.included || !!it.included
        it.variantId = card.variantId
        it.needsPhoto = !!card.needsPhoto
        it.needsNameplate = !!card.needsNameplate
        it.needsEngraving = !!card.needsEngraving
        it.needsNameplateText = !!card.needsNameplateText
        it.nameplateIncluded = !!card.nameplateIncluded
        it.photoFeeProduct = card.photoFeeProduct || null
        it.engravingFeeProduct = card.engravingFeeProduct || null
        it.nameplateProductGold = card.nameplateProductGold || null
        it.nameplateProductSilver = card.nameplateProductSilver || null
        it.nameplateGoldLabel = card.nameplateGoldLabel || ''
        it.nameplateSilverLabel = card.nameplateSilverLabel || ''
        it.nameplatePreviewImageGold = card.nameplatePreviewImageGold || null
        it.nameplatePreviewImageSilver = card.nameplatePreviewImageSilver || null
        it.personaliseImage = card.personaliseImage || null
        it.personaliseImageGold = card.personaliseImageGold || null
        it.personaliseImageSilver = card.personaliseImageSilver || null
        it.cardImageGold = card.cardImageGold || null
        it.cardImageSilver = card.cardImageSilver || null
        it.reviewBlankCardImage = card.reviewBlankCardImage || null
        it.photoPrintSize = normalizePhotoPrintSize(card.photoPrintSize)
        it.photoOrientation = normalizePhotoOrientation(card.photoOrientation)
        it.photoOrientationDefault = normalizePhotoOrientationDefault(card.photoOrientationDefault)
        it.photoMockupFrame = card.photoMockupFrame || null
        it.photoCardTitle = card.photoCardTitle || ''
        it.photoCardSubtitle = card.photoCardSubtitle || ''
        it.engravingCardTitle = card.engravingCardTitle || ''
        it.engravingCardSubtitle = card.engravingCardSubtitle || ''
        it.variantMeta = card.variantMeta || ''
        if (card.itemBlockId) it.itemBlockId = card.itemBlockId
        it.quantity = card.included || it.included ? 1 : normalizeItemQuantity(it.quantity)
        next.push(it)
      })
      entry.items = next
      // Do not auto-fill included pouch when validation cleared a choice —
      // family must re-select. Revalidate ashes placement against remaining items.
      syncAshesPlacement(entry)
    })
    return removed
  }

  function activeCategories(catalog) {
    return (catalog && catalog.categories) || []
  }

  function serializeAddonBucket(srcAddons) {
    var addonsOut = {}
    ;['photo', 'nameplate', 'engraving'].forEach(function (type) {
      var a = srcAddons && srcAddons[type]
      if (!a) return
      addonsOut[type] = {
        choiceId: a.choiceId || null,
        choiceLabel: a.choiceLabel || null,
        variantId: a.variant && a.variant.id ? a.variant.id : (a.variantId || null),
        colour: type === 'nameplate' ? (a.colour || null) : undefined,
        photoUrl: a.photoUrl || null,
        photoPrintUrl: a.photoPrintUrl || null,
        size: a.size || null,
        orientation: a.orientation || null,
        _orientationTouched: type === 'photo' ? !!a._orientationTouched : undefined,
        text: a.text || null,
        textLine2: a.textLine2 || null,
        secondLineMode: a.secondLineMode || null,
        lines: a.lines == null ? null : a.lines,
        _linesTouched: (type === 'nameplate' || type === 'engraving')
          ? !!a._linesTouched
          : undefined,
        _line2Touched: (type === 'nameplate' || type === 'engraving')
          ? !!a._line2Touched
          : undefined,
        font: type === 'nameplate' ? (a.font || null) : null,
        _fontTouched: type === 'nameplate' ? !!a._fontTouched : undefined,
      }
    })
    return addonsOut
  }

  function serializeMemorials(state) {
    var out = {}
    Object.keys(state.memorials || {}).forEach(function (key) {
      var e = state.memorials[key]
      ensureItemAddons(e)
      migrateLegacyFlatAddons(e)
      // Clone addons into a fresh object — never assign `e.addons` then overwrite
      // `out[key].addons[type]`, which mutated live wizard state and left click
      // handlers holding stale nameplate/photo references (One/Two needed many taps).
      var itemAddonsOut = {}
      Object.keys(e.itemAddons || {}).forEach(function (pid) {
        var bucketOut = serializeAddonBucket(e.itemAddons[pid])
        if (Object.keys(bucketOut).length) itemAddonsOut[pid] = bucketOut
      })
      // Legacy flat `addons`: first selected product with personalisation (draft v2 readers).
      var legacyAddons = {}
      ;(e.items || []).some(function (it) {
        if (!it || it.productId == null) return false
        var pid = String(it.productId)
        if (!itemAddonsOut[pid]) return false
        legacyAddons = itemAddonsOut[pid]
        return true
      })
      if (!Object.keys(legacyAddons).length) {
        legacyAddons = serializeAddonBucket(e.addons)
      }
      bindPlacementWeight(e, state)
      var ashesPlacementMode =
        fullCapacityVesselCount(e) >= 2
          ? syncAshesPlacement(e, null, state)
          : (syncAshesPlacement(e, null, state), null)
      var ashSplit =
        ashesPlacementMode === 'divided'
          ? 'yes'
          : ashesPlacementMode === 'single_urn'
            ? 'no'
            : null
      var ashDestinationProductId =
        ashesPlacementMode === 'single_urn' ? syncAshDestination(e) : null
      var ashesReceivingProductIds =
        ashesPlacementMode === 'divided' ? receivingIdsForEntry(e) : null
      out[key] = {
        mode: e.mode,
        skippedOptional: !!e.skippedOptional,
        ashesPlacementMode: ashesPlacementMode,
        ashSplit: ashSplit,
        ashDestinationProductId: ashDestinationProductId,
        ashesReceivingProductIds: ashesReceivingProductIds,
        ashesPlacementUrnIds:
          ashesPlacementMode ? fullCapacitySelectionKey(e) : null,
        ashesKeepsakeProductIds: e.ashesKeepsakeProductIds || null,
        ashesRemainingKind: e.ashesRemainingKind || null,
        ashesRemainingProductId: e.ashesRemainingProductId || null,
        ashesRemainingLabel: e.ashesRemainingLabel || null,
        items: (e.items || []).map(function (it) {
          return {
            productId: it.productId,
            variantId: it.variantId,
            included: !!it.included,
            quantity: it.quantity || 1,
            properties: it.properties || {},
            label: it.label,
            styleTitle: it.styleTitle || it.label || '',
            shopifyTitle: it.shopifyTitle || '',
            capacityRole: it.capacityRole || '',
            capacityKg: it.capacityKg != null ? it.capacityKg : null,
            weightMaxKg: it.weightMaxKg != null ? it.weightMaxKg : null,
            capacityLabel: it.capacityLabel || '',
            price: it.price,
            itemBlockId: it.itemBlockId || null,
            needsPhoto: !!it.needsPhoto,
            needsNameplate: !!it.needsNameplate,
            needsEngraving: !!it.needsEngraving,
            needsNameplateText: !!it.needsNameplateText,
          }
        }),
        addons: legacyAddons,
        itemAddons: itemAddonsOut,
      }
    })
    if (state.portraitOffer && state.portraitOffer.variantId) {
      out.__portraitOffer = {
        productId: state.portraitOffer.productId,
        variantId: state.portraitOffer.variantId,
        label: state.portraitOffer.label,
        price: state.portraitOffer.price,
        image: state.portraitOffer.image || null,
        photoUrl: state.portraitOffer.photoUrl || null,
      }
    }
    return out
  }

  function track(eventName, props) {
    try {
      var payload = Object.assign({ event: eventName }, props || {})
      if (global.dataLayer && typeof global.dataLayer.push === 'function') {
        global.dataLayer.push(payload)
      }
      if (global.gtag) global.gtag('event', eventName, props || {})
    } catch (err) { /* no-op */ }
  }

  function portraitOfferVisible(catalog, state) {
    var p = catalog && catalog.settings && catalog.settings.portrait
    if (!p || !p.enabled || !p.product) return false
    // Keep the card after add so the family can upload / change the photo thumb.
    var variant = firstAvailableVariant(p.product)
    return !!(variant && variant.available !== false)
  }

  global.AngelAshesBookingMemorials = {
    normalizeMemorialSettings: normalizeMemorialSettings,
    normalizePetTypeToken: normalizePetTypeToken,
    inferPetSpecies: inferPetSpecies,
    resolvePetClippingKind: resolvePetClippingKind,
    isFurFeatherMemorialCategory: isFurFeatherMemorialCategory,
    resolveFurFeatherKind: resolveFurFeatherKind,
    resolveFurFeatherPresentation: resolveFurFeatherPresentation,
    defaultFurFeatherCardDescription: defaultFurFeatherCardDescription,
    includedMemorialPackagingLabels: includedMemorialPackagingLabels,
    isGenericOptionalMemorialHelper: isGenericOptionalMemorialHelper,
    isPawNoseClayMemorialCategory: isPawNoseClayMemorialCategory,
    resolvePawNoseClayPresentation: resolvePawNoseClayPresentation,
    resolveProductPetType: resolveProductPetType,
    petTypeAllowsProduct: petTypeAllowsProduct,
    buildCatalog: buildCatalog,
    getEntry: getEntry,
    emptyEntry: emptyEntry,
    ensureCategoryDefaults: ensureCategoryDefaults,
    applyIncludedDefault: applyIncludedDefault,
    MEMORIAL_ORDER_QTY_MAX: MEMORIAL_ORDER_QTY_MAX,
    categoryAllowsOrderQuantity: categoryAllowsOrderQuantity,
    normalizeItemQuantity: normalizeItemQuantity,
    itemQuantity: itemQuantity,
    selectedItemForCard: selectedItemForCard,
    setItemQuantity: setItemQuantity,
    urnVesselCount: urnVesselCount,
    urnDistinctCount: urnDistinctCount,
    fullCapacityVesselCount: fullCapacityVesselCount,
    urnRoleVesselCount: urnRoleVesselCount,
    placementUrnItems: placementUrnItems,
    needsAshesPlacementChoice: needsAshesPlacementChoice,
    keepsakeUnitCount: keepsakeUnitCount,
    selectionFromCard: selectionFromCard,
    isSelected: isSelected,
    selectionIndex: selectionIndex,
    urnAshesTagForProduct: urnAshesTagForProduct,
    toggleSelect: toggleSelect,
    shouldPromptChangeOrAdd: shouldPromptChangeOrAdd,
    replaceSelect: replaceSelect,
    addSelect: addSelect,
    clearUrnChangeOrAddPending: clearUrnChangeOrAddPending,
    migrateCompatibleItemAddons: migrateCompatibleItemAddons,
    skipOptionalKeepsakes: skipOptionalKeepsakes,
    validateSelections: validateSelections,
    activeCategories: activeCategories,
    serializeMemorials: serializeMemorials,
    interpolatePet: interpolatePet,
    firstAvailableVariant: firstAvailableVariant,
    productTitle: productTitle,
    getMemorialDisplayTitle: getMemorialDisplayTitle,
    formatMemorialSummaryLabel: formatMemorialSummaryLabel,
    resolveCapacityRole: resolveCapacityRole,
    normalizeCapacityRole: normalizeCapacityRole,
    isMiniHeartProduct: isMiniHeartProduct,
    isFullCapacityItem: isFullCapacityItem,
    isKeepsakeItem: isKeepsakeItem,
    keepsakeActsAsPortion: keepsakeActsAsPortion,
    keepsakeActsAsFullCapacity: keepsakeActsAsFullCapacity,
    keepsakeCapacityKg: keepsakeCapacityKg,
    bindPlacementWeight: bindPlacementWeight,
    configuredKeepsakeItems: configuredKeepsakeItems,
    fullCapacityItems: fullCapacityItems,
    keepsakeItems: keepsakeItems,
    syncAutoIncludedPouch: syncAutoIncludedPouch,
    keepsakeUnitCount: keepsakeUnitCount,
    fullCapacityVesselCount: fullCapacityVesselCount,
    fullCapacitySelectionKey: fullCapacitySelectionKey,
    CAPACITY_ROLES: {
      FULL: CAPACITY_ROLE_FULL,
      KEEPSAKE: CAPACITY_ROLE_KEEPSAKE,
      SCATTER: CAPACITY_ROLE_SCATTER,
    },
    sizedShopifyImageUrl: sizedShopifyImageUrl,
    MEMORIAL_GRID_THUMB_PX: MEMORIAL_GRID_THUMB_PX,
    MEMORIAL_SUMMARY_THUMB_PX: MEMORIAL_SUMMARY_THUMB_PX,
    MEMORIAL_REVIEW_THUMB_PX: MEMORIAL_REVIEW_THUMB_PX,
    PHOTO_ADDON_THUMB_PX: PHOTO_ADDON_THUMB_PX,
    MEMORIAL_ARTBOARD_PX: MEMORIAL_ARTBOARD_PX,
    GOODBYE_VISUAL_PX: GOODBYE_VISUAL_PX,
    prefetchImageUrl: prefetchImageUrl,
    prefetchImageUrls: prefetchImageUrls,
    warmWizardImages: warmWizardImages,
    warmImagesForStep: warmImagesForStep,
    scheduleWizardImageWarm: scheduleWizardImageWarm,
    collectPersonaliseStepWarmUrls: collectPersonaliseStepWarmUrls,
    personaliseItemLabel: personaliseItemLabel,
    track: track,
    portraitOfferVisible: portraitOfferVisible,
    addonFlagsForProduct: addonFlagsForProduct,
    parseMaxKgFromVariantLabel: parseMaxKgFromVariantLabel,
    shortWeightSizeLabel: shortWeightSizeLabel,
    isWeightSizeVariantMeta: isWeightSizeVariantMeta,
    teImageUrl: teImageUrl,
    normalizeNameplateMetal: normalizeNameplateMetal,
    resolvePersonaliseStepImage: resolvePersonaliseStepImage,
    resolveMetalCardImage: resolveMetalCardImage,
    variantIdForNameplateMetal: variantIdForNameplateMetal,
    cardImagesFrom: cardImagesFrom,
    resolveReviewBlankCardImage: resolveReviewBlankCardImage,
    reviewUsesBlankCardWhenNoPhoto: reviewUsesBlankCardWhenNoPhoto,
    resolvePersonaliseMockupFrame: resolvePersonaliseMockupFrame,
    resolvePhotoStepMockupFrame: resolvePhotoStepMockupFrame,
    deriveMetalPersonaliseUrl: deriveMetalPersonaliseUrl,
    resolveMetalPersonaliseUrl: resolveMetalPersonaliseUrl,
    nameplateMetalForProduct: nameplateMetalForProduct,
    teCardImageFromBlock: teCardImageFromBlock,
    teVariantCardImageFromSettings: teVariantCardImageFromSettings,
    productKeepsStableCardPhoto: productKeepsStableCardPhoto,
    looksLikePhotoTributeProduct: looksLikePhotoTributeProduct,
    productHasWeightTierSizeImages: productHasWeightTierSizeImages,
    resolveWeightTierDisplayImage: resolveWeightTierDisplayImage,
    variantTitleMaxKgCutoffs: variantTitleMaxKgCutoffs,
    variantMetafieldMaxKgCutoffs: variantMetafieldMaxKgCutoffs,
    variantMaxKgCutoffs: variantMaxKgCutoffs,
    resolveVariantByWeightCutoffs: resolveVariantByWeightCutoffs,
    resolveMemorialVariant: resolveMemorialVariant,
    isMultiSelectMode: isMultiSelectMode,
    isRequiredSelectionMode: isRequiredSelectionMode,
    ensureItemAddons: ensureItemAddons,
    pruneItemAddons: pruneItemAddons,
    focusAddonProduct: focusAddonProduct,
    migrateLegacyFlatAddons: migrateLegacyFlatAddons,
    syncAshSplitChoice: syncAshSplitChoice,
    syncAshesPlacement: syncAshesPlacement,
    normalizeAshesPlacementMode: normalizeAshesPlacementMode,
    ashesPlacementDisplayValue: ashesPlacementDisplayValue,
    ashesArrangementSummaryLines: ashesArrangementSummaryLines,
    ashesArrangementReviewLines: ashesArrangementReviewLines,
    cardShowsCapacityLabel: cardShowsCapacityLabel,
    petExceedsKeepsakeCapacity: petExceedsKeepsakeCapacity,
    keepsakeNoticeCopy: keepsakeNoticeCopy,
    ashesPlacementReady: ashesPlacementReady,
    setAshesPlacementMode: setAshesPlacementMode,
    ashSplitDisplayValue: ashSplitDisplayValue,
    syncAshDestination: syncAshDestination,
    ashDestinationDisplayValue: ashDestinationDisplayValue,
    setAshDestination: setAshDestination,
    clearAshesPlacement: clearAshesPlacement,
    findCardInCategory: findCardInCategory,
    findDisplayCardInCategory: findDisplayCardInCategory,
    normalizeCatalogSort: normalizeCatalogSort,
    sortCatalogCards: sortCatalogCards,
    categoryShowsFeaturedCap: categoryShowsFeaturedCap,
    ensureSelectedCatalogCards: ensureSelectedCatalogCards,
    visibleCategoryCards: visibleCategoryCards,
    resolveCardImage: resolveCardImage,
    selectSwatchColour: selectSwatchColour,
    selectInlineVariant: selectInlineVariant,
    productHasWeightSizingVariants: productHasWeightSizingVariants,
    shouldInlineProductVariants: shouldInlineProductVariants,
    variantsForInlineCardTray: variantsForInlineCardTray,
    variantsForDetailPicker: variantsForDetailPicker,
    variantForNameplateColour: variantForNameplateColour,
    isNameplateMetalVariant: isNameplateMetalVariant,
    nameplateMetalFromVariant: nameplateMetalFromVariant,
    isFamilyMemberSelected: isFamilyMemberSelected,
    collapseSwatchFamilies: collapseSwatchFamilies,
    resolveCardSwatchMeta: resolveCardSwatchMeta,
    /** Generic card option model (see buildCardOptionModel). */
    CARD_OPTION_TYPES: {
      NONE: CARD_OPTION_NONE,
      COLOUR: CARD_OPTION_COLOUR,
      QUANTITY: CARD_OPTION_QUANTITY,
    },
    buildCardOptionModel: buildCardOptionModel,
    buildSwatchOption: buildSwatchOption,
    findCardOption: findCardOption,
    deriveQuantityUnit: deriveQuantityUnit,
    normalizeQuantityUnitHint: normalizeQuantityUnitHint,
    formatQuantityLabel: formatQuantityLabel,
    teSwatchesEnabled: teSwatchesEnabled,
    teSwatchSlotEntries: teSwatchSlotEntries,
    makeColourSlotItemBlock: makeColourSlotItemBlock,
    ensureTeFamilySwatchGroup: ensureTeFamilySwatchGroup,
  }
})(typeof window !== 'undefined' ? window : this)
